import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useCart } from '../hooks/useCart';
import { usePermissions } from '../hooks/usePermissions';
import { useOfflineMode } from '../hooks/useOfflineMode';
import cartService from '../services/cartService';
import { QuickTableDropdown } from '../components/UnifiedTableSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { getGabonTimeString } from '../utils/gabonTime';
import { Html5QrcodeScanner } from 'html5-qrcode';
import ModernTableCard, { CompactTableCard } from '../components/ModernTableCard';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote,
    Smartphone, X, Check, Package, Search, MapPin, ChefHat,
    Users, Send, Utensils, Flame, AlertTriangle, Edit, Copy,
    Ban, Settings, Zap, Pin, AlertCircle, FileText, Bell, 
    Volume2, VolumeX, CheckCircle, Clock, TrendingUp, Activity,
    Monitor, LayoutGrid, DollarSign, Loader2, Calculator, Receipt, LogOut,
    Gift, Award, Star, Crown, Phone, UserPlus, QrCode, ScanLine, Camera, User,
    MessageSquare, Wine, ChevronDown, ChevronUp, WifiOff, Wifi, RefreshCw, Lock, Coins, FolderPlus, UtensilsCrossed, Home
} from 'lucide-react';
import { cn } from '../lib/utils';
import POSLockScreen from '../components/POSLockScreen';
import CashClosingModal from '../components/CashClosingModal';
import RegisterOpeningDialog from '../components/RegisterOpeningDialog';
import MobileCartPanel from '../components/MobileCartPanel';
import UnifiedCart from '../components/UnifiedCart';
import ThemeSwitch from '../components/ThemeSwitch';
import { useTheme } from '../contexts/ThemeContext';
import useRolePermissions from '../hooks/useRolePermissions';
import { PERMISSION_TYPES } from '../config/rolePermissions';
import useRealtimeSync, { ConnectionState, SyncEventType } from '../hooks/useRealtimeSync';
import ConnectionStatusIndicator from '../components/ConnectionStatusIndicator';
import CategoryManager from '../components/CategoryManager';
import FormulaCard from '../components/FormulaCard';
import FormulaSelectionModal from '../components/FormulaSelectionModal';
import FormulaManager from '../components/FormulaManager';
import FormulaQuickEdit from '../components/FormulaQuickEdit';
import QuickMessageModal from '../components/QuickMessageModal';
import SupplementSelectionDialog from '../components/pos/SupplementSelectionDialog';

// Extracted POS Components
import { SupervisionBar, ReadyOrdersPanel, NotificationPopup, PaymentDialog, LoyaltyDialog, SettingsDialog, ProductEditDialog, FormulaDialog } from '../components/pos';

// ========================================
// POS PAGE V2 - With Manager Mode & Fast Service
// ========================================

const POSPageV2 = () => {
    const { 
        api, 
        user,
        canScanLoyalty,
        canValidateLoyaltyPurchase,
        canAddLoyaltyPointsManual,
        canOfferLoyaltyBonus,
        canCorrectLoyaltyAccount
    } = useAuth();
    const navigate = useNavigate();
    
    // Import et utilisation du hook de permissions centralisé
    const permissions = usePermissions(user);
    
    // Role checks - basés sur le système de permissions centralisé
    const isManager = permissions.isManagement;
    const isAdmin = permissions.isAdmin;
    const isServer = permissions.isServer;
    const canViewAllTables = permissions.canViewAllTables;
    const canCancelOrder = permissions.canCancelOrder;
    const canAccessSettings = permissions.canAccessAdminSettings;
    const canViewSupervision = permissions.canViewSupervisionBar;
    
    // Core data
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [supplements, setSupplements] = useState([]);
    const [accompaniments, setAccompaniments] = useState([]);
    const [tables, setTables] = useState([]);
    const [popularProducts, setPopularProducts] = useState([]);
    const [formulas, setFormulas] = useState([]); // Formules/Menus
    const [expandedFormulaSections, setExpandedFormulaSections] = useState({
        petit_dejeuner: true,
        dejeuner: true,
        diner: true,
        all: true
    }); // Accordion state for formula sections
    const [loading, setLoading] = useState(true);
    const [posSettings, setPosSettings] = useState({
        managerModeEnabled: true,
        fastServiceEnabled: true,
        kitchenSendMode: 'button', // 'auto_add', 'button', 'payment'
        tableRequired: true,
        supplementsInGrid: false,
        popularMode: 'auto' // 'auto', 'manual'
    });
    
    // Ready Orders State (Kitchen notifications)
    const [readyOrders, setReadyOrders] = useState([]);
    const [showReadyPanel, setShowReadyPanel] = useState(true);
    const [readySoundEnabled, setReadySoundEnabled] = useState(true);
    const [previousReadyCount, setPreviousReadyCount] = useState(0);
    const readyAudioRef = useRef(null);
    
    // Notification Popup State (Bar/Kitchen ready alerts)
    const [notificationPopup, setNotificationPopup] = useState(null);
    // Format: { type: 'bar'|'kitchen', tableNumber: 'T5', orderNumber: '001', items: [...], timestamp: Date }
    
    // Supervision Bar State (Real-time stats for managers)
    const [supervisionStats, setSupervisionStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    
    // Gabon Time Clock
    const [currentTime, setCurrentTime] = useState(getGabonTimeString());
    
    // Update clock every minute
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(getGabonTimeString());
        }, 1000);
        return () => clearInterval(timer);
    }, []);
    
    // UI State
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // ========================================
    // NEW PERSISTENT CART SYSTEM (Backend-driven)
    // ========================================
    // The cart is now stored in the database and linked to the user.
    // Selecting a table ONLY links it - does NOT clear the cart!
    // ========================================
    
    const {
        cart: persistentCart,
        cartItems: backendCartItems,
        cartTotal: backendCartTotal,
        itemCount: backendItemCount,
        linkedTableId,
        linkedTableNumber,
        isEmpty: cartIsEmpty,
        loading: cartLoading,
        actionLoading: cartActionLoading,
        fetchCart,
        addItem: addItemToBackend,
        updateItemQuantity: updateBackendItemQuantity,
        removeItem: removeBackendItem,
        clearCartItems: clearBackendCart,
        linkToTable,
        unlinkFromTable,
        sendToKitchen: sendBackendToKitchen,
        processDirectSale,
        loadFromTable: loadDraftsFromTable,
        setCart: setBackendCart
    } = useCart();
    
    // Offline mode hook
    const {
        isOnline,
        isOffline,
        pendingOrdersCount,
        isSyncing,
        offlineProducts,
        offlineCategories,
        lastSyncTime,
        cacheProductsAndCategories,
        saveOrderLocally,
        getPendingOrders,
        syncPendingOrders,
        saveCartOffline,
        recoverCart,
        deletePendingOrder
    } = useOfflineMode(api);
    
    // Role-based permissions (extended)
    const {
        permissions: rolePermissions,
        specialAccess,
        roleInfo,
        isAdmin: isRoleAdmin,
        isManager: isRoleManager,
        isBarman,
        isCuisinier,
        canAccessCaisse,
        filterCategories,
        can: canRole,
    } = useRolePermissions();
    
    // ========================================
    // REAL-TIME SYNCHRONIZATION
    // ========================================
    const {
        connectionState,
        isConnected,
        isOnline: wsIsOnline,
        on: onSyncEvent,
        off: offSyncEvent,
        send: sendSync,
        reconnectAttempts
    } = useRealtimeSync({ debug: false });
    
    // Local cart state for UI (synced from backend)
    // We keep local state for smooth UI but sync with backend on actions
    const [cart, setCart] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [currentTableOrder, setCurrentTableOrder] = useState(null); // Commande en cours de la table
    const [orderItems, setOrderItems] = useState([]); // Items déjà envoyés (sent_items)
    const [customerName, setCustomerName] = useState('');
    const [orderNotes, setOrderNotes] = useState('');
    const [isTakeaway, setIsTakeaway] = useState(false); // Commande à emporter
    const [sentItemIds, setSentItemIds] = useState(new Set()); // IDs des items déjà envoyés en cuisine
    const [loadingTableOrder, setLoadingTableOrder] = useState(false); // Loading state when switching tables
    const [tableChangeConfirmDialog, setTableChangeConfirmDialog] = useState({ open: false, newTable: null }); // Dialog for confirming table change
    
    // Formula Selection Modal State
    const [formulaModal, setFormulaModal] = useState({ open: false, formula: null });
    
    // Formula Manager State (back-office configuration)
    const [formulaManagerOpen, setFormulaManagerOpen] = useState(false);
    
    // Formula Quick Edit State (rapid edit from POS)
    const [formulaQuickEdit, setFormulaQuickEdit] = useState({ open: false, formula: null });
    
    // Quick Message Modal State (send message from table)
    const [quickMessageModal, setQuickMessageModal] = useState({ open: false, tableNumber: null });
    
    // Message states for kitchen and bar
    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [messageDestination, setMessageDestination] = useState('cuisine'); // 'cuisine' or 'bar'
    const [messageText, setMessageText] = useState('');
    
    // Sync local cart from backend on load
    // NOTE: Le nettoyage du panier est géré par le BACKEND dans link-table
    // Le frontend ne doit PAS faire de nettoyage supplémentaire
    // ========================================
    // FLAG DE BLOCAGE DE RÉHYDRATATION
    // ========================================
    // Ce flag empêche la réhydratation automatique du panier quand on
    // arrive sur une table vide via "Nouvelle commande"
    const [blockCartRehydration, setBlockCartRehydration] = useState(false);
    
    useEffect(() => {
        // SÉCURITÉ: Si le blocage de réhydratation est actif, ne pas charger le panier backend
        if (blockCartRehydration) {
            console.log('[POS] Cart rehydration BLOCKED - keeping cart empty');
            return;
        }
        
        if (!cartLoading && backendCartItems) {
            // Map backend cart items to local format - PAS de nettoyage côté frontend
            const mappedItems = backendCartItems.map(item => ({
                id: item.id,
                product_id: item.product_id,
                product_name: item.product_name,
                name: item.product_name,
                price: item.unit_price,
                quantity: item.quantity,
                destination: item.destination,
                category: item.category,
                supplements: item.supplements || [],
                accompaniment: item.accompaniment,
                notes: item.notes,
                sent_to_kitchen: false // Cart items are never sent yet
            }));
            setCart(mappedItems);
            
            // Sync linked table from backend ONLY if we have a linked table
            // Ne PAS effacer selectedTable si linkedTableId est null - laisse assignCartToTable gérer
            if (linkedTableId && linkedTableNumber) {
                const linkedTable = tables.find(t => t.id === linkedTableId);
                if (linkedTable) {
                    setSelectedTable(linkedTable);
                } else {
                    // Table not found in list, but we have the number
                    // Create a minimal table object for display
                    setSelectedTable({
                        id: linkedTableId,
                        number: linkedTableNumber,
                        status: 'occupee',
                        zone: ''
                    });
                }
            }
            // NOTE: On ne fait plus setSelectedTable(null) ici
            // Car assignCartToTable gère déjà setSelectedTable(table)
        }
    }, [cartLoading, backendCartItems, linkedTableId, linkedTableNumber, tables, blockCartRehydration]);
    
    // Legacy keys for compatibility (can be removed later)
    const CART_STORAGE_KEY_V2 = 'pos_cart_v2';
    const CART_TABLE_KEY_V2 = 'pos_cart_table_v2';
    
    // Manager Mode State
    const [managerMode, setManagerMode] = useState(false);
    const [fastServiceMode, setFastServiceMode] = useState(false);
    const [productEditDialog, setProductEditDialog] = useState({ open: false, product: null, isNew: false });
    const [supplementEditDialog, setSupplementEditDialog] = useState({ open: false, supplement: null, isNew: false });
    const [settingsDialog, setSettingsDialog] = useState({ open: false });
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);  // Category management dialog
    
    // Cash Closing Modal State
    const [cashClosingModal, setCashClosingModal] = useState({ open: false, registerId: null, registerName: '' });
    
    // Register Opening Dialog State
    const [openingDialog, setOpeningDialog] = useState({ open: false });
    const [availableRegisters, setAvailableRegisters] = useState([]);
    
    // Formula creation dialog
    const [formulaDialog, setFormulaDialog] = useState({ open: false });
    const [newFormula, setNewFormula] = useState({
        name: '',
        description: '',
        price: '',
        steps: [{ name: 'Entrée', choices: [], required: true }, { name: 'Plat', choices: [], required: true }, { name: 'Dessert', choices: [], required: false }],
        meal_type: 'all'
    });
    const [formulaProductFilter, setFormulaProductFilter] = useState({ category: 'all', search: '' });
    const [editingFormula, setEditingFormula] = useState(null); // For editing existing formulas
    
    // Permissions for formula editing
    const canEditFormulas = user?.role && ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user.role);
    
    // Permissions for product editing (same as formulas)
    const canEditProducts = user?.role && ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user.role);
    
    // Toggle to show all products even in Bar mode (for managers who need to see everything)
    const [showAllProductsInBarMode, setShowAllProductsInBarMode] = useState(false);
    
    // Cart supplement dialog - enhanced for responsive component
    const [cartSupplementDialog, setCartSupplementDialog] = useState({ 
        open: false, 
        cartIndex: null,
        selectedSupplements: [], // Track supplements selected in dialog
        allowedSupplementIds: null // IDs des suppléments autorisés pour ce produit
    });
    
    // Cash Session State
    const [cashSession, setCashSession] = useState(null);
    const [sessionLoading, setSessionLoading] = useState(true); // Track session loading state separately
    const [closingDialog, setClosingDialog] = useState({ open: false });
    const [closingCash, setClosingCash] = useState('');
    const [closingNotes, setClosingNotes] = useState('');
    const [closingLoading, setClosingLoading] = useState(false);
    // Open orders check state
    const [openOrdersCheck, setOpenOrdersCheck] = useState(null);
    const [checkingOpenOrders, setCheckingOpenOrders] = useState(false);
    const [forceCloseMode, setForceCloseMode] = useState(false);
    const [forceCloseReason, setForceCloseReason] = useState('');
    
    // Register closure state - forced redirect when register is closed
    const [registerClosed, setRegisterClosed] = useState(false);
    const [registerClosedReason, setRegisterClosedReason] = useState('');
    const wsRef = useRef(null);
    const globalWsRef = useRef(null);  // WebSocket global pour les notifications
    
    // Mobile cart panel - collapsed by default on mobile
    const [mobileCartExpanded, setMobileCartExpanded] = useState(false);
    
    // Mobile bottom actions zone - collapsed by default when cart is open
    const [mobileActionsExpanded, setMobileActionsExpanded] = useState(false);
    
    // Cart footer collapsed state (for iPad/desktop - only bottom section)
    const [cartFooterCollapsed, setCartFooterCollapsed] = useState(false);
    
    // Dialogs
    const [accompanimentDialog, setAccompanimentDialog] = useState({ 
        open: false, 
        product: null
    });
    const [paymentDialog, setPaymentDialog] = useState({ open: false });
    // Table dialog SUPPRIMÉ - utilise QuickTableDropdown
    const [availableTables, setAvailableTables] = useState([]);
    const [showTableSelect, setShowTableSelect] = useState(false);
    
    // Payment
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentAmount, setPaymentAmount] = useState('');
    
    // Loyalty Program - Le Cercle Aldrin's
    const [loyaltyDialog, setLoyaltyDialog] = useState({ open: false });
    const [loyaltySearchPhone, setLoyaltySearchPhone] = useState('');
    const [loyaltyMember, setLoyaltyMember] = useState(null);
    const [loyaltySearching, setLoyaltySearching] = useState(false);
    const [loyaltyPointsToAdd, setLoyaltyPointsToAdd] = useState(0);
    const [loyaltyPointsReason, setLoyaltyPointsReason] = useState('');
    const [loyaltyAddPointsMode, setLoyaltyAddPointsMode] = useState(false);
    const [selectedLoyaltyMember, setSelectedLoyaltyMember] = useState(null); // For payment
    
    // Reward Scanner - Scanner récompense fidélité optimisé
    const [rewardScannerOpen, setRewardScannerOpen] = useState(false);
    const [verifiedReward, setVerifiedReward] = useState(null);
    const [selectedRewardProduct, setSelectedRewardProduct] = useState(null);
    const [rewardScannerLoading, setRewardScannerLoading] = useState(false);
    const [rewardConfirmLoading, setRewardConfirmLoading] = useState(false);
    
    // Selected accompaniment in dialog
    const [selectedAccompaniment, setSelectedAccompaniment] = useState(null);
    
    // ========================================
    // ROLE-BASED CATEGORY FILTERING
    // ========================================
    
    // Filter categories based on user role (Barman sees only drinks)
    const filteredCategories = useMemo(() => {
        if (!categories || !categories.length) return [];
        
        // Barman: only drink categories
        if (specialAccess.isBarOnly && specialAccess.allowedCategories) {
            const allowed = specialAccess.allowedCategories.map(c => c.toLowerCase());
            return categories.filter(cat => {
                const catName = (cat.name || cat.label || '').toLowerCase();
                return allowed.some(a => catName.includes(a.toLowerCase()));
            });
        }
        
        // Kitchen staff: no access to POS categories
        if (specialAccess.isKitchenOnly) {
            return []; // Cuisinier ne devrait pas être sur le POS
        }
        
        // All other roles: all categories
        return categories;
    }, [categories, specialAccess]);
    
    // Filter products based on allowed categories (role-based)
    const roleFilteredProducts = useMemo(() => {
        if (!products || !products.length) return products;
        
        // Barman: only products from drink categories
        if (specialAccess.isBarOnly && specialAccess.allowedCategories) {
            const allowedCatNames = specialAccess.allowedCategories.map(c => c.toLowerCase());
            const allowedCatIds = filteredCategories.map(c => c.id);
            
            return products.filter(p => {
                const productCat = (p.category || p.category_name || '').toLowerCase();
                return allowedCatNames.some(a => productCat.includes(a)) || 
                       allowedCatIds.includes(p.category_id);
            });
        }
        
        return products;
    }, [products, specialAccess, filteredCategories]);
    
    // ========================================
    // BAR REGISTER DETECTION
    // ========================================
    
    // Detect if current register is Bar register
    const isBarRegister = useMemo(() => {
        if (!cashSession) return false;
        const regName = (cashSession.register_name || '').toLowerCase();
        const regType = cashSession.register_type || '';
        return regType === 'bar' || regName.includes('bar');
    }, [cashSession]);
    
    // Bar-specific settings
    const barConfig = useMemo(() => {
        if (!isBarRegister) return null;
        return {
            destination: 'bar',
            sendToKitchen: false,
            sendToBar: true,
            allowedCategories: ['boissons', 'boissons froides', 'boissons chaudes', 'desserts', 'boissons fraîches'],
            simplifiedInterface: true,
            quickMode: true
        };
    }, [isBarRegister]);
    
    // Filter categories and products for Bar register
    const barFilteredCategories = useMemo(() => {
        if (!isBarRegister || !barConfig) return filteredCategories;
        
        const allowed = barConfig.allowedCategories.map(c => c.toLowerCase());
        return categories.filter(cat => {
            const catName = (cat.name || cat.label || '').toLowerCase();
            return allowed.some(a => catName.includes(a)) || cat.is_bar_category || cat.destination === 'bar';
        });
    }, [isBarRegister, barConfig, categories, filteredCategories]);
    
    const barFilteredProducts = useMemo(() => {
        if (!isBarRegister || !barConfig) return roleFilteredProducts;
        
        const allowed = barConfig.allowedCategories.map(c => c.toLowerCase());
        const barCatIds = barFilteredCategories.map(c => c.id);
        
        return products.filter(p => {
            const productCat = (p.category || p.category_name || '').toLowerCase();
            const isBarProduct = p.is_bar_product || p.destination === 'bar';
            return isBarProduct || 
                   allowed.some(a => productCat.includes(a)) || 
                   barCatIds.includes(p.category_id) ||
                   barCatIds.includes(p.category);
        });
    }, [isBarRegister, barConfig, products, barFilteredCategories, roleFilteredProducts]);
    
    // Final filtered categories and products (considering both role and bar register)
    // If showAllProductsInBarMode is true, bypass bar filtering
    const displayCategories = (isBarRegister && !showAllProductsInBarMode) ? barFilteredCategories : filteredCategories;
    const displayProducts = (isBarRegister && !showAllProductsInBarMode) ? barFilteredProducts : roleFilteredProducts;
    
    // ========================================
    // POS LOCK SCREEN STATE
    // ========================================
    // Ref pour tracker si l'état de verrouillage a été initialisé
    const lockStateInitializedRef = useRef(false);
    const previousLockStateRef = useRef(null);
    
    // Persister l'état de verrouillage dans localStorage pour survivre au refresh
    const [isLocked, setIsLocked] = useState(() => {
        try {
            const savedLockState = localStorage.getItem('pos_lock_state');
            if (savedLockState) {
                const parsed = JSON.parse(savedLockState);
                if (parsed.locked && parsed.timestamp) {
                    const elapsed = Date.now() - parsed.timestamp;
                    const maxLockDuration = 8 * 60 * 60 * 1000; // 8 heures
                    if (elapsed < maxLockDuration) {
                        lockStateInitializedRef.current = true;
                        previousLockStateRef.current = true;
                        return true;
                    }
                }
            }
        } catch (e) {
            console.error('Error reading lock state:', e);
        }
        lockStateInitializedRef.current = true;
        previousLockStateRef.current = false;
        return false;
    });
    const [lockedByInactivity, setLockedByInactivity] = useState(() => {
        try {
            const savedLockState = localStorage.getItem('pos_lock_state');
            if (savedLockState) {
                const parsed = JSON.parse(savedLockState);
                return parsed.byInactivity || false;
            }
        } catch (e) {}
        return false;
    });
    const [inactivityTimeout, setInactivityTimeout] = useState(5);
    const lastActivityRef = useRef(Date.now());
    const inactivityTimerRef = useRef(null);
    
    // Sauvegarder l'état de verrouillage dans localStorage (sans notification)
    useEffect(() => {
        // Ne pas sauvegarder si l'état n'a pas vraiment changé
        if (previousLockStateRef.current === isLocked) return;
        previousLockStateRef.current = isLocked;
        
        try {
            if (isLocked) {
                localStorage.setItem('pos_lock_state', JSON.stringify({
                    locked: true,
                    byInactivity: lockedByInactivity,
                    timestamp: Date.now(),
                    path: window.location.pathname
                }));
            } else {
                localStorage.removeItem('pos_lock_state');
            }
        } catch (e) {
            console.error('Error saving lock state:', e);
        }
    }, [isLocked, lockedByInactivity]);
    
    // Refs
    const searchRef = useRef(null);
    
    // ========================================
    // INACTIVITY DETECTION & AUTO-LOCK
    // ========================================
    
    // Reset activity timer on user interaction
    const resetActivityTimer = useCallback(() => {
        if (!isLocked) {
            lastActivityRef.current = Date.now();
        }
    }, [isLocked]);
    
    // Track user activity (seulement si non verrouillé)
    useEffect(() => {
        if (isLocked) return;
        
        const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
        
        events.forEach(event => {
            window.addEventListener(event, resetActivityTimer, { passive: true });
        });
        
        return () => {
            events.forEach(event => {
                window.removeEventListener(event, resetActivityTimer);
            });
        };
    }, [resetActivityTimer, isLocked]);
    
    // Vérifier l'état de verrouillage au retour de focus (une seule fois au montage)
    useEffect(() => {
        const checkLockState = () => {
            try {
                const savedLockState = localStorage.getItem('pos_lock_state');
                if (savedLockState) {
                    const parsed = JSON.parse(savedLockState);
                    if (parsed.locked) {
                        const elapsed = Date.now() - parsed.timestamp;
                        const maxLockDuration = 8 * 60 * 60 * 1000;
                        if (elapsed < maxLockDuration) {
                            // Utiliser une fonction de mise à jour pour éviter les boucles
                            setIsLocked(prev => {
                                if (!prev) {
                                    setLockedByInactivity(parsed.byInactivity || false);
                                    return true;
                                }
                                return prev;
                            });
                        }
                    }
                }
            } catch (e) {
                console.error('Error checking lock state:', e);
            }
        };
        
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkLockState();
            }
        };
        
        // Écouter le retour de focus
        window.addEventListener('focus', checkLockState);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', checkLockState);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []); // Dépendances vides - ne s'exécute qu'au montage
    
    // Check for inactivity and auto-lock
    useEffect(() => {
        if (isLocked || inactivityTimeout <= 0) return;
        
        const checkInactivity = () => {
            const now = Date.now();
            const elapsed = (now - lastActivityRef.current) / 1000 / 60; // minutes
            
            if (elapsed >= inactivityTimeout) {
                setIsLocked(true);
                setLockedByInactivity(true);
            }
        };
        
        // Check every 30 seconds
        inactivityTimerRef.current = setInterval(checkInactivity, 30000);
        
        return () => {
            if (inactivityTimerRef.current) {
                clearInterval(inactivityTimerRef.current);
            }
        };
    }, [isLocked, inactivityTimeout]);
    
    // Handle manual lock (sans notification - la notification est gérée par le composant)
    const handleLockPOS = useCallback(() => {
        setIsLocked(prev => {
            if (!prev) {
                setLockedByInactivity(false);
                return true;
            }
            return prev;
        });
    }, []);
    
    // Ref pour éviter les notifications multiples
    const unlockNotificationSentRef = useRef(false);
    
    // Handle unlock with PIN verification
    const handleUnlockPOS = useCallback(async (pin, isAdminBypass = false) => {
        // Empêcher les appels multiples
        if (unlockNotificationSentRef.current) return false;
        
        // Admin bypass - no PIN needed
        if (isAdminBypass && isAdmin) {
            unlockNotificationSentRef.current = true;
            setIsLocked(false);
            setLockedByInactivity(false);
            lastActivityRef.current = Date.now();
            toast.success('Caisse déverrouillée (Admin)', { duration: 2000 });
            // Reset après un délai
            setTimeout(() => { unlockNotificationSentRef.current = false; }, 3000);
            return true;
        }
        
        // Verify PIN via API
        try {
            const response = await api().post('/auth/verify-pin', { 
                pin: pin 
            });
            
            if (response.data?.valid) {
                unlockNotificationSentRef.current = true;
                setIsLocked(false);
                setLockedByInactivity(false);
                lastActivityRef.current = Date.now();
                toast.success('Caisse déverrouillée', { duration: 2000 });
                // Reset après un délai
                setTimeout(() => { unlockNotificationSentRef.current = false; }, 3000);
                return true;
            }
            return false;
        } catch (error) {
            console.error('PIN verification error:', error);
            return false;
        }
    }, [api, isAdmin]);
    
    // ========================================
    // DATA FETCHING
    // ========================================
    
    // Track if initial fetch has been done to prevent re-fetching loops
    const hasFetchedRef = useRef(false);
    
    // Force refresh data (ignore hasFetchedRef guard)
    const refreshData = useCallback(async () => {
        try {
            const [tabRes] = await Promise.all([
                api().get('/tables')
            ]);
            setTables(tabRes.data || []);
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    }, [api]);
    
    const fetchData = useCallback(async () => {
        // Prevent multiple fetches - only fetch once on mount or when explicitly needed
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        
        setLoading(true);
        
        // If offline, use cached data
        if (isOffline && offlineProducts.length > 0) {
            setProducts(offlineProducts);
            setCategories(offlineCategories);
            toast.info('Utilisation des données en cache (mode hors-ligne)');
            setLoading(false);
            return;
        }
        
        try {
            const [catRes, prodRes, supRes, accRes, tabRes, popRes, settingsRes, formulasRes] = await Promise.all([
                api().get('/pos/categories'),
                api().get('/pos/products?available_only=false'),
                api().get('/pos/supplements').catch(() => ({ data: [] })),
                api().get('/pos/accompaniments').catch(() => ({ data: [] })),
                api().get('/tables'),
                api().get('/pos/products/popular').catch(() => ({ data: [] })),
                api().get('/pos/settings').catch(() => ({ data: null })),
                api().get('/pos/formulas').catch(() => ({ data: [] }))
            ]);
            
            const categoriesData = catRes.data || [];
            const productsData = prodRes.data || [];
            
            setCategories(categoriesData);
            setProducts(productsData);
            setSupplements(supRes.data || []);
            setAccompaniments(accRes.data || []);
            setTables(tabRes.data || []);
            setPopularProducts(popRes.data || []);
            setFormulas(formulasRes.data || []);
            if (settingsRes.data) {
                setPosSettings(prev => ({ ...prev, ...settingsRes.data }));
            }
            
            // Cache products and categories for offline use (don't trigger re-renders)
            if (productsData.length > 0 && categoriesData.length > 0) {
                cacheProductsAndCategories(productsData, categoriesData);
            }
            
        } catch (error) {
            console.error('Error fetching POS data:', error);
            
            // If fetch fails and we have cached data, use it
            if (offlineProducts.length > 0) {
                setProducts(offlineProducts);
                setCategories(offlineCategories);
                toast.warning('Connexion perdue - données en cache utilisées');
            } else {
                toast.error('Erreur de chargement');
            }
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, isOffline]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // ========================================
    // URL PARAMS HANDLING (from Tables page)
    // ========================================
    const [searchParams] = useSearchParams();
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
    
    // ========================================
    // READY ORDERS POLLING (Kitchen notifications)
    // ========================================
    
    // Initialize audio for ready notification
    useEffect(() => {
        // Créer l'audio avec volume maximum
        readyAudioRef.current = new Audio('/notification.mp3');
        readyAudioRef.current.preload = 'auto';
        readyAudioRef.current.volume = 1.0; // Volume maximum
        
        // Précharger plusieurs fois pour garantir la disponibilité
        readyAudioRef.current.load();
        
        // Créer un deuxième audio pour notifications multiples rapides
        const secondaryAudio = new Audio('/notification.mp3');
        secondaryAudio.preload = 'auto';
        secondaryAudio.volume = 1.0;
        
        return () => {
            if (readyAudioRef.current) {
                readyAudioRef.current.pause();
                readyAudioRef.current = null;
            }
        };
    }, []);
    
    const playReadySound = useCallback(() => {
        if (!readySoundEnabled) {
            console.log('[POS] Son désactivé, notification ignorée');
            return;
        }
        
        console.log('[POS] 🔊 Lecture du son de notification...');
        
        try {
            // Méthode 1: Utiliser l'audio préchargé
            if (readyAudioRef.current) {
                readyAudioRef.current.currentTime = 0;
                readyAudioRef.current.volume = 1.0; // S'assurer que le volume est au max
                
                const playPromise = readyAudioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            console.log('[POS] ✅ Son joué avec succès');
                        })
                        .catch((err) => {
                            console.warn('[POS] ⚠️ Erreur lecture audio (autoplay bloqué?):', err.message);
                            // Méthode 2: Créer un nouvel audio si le premier échoue
                            try {
                                const fallbackAudio = new Audio('/notification.mp3');
                                fallbackAudio.volume = 1.0;
                                fallbackAudio.play().catch(() => {});
                            } catch {}
                        });
                }
            }
            
            // Méthode 3: Utiliser Web Audio API si disponible (son plus fort)
            if (window.AudioContext || window.webkitAudioContext) {
                try {
                    const AudioContext = window.AudioContext || window.webkitAudioContext;
                    const audioCtx = new AudioContext();
                    
                    fetch('/notification.mp3')
                        .then(response => response.arrayBuffer())
                        .then(arrayBuffer => audioCtx.decodeAudioData(arrayBuffer))
                        .then(audioBuffer => {
                            const source = audioCtx.createBufferSource();
                            const gainNode = audioCtx.createGain();
                            
                            source.buffer = audioBuffer;
                            gainNode.gain.value = 2.0; // Amplifier le son (2x)
                            
                            source.connect(gainNode);
                            gainNode.connect(audioCtx.destination);
                            source.start(0);
                        })
                        .catch(() => {});
                } catch {}
            }
        } catch (err) {
            console.error('[POS] Erreur son notification:', err);
        }
    }, [readySoundEnabled]);
    
    // Fetch ready orders from kitchen
    const fetchReadyOrders = useCallback(async () => {
        try {
            const res = await api().get('/kitchen/tickets');
            const tickets = res.data || [];
            
            // Filter only "pret" (ready) orders and remove duplicates by order_id
            const seenOrderIds = new Set();
            const ready = tickets
                .filter(t => {
                    // Only include "pret" status
                    if (t.kitchen_status !== 'pret') return false;
                    
                    // Remove duplicates
                    const orderId = t.order_id || t.id;
                    if (seenOrderIds.has(orderId)) {
                        console.warn('[fetchReadyOrders] Doublon ignoré:', orderId);
                        return false;
                    }
                    seenOrderIds.add(orderId);
                    return true;
                })
                .map(t => {
                    // Calculate time since ready
                    let readySince = 0;
                    if (t.ready_at) {
                        const readyTime = new Date(t.ready_at);
                        readySince = Math.floor((new Date() - readyTime) / 60000);
                    }
                    return {
                        ...t,
                        ready_since: readySince
                    };
                })
                // Trier par temps d'attente décroissant (plus vieux en premier)
                .sort((a, b) => (b.ready_since || 0) - (a.ready_since || 0));
            
            // Check if new orders became ready
            if (ready.length > previousReadyCount && previousReadyCount >= 0) {
                playReadySound();
                setShowReadyPanel(true);
                
                // Show toast for new ready orders
                const newCount = ready.length - previousReadyCount;
                if (newCount > 0 && previousReadyCount > 0) {
                    toast.success(`🔔 ${newCount} nouvelle${newCount > 1 ? 's' : ''} commande${newCount > 1 ? 's' : ''} prête${newCount > 1 ? 's' : ''} !`, {
                        duration: 5000
                    });
                }
            }
            
            setPreviousReadyCount(ready.length);
            setReadyOrders(ready);
        } catch (error) {
            console.error('Error fetching ready orders:', error);
        }
    }, [api, previousReadyCount, playReadySound]);
    
    // Poll for ready orders every 5 seconds
    useEffect(() => {
        fetchReadyOrders();
        const interval = setInterval(fetchReadyOrders, 60000); // Refresh every 60 seconds (1 minute)
        return () => clearInterval(interval);
    }, [fetchReadyOrders]);

    // ========================================
    // REGISTER CLOSURE DETECTION (WebSocket + Polling)
    // ========================================
    
    // Handle forced redirect when register is closed
    const handleRegisterClosed = useCallback((reason = 'normal') => {
        setRegisterClosed(true);
        setRegisterClosedReason(reason);
        
        // Clear any local data
        setCart([]);
        setCashSession(null);
        
        // Show prominent notification
        const message = reason === 'emergency' 
            ? 'Cette caisse a été fermée d\'urgence par un administrateur.'
            : 'Cette caisse a été fermée. Vous allez être redirigé.';
        
        toast.error(message, { duration: 5000 });
        
        // Redirect after a short delay
        setTimeout(() => {
            navigate('/dashboard', { replace: true });
        }, 2000);
    }, [navigate]);
    
    // WebSocket connection for real-time register closure detection
    useEffect(() => {
        if (!cashSession?.register_id || !user?.token) return;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = backendUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProtocol}://${wsHost}/api/ws/pos/${cashSession.register_id}?token=${user.token}`;
        
        try {
            wsRef.current = new WebSocket(wsUrl);
            
            wsRef.current.onopen = () => {
                console.log('[POS] WebSocket connected for register:', cashSession.register_id);
            };
            
            wsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log('[POS] WebSocket message received:', data.type, data.action);
                    
                    // Handle register closure message
                    if (data.type === 'register_closed') {
                        console.log('[POS] Register closed via WebSocket:', data);
                        handleRegisterClosed(data.reason || 'normal');
                    }
                    
                    // Handle session update (locked, transferred, etc.)
                    if (data.type === 'session_update') {
                        if (data.session_status === 'closed') {
                            handleRegisterClosed('normal');
                        }
                    }
                    
                    // Handle kitchen ready notification
                    if (data.type === 'order_update' && data.action === 'kitchen_ready') {
                        // Le WebSocket envoie 'order' ou 'order_data' selon la source
                        const orderData = data.order_data || data.order || {};
                        console.log('[POS] Commande cuisine prête:', orderData);
                        const tableNum = orderData.table_number || '?';
                        const orderNum = orderData.order_number?.split('-').pop() || '?';
                        
                        // Check if this notification is targeted to specific user
                        const targetUserId = orderData.target_user_id || orderData.created_by_id;
                        const isTargetedToMe = !targetUserId || targetUserId === user?.id;
                        
                        // Only show notification if:
                        // 1. No target specified (broadcast to all) OR
                        // 2. Target is current user
                        if (isTargetedToMe) {
                            // Jouer le son de notification
                            playReadySound();
                            
                            // Afficher le popup de notification
                            setNotificationPopup({
                                type: 'kitchen',
                                tableNumber: tableNum,
                                orderNumber: orderNum,
                                orderId: orderData.order_id || orderData.id,
                                timestamp: new Date()
                            });
                            
                            // Toast de confirmation
                            toast.success(`🍽️ Cuisine: Commande table ${tableNum} prête !`, {
                                duration: 8000,
                                id: `kitchen-ready-${orderData.order_id || orderData.id}`
                            });
                        } else {
                            console.log(`[POS] Notification cuisine ignorée (ciblée pour ${targetUserId}, je suis ${user?.id})`);
                        }
                        
                        // Rafraîchir la liste des commandes prêtes pour tous
                        fetchReadyOrders();
                    }
                    
                    // Handle bar ready notification
                    if (data.type === 'order_update' && data.action === 'bar_ready') {
                        // Le WebSocket envoie 'order' ou 'order_data' selon la source
                        const orderData = data.order_data || data.order || {};
                        console.log('[POS] Commande bar prête:', orderData);
                        const tableNum = orderData.table_number || '?';
                        const orderNum = orderData.order_number?.split('-').pop() || '?';
                        
                        // Check if this notification is targeted to specific user
                        const targetUserId = orderData.target_user_id || orderData.created_by_id;
                        const isTargetedToMe = !targetUserId || targetUserId === user?.id;
                        
                        // Only show notification if:
                        // 1. No target specified (broadcast to all) OR
                        // 2. Target is current user
                        if (isTargetedToMe) {
                            // Jouer le son de notification
                            playReadySound();
                            
                            // Afficher le popup de notification
                            setNotificationPopup({
                                type: 'bar',
                                tableNumber: tableNum,
                                orderNumber: orderNum,
                                orderId: orderData.order_id || orderData.id,
                                timestamp: new Date()
                            });
                            
                            // Toast de confirmation
                            toast.success(`🍹 Bar: Commande table ${tableNum} prête !`, {
                                duration: 8000,
                                id: `bar-ready-${orderData.order_id || orderData.id}`
                            });
                        } else {
                            console.log(`[POS] Notification bar ignorée (ciblée pour ${targetUserId}, je suis ${user?.id})`);
                        }
                        
                        // Rafraîchir la liste des commandes prêtes pour tous
                        fetchReadyOrders();
                    }
                    
                    // Handle stock update - update product quantities in real-time
                    if (data.type === 'stock_update') {
                        console.log('[POS] Stock update received:', data.updates?.length, 'products');
                        setProducts(prevProducts => {
                            return prevProducts.map(product => {
                                const update = data.updates?.find(u => u.product_id === product.id);
                                if (update) {
                                    return {
                                        ...product,
                                        stock_quantity: update.new_quantity,
                                        stock_status: update.status
                                    };
                                }
                                return product;
                            });
                        });
                    }
                    
                    // Handle product update (price change, availability, etc.)
                    if (data.type === 'product_update') {
                        console.log('[POS] Product update received:', data.action, data.product?.name);
                        if (data.action === 'updated' && data.product) {
                            setProducts(prevProducts => 
                                prevProducts.map(p => p.id === data.product.id ? {...p, ...data.product} : p)
                            );
                        }
                    }
                } catch (e) {
                    console.error('[POS] WebSocket message parse error:', e);
                }
            };
            
            wsRef.current.onclose = (event) => {
                console.log('[POS] WebSocket disconnected:', event.code, event.reason);
                
                // If closed with code 4100, it means register was closed
                if (event.code === 4100) {
                    handleRegisterClosed('normal');
                }
            };
            
            wsRef.current.onerror = (error) => {
                console.error('[POS] WebSocket error:', error);
            };
        } catch (error) {
            console.error('[POS] WebSocket connection error:', error);
        }
        
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [cashSession?.register_id, user?.token, handleRegisterClosed]);
    
    // ========================================
    // GLOBAL WEBSOCKET FOR BAR/KITCHEN NOTIFICATIONS
    // ========================================
    useEffect(() => {
        if (!user?.token) return;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = backendUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProtocol}://${wsHost}/api/ws/pos/global?token=${user.token}`;
        
        let pingInterval = null;
        let reconnectTimeout = null;
        
        const connectWebSocket = () => {
            try {
                console.log('[POS] Connecting to global WebSocket:', wsUrl.substring(0, 60) + '...');
                globalWsRef.current = new WebSocket(wsUrl);
                
                globalWsRef.current.onopen = () => {
                    console.log('[POS] ✅ Global WebSocket connected for notifications');
                    
                    // Start ping interval to keep connection alive
                    pingInterval = setInterval(() => {
                        if (globalWsRef.current?.readyState === WebSocket.OPEN) {
                            globalWsRef.current.send(JSON.stringify({ type: 'ping' }));
                        }
                    }, 30000); // Ping every 30 seconds
                };
                
                globalWsRef.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('[POS Global] WebSocket message:', data.type, data.action);
                        
                        // Ignore pong and config messages
                        if (data.type === 'pong' || data.type === 'global_config') {
                            return;
                        }
                        
                        // Handle bar ready notification
                        if (data.type === 'order_update' && data.action === 'bar_ready') {
                            const orderData = data.order_data || data.order || {};
                            console.log('[POS] 🍹 BAR NOTIFICATION REÇUE:', orderData);
                            const tableNum = orderData.table_number || '?';
                            const orderNum = orderData.order_number?.split('-').pop() || '?';
                            
                            // Jouer le son de notification
                            playReadySound();
                            
                            // Afficher le popup de notification
                            setNotificationPopup({
                                type: 'bar',
                                tableNumber: tableNum,
                                orderNumber: orderNum,
                                orderId: orderData.order_id || orderData.id,
                                timestamp: new Date()
                            });
                            
                            // Toast de confirmation
                            toast.success(`🍹 Bar: Commande table ${tableNum} prête !`, {
                                duration: 8000,
                                id: `bar-ready-${orderData.order_id || Date.now()}`
                            });
                            
                            // Rafraîchir la liste des commandes prêtes
                            fetchReadyOrders();
                        }
                        
                        // Handle kitchen ready notification
                        if (data.type === 'order_update' && data.action === 'kitchen_ready') {
                            const orderData = data.order_data || data.order || {};
                            console.log('[POS] 🍽️ CUISINE NOTIFICATION REÇUE:', orderData);
                            const tableNum = orderData.table_number || '?';
                            const orderNum = orderData.order_number?.split('-').pop() || '?';
                            
                            // Jouer le son de notification
                            playReadySound();
                            
                            // Afficher le popup de notification
                            setNotificationPopup({
                                type: 'kitchen',
                                tableNumber: tableNum,
                                orderNumber: orderNum,
                                orderId: orderData.order_id || orderData.id,
                                timestamp: new Date()
                            });
                            
                            // Toast de confirmation
                            toast.success(`🍽️ Cuisine: Commande table ${tableNum} prête !`, {
                                duration: 8000,
                                id: `kitchen-ready-${orderData.order_id || Date.now()}`
                            });
                            
                            // Rafraîchir la liste des commandes prêtes
                            fetchReadyOrders();
                        }
                    } catch (e) {
                        console.error('[POS Global] WebSocket message parse error:', e);
                    }
                };
                
                globalWsRef.current.onclose = (event) => {
                    console.log('[POS] Global WebSocket disconnected. Code:', event.code, 'Reason:', event.reason);
                    if (pingInterval) clearInterval(pingInterval);
                    
                    // Reconnect after 5 seconds if not manually closed
                    if (event.code !== 1000) {
                        reconnectTimeout = setTimeout(() => {
                            console.log('[POS] Attempting to reconnect global WebSocket...');
                            connectWebSocket();
                        }, 5000);
                    }
                };
                
                globalWsRef.current.onerror = (error) => {
                    console.error('[POS] Global WebSocket error:', error);
                };
            } catch (error) {
                console.error('[POS] Global WebSocket connection error:', error);
            }
        };
        
        connectWebSocket();
        
        return () => {
            if (pingInterval) clearInterval(pingInterval);
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            if (globalWsRef.current) {
                globalWsRef.current.close(1000, 'Component unmount');
            }
        };
    }, [user?.token, playReadySound, fetchReadyOrders]);
    
    // Polling fallback: Check session validity every 15 seconds
    useEffect(() => {
        if (!cashSession?.id && !cashSession?.register_id) return;
        
        const checkSessionValidity = async () => {
            try {
                // Use the new session validation endpoint with headers
                const response = await api().get('/pos/session/validate', {
                    headers: {
                        'X-Session-Id': cashSession?.id || '',
                        'X-Register-Id': cashSession?.register_id || ''
                    }
                });
                
                // If session is not valid, redirect
                if (response.data && !response.data.valid) {
                    handleRegisterClosed(response.data.reason || 'normal');
                }
            } catch (error) {
                // 423 = Locked (session closed)
                if (error.response?.status === 423) {
                    const detail = error.response.data?.detail;
                    const reason = detail?.reason || 'normal';
                    const message = detail?.message || 'Session fermée';
                    
                    // Show the error message
                    toast.error(message, { duration: 5000 });
                    handleRegisterClosed(reason);
                }
            }
        };
        
        // Check immediately on mount
        checkSessionValidity();
        
        // Then check every 15 seconds
        const interval = setInterval(checkSessionValidity, 15000);
        
        return () => clearInterval(interval);
    }, [api, cashSession?.id, cashSession?.register_id, handleRegisterClosed]);
    
    // ========================================
    // REAL-TIME SYNC EVENT HANDLERS
    // ========================================
    
    // Fetch products separately (for real-time refresh)
    const fetchProducts = useCallback(async () => {
        try {
            const prodRes = await api().get('/pos/products?available_only=false');
            const productsData = prodRes.data || [];
            setProducts(productsData);
            console.log('[SYNC] Products refreshed:', productsData.length);
        } catch (error) {
            console.error('Error refreshing products:', error);
        }
    }, [api]);
    
    // ========================================
    // STOCK SYNC POLLING (Fallback si WebSocket échoue)
    // Garantit que tous les profils voient le même stock en temps réel
    // ========================================
    useEffect(() => {
        // Polling des produits toutes les 15 secondes pour synchronisation stock temps réel
        const stockSyncInterval = setInterval(() => {
            fetchProducts();
        }, 15000); // 15 secondes - assez fréquent pour stock temps réel
        
        return () => clearInterval(stockSyncInterval);
    }, [fetchProducts]);
    
    // Fetch cash session (for real-time refresh)
    const fetchCashSession = useCallback(async () => {
        try {
            const res = await api().get('/pos/cash-session');
            setCashSession(res.data);
        } catch (error) {
            console.error('Error fetching cash session:', error);
            
            // Don't clear session if cash closing modal is open - user is in the process of closing
            if (cashClosingModal.open) {
                console.log('Cash closing modal is open, keeping session state');
                return;
            }
            
            // Handle 423 - Session closed
            if (error.response?.status === 423) {
                const detail = error.response.data?.detail;
                const reason = detail?.closure_type || detail?.reason || 'normal';
                
                // Show message and trigger redirect
                toast.error(detail?.message || 'Votre session a été fermée.', { duration: 5000 });
                handleRegisterClosed(reason);
            }
            
            // Clear session on error
            setCashSession(null);
        } finally {
            // Mark session loading as complete
            setSessionLoading(false);
        }
    }, [api, handleRegisterClosed, cashClosingModal.open]);
    
    // Handle real-time table updates
    useEffect(() => {
        const handleTableUpdate = (data) => {
            console.log('[SYNC] Table update:', data.action, data.table?.number);
            
            if (data.action === 'status_changed' || data.action === 'assigned') {
                // Update tables state
                setTables(prev => prev.map(t => 
                    t.id === data.table?.id ? { ...t, ...data.table } : t
                ));
                
                // If it's our selected table, update it
                if (selectedTable?.id === data.table?.id) {
                    setSelectedTable(prev => ({ ...prev, ...data.table }));
                }
            } else if (data.action === 'freed') {
                setTables(prev => prev.map(t => 
                    t.id === data.table?.id 
                        ? { ...t, status: 'available', current_order_id: null }
                        : t
                ));
            }
        };
        
        const handleOrderUpdate = (data) => {
            console.log('[SYNC] Order update:', data.action, data.order?.order_number);
            
            // Update current table order if it matches
            if (currentTableOrder?.id === data.order?.id) {
                if (data.action === 'paid' || data.action === 'cancelled') {
                    setCurrentTableOrder(null);
                    setSentItemIds(new Set());
                } else {
                    setCurrentTableOrder(prev => ({ ...prev, ...data.order }));
                }
            }
            
            // Notify if order is ready
            if (data.action === 'ready' && data.order?.table_number === selectedTable?.number) {
                toast.success(`Commande Table ${data.order.table_number} prête !`, {
                    icon: '🍽️',
                    duration: 5000
                });
            }
        };
        
        const handleStockUpdate = (data) => {
            console.log('[SYNC] Stock update:', data.updates?.length, 'products');
            if (data.updates?.length) {
                setProducts(prev => prev.map(product => {
                    const update = data.updates.find(u => u.product_id === product.id);
                    if (update) {
                        return {
                            ...product,
                            stock_quantity: update.new_quantity,
                            stock_status: update.status || (update.new_quantity > 5 ? 'OK' : update.new_quantity > 0 ? 'low' : 'out')
                        };
                    }
                    return product;
                }));
            }
        };
        
        const handleForceRefresh = (data) => {
            console.log('[SYNC] Force refresh:', data.reason);
            // Reload all data
            fetchProducts();
            fetchAvailableTables();
            fetchCashSession();
            toast.info('Données mises à jour', { duration: 2000 });
        };
        
        const handleRegisterStatus = (data) => {
            console.log('[SYNC] Register status:', data.status, data.register_id);
            
            // If our register was closed, handle it
            if (data.register_id === cashSession?.register_id && data.status === 'closed') {
                handleRegisterClosed('admin_close');
            }
        };
        
        const handleCartSync = (data) => {
            // Only relevant for admin supervision
            if (data.register_id !== cashSession?.register_id) {
                console.log('[SYNC] Other register cart update:', data.register_id);
            }
        };
        
        // Subscribe to events
        const unsubTable = onSyncEvent(SyncEventType.TABLE_UPDATE, handleTableUpdate);
        const unsubOrder = onSyncEvent(SyncEventType.ORDER_UPDATE, handleOrderUpdate);
        const unsubStock = onSyncEvent(SyncEventType.STOCK_UPDATE, handleStockUpdate);
        const unsubRefresh = onSyncEvent(SyncEventType.FORCE_REFRESH, handleForceRefresh);
        const unsubRegister = onSyncEvent(SyncEventType.REGISTER_STATUS, handleRegisterStatus);
        const unsubCart = onSyncEvent(SyncEventType.CART_SYNC, handleCartSync);
        
        return () => {
            unsubTable();
            unsubOrder();
            unsubStock();
            unsubRefresh();
            unsubRegister();
            unsubCart();
        };
    }, [onSyncEvent, selectedTable, currentTableOrder, cashSession?.register_id, handleRegisterClosed, fetchProducts, fetchCashSession]);

    // Fetch available tables for quick selection
    const fetchAvailableTables = useCallback(async () => {
        try {
            const res = await api().get('/tables');
            let tables = res.data || [];
            
            // FILTRAGE PAR RÔLE : Les serveurs voient leurs tables assignées ET toutes les tables libres
            if (isServer && !canViewAllTables) {
                tables = tables.filter(t => 
                    // Tables libres visibles par tous les serveurs (pour pouvoir les prendre)
                    t.status === 'libre' || t.status === 'free' || t.status === 'available' ||
                    // Tables assignées au serveur actuel
                    t.assigned_to_id === user?.id || 
                    t.server_id === user?.id ||
                    t.waiter_id === user?.id
                );
            }
            
            // Sort by number and filter only "libre" tables for the dropdown
            const freeTables = tables
                .filter(t => t.status === 'libre' || t.status === 'free' || t.status === 'available')
                .sort((a, b) => a.number - b.number);
            setAvailableTables(freeTables);
        } catch (error) {
            console.error('Error fetching tables:', error);
        }
    }, [api, isServer, canViewAllTables, user?.id]);

    // Assign cart to a table
    const assignCartToTable = async (table) => {
        // RÈGLE CRITIQUE MISE À JOUR:
        // - Si la table a une commande active → conserver le panier
        // - Si la table est VIDE → vider le panier
        
        try {
            // Vérifier si la table a une commande active
            const tableHasActiveOrder = table.status === 'occupee' && 
                (table.sent_items?.length > 0 || table.activeOrderId);
            
            if (!tableHasActiveOrder) {
                // TABLE VIDE: Vider le panier AVANT de lier
                console.log(`[POS] Table ${table.number} est VIDE - vidage du panier`);
                
                // Activer le blocage de réhydratation
                setBlockCartRehydration(true);
                
                // Vider le localStorage
                localStorage.removeItem(CART_TABLE_KEY_V2);
                localStorage.removeItem(CART_STORAGE_KEY_V2);
                localStorage.removeItem('pos_pending_cart');
                
                // Vider le state local
                setCart([]);
                setOrderItems([]);
                setSentItemIds(new Set());
                setCurrentTableOrder(null);
                
                // Vider le panier backend
                await clearBackendCart();
            }
            
            // Lier la table au panier
            await linkToTable(table.id, !tableHasActiveOrder);
            
            // Mettre à jour l'état local de la table sélectionnée
            setSelectedTable(table);
            setShowTableSelect(false);
            
            // Charger la commande de la table si elle existe
            if (tableHasActiveOrder) {
                await loadTableOrder(table, true); // keepCurrentCart = true pour conserver le panier local
                console.log(`[POS] Panier lié à la table ${table.number} - items conservés`);
            } else {
                // Désactiver le blocage après un court délai
                setTimeout(() => {
                    setBlockCartRehydration(false);
                }, 500);
                console.log(`[POS] Table ${table.number} ouverte avec panier vide`);
            }
            
        } catch (error) {
            console.error('Error linking table:', error);
            toast.error('Erreur lors de la liaison de la table');
            setBlockCartRehydration(false);
        }
    };

    // NOTE: Plus de persistance localStorage - la caisse s'ouvre toujours propre
    // Le panier est temporaire et sert uniquement à préparer l'envoi en cours
    
    // Real-time status update for cart items
    const refreshCartStatuses = useCallback(async () => {
        if (!currentTableOrder?.id || cart.length === 0) return;
        
        try {
            const res = await api().get(`/pos/orders/${currentTableOrder.id}`);
            if (res.data && res.data.items) {
                const updatedItems = res.data.items;
                
                setCart(prevCart => {
                    let hasChanges = false;
                    const newCart = prevCart.map(cartItem => {
                        // Find matching item by product_id or id
                        const updatedItem = updatedItems.find(ui => 
                            ui.id === cartItem.id || 
                            (ui.product_id === cartItem.product_id && ui.quantity === cartItem.quantity)
                        );
                        
                        if (updatedItem) {
                            const newKitchenStatus = updatedItem.kitchen_status;
                            const newBarStatus = updatedItem.bar_status;
                            
                            // Check if status changed
                            if (newKitchenStatus !== cartItem.kitchen_status || newBarStatus !== cartItem.bar_status) {
                                hasChanges = true;
                                
                                // Show toast for status change to "pret"
                                if ((newKitchenStatus === 'pret' && cartItem.kitchen_status !== 'pret') ||
                                    (newBarStatus === 'pret' && cartItem.bar_status !== 'pret')) {
                                    toast.success(
                                        `${cartItem.product_name} est prêt !`,
                                        { icon: newBarStatus === 'pret' ? '🍹' : '🍳', duration: 5000 }
                                    );
                                    playReadySound();
                                }
                                
                                return {
                                    ...cartItem,
                                    kitchen_status: newKitchenStatus,
                                    bar_status: newBarStatus,
                                    sent_to_kitchen: !!(newKitchenStatus || newBarStatus)
                                };
                            }
                        }
                        return cartItem;
                    });
                    
                    return hasChanges ? newCart : prevCart;
                });
            }
        } catch (error) {
            // Silently fail - status refresh is optional
        }
    }, [api, currentTableOrder?.id, cart.length, playReadySound]);
    
    // Poll cart item statuses every 5 seconds when there's an active order
    useEffect(() => {
        if (!currentTableOrder?.id) return;
        
        const interval = setInterval(refreshCartStatuses, 60000); // Refresh every 60 seconds (1 minute)
        return () => clearInterval(interval);
    }, [refreshCartStatuses, currentTableOrder?.id]);
    
    // Mark order as served
    const markOrderServed = async (orderId) => {
        // Sauvegarde de l'état pour rollback
        const previousReadyOrders = [...readyOrders];
        
        // Mise à jour optimiste - retirer immédiatement de la liste
        setReadyOrders(prev => prev.filter(o => o.order_id !== orderId));
        
        try {
            // Utiliser le nouvel endpoint POS dédié (pas l'endpoint cuisine)
            const response = await api().post(`/pos/orders/${orderId}/mark-served`);
            
            if (response.data?.success) {
                toast.success(`✅ ${response.data.message || 'Commande marquée comme servie'}`, {
                    duration: 3000
                });
                
                // Rafraîchir la liste pour synchroniser avec la base
                fetchReadyOrders();
            } else {
                throw new Error(response.data?.detail || 'Réponse inattendue du serveur');
            }
        } catch (error) {
            // Rollback - restaurer l'état précédent
            setReadyOrders(previousReadyOrders);
            
            // Message d'erreur détaillé
            const errorDetail = error.response?.data?.detail 
                || error.response?.data?.message 
                || error.message 
                || 'Erreur inconnue';
            
            const statusCode = error.response?.status;
            
            console.error('[markOrderServed] Échec:', {
                orderId,
                statusCode,
                errorDetail,
                fullError: error.response?.data || error.message
            });
            
            // Toast avec message explicite
            if (statusCode === 403) {
                toast.error(`⛔ Permission refusée: ${errorDetail}`);
            } else if (statusCode === 404) {
                toast.error(`❌ Commande non trouvée: ${errorDetail}`);
            } else if (statusCode === 400) {
                toast.error(`⚠️ ${errorDetail}`);
            } else {
                toast.error(`❌ Erreur: ${errorDetail}`);
            }
        }
    };
    
    // ========================================
    // SUPERVISION BAR - Real-time stats polling
    // ========================================
    
    const fetchSupervisionStats = useCallback(async () => {
        if (!isManager) return;
        
        try {
            const res = await api().get('/pos/supervision/live-stats');
            setSupervisionStats(res.data);
            setRecentActivity(res.data.recent_activity || []);
        } catch (error) {
            // Silently fail - supervision is optional
            console.error('Error fetching supervision stats:', error);
        }
    }, [api, isManager]);
    
    // Poll supervision stats every 10 seconds for managers
    useEffect(() => {
        if (!isManager) return;
        
        fetchSupervisionStats();
        const interval = setInterval(fetchSupervisionStats, 60000); // Refresh every 60 seconds (1 minute)
        return () => clearInterval(interval);
    }, [fetchSupervisionStats, isManager]);
    
    // ========================================
    // CASH SESSION MANAGEMENT
    // ========================================
    
    // fetchCashSession is defined earlier in the component (before the real-time sync handlers)
    
    useEffect(() => {
        fetchCashSession();
        // Refresh session every 30 seconds
        const interval = setInterval(fetchCashSession, 60000); // Refresh cash session every 60 seconds (1 minute)
        return () => clearInterval(interval);
    }, [fetchCashSession]);
    
    const handleCloseCash = async () => {
        if (!closingCash || parseFloat(closingCash) < 0) {
            toast.error('Veuillez entrer un montant valide');
            return;
        }
        
        // Check if gap exists and notes are required
        const expected = cashSession?.expected_cash || 0;
        const counted = parseFloat(closingCash);
        const hasGap = counted !== expected;
        
        // Admin can close without justification
        if (hasGap && !closingNotes && !forceCloseMode && !isAdmin) {
            toast.error('Le motif de l\'écart est obligatoire');
            return;
        }
        
        // If in force close mode, require reason (except for Admin)
        if (forceCloseMode && !forceCloseReason && !isAdmin) {
            toast.error('Le motif de la fermeture forcée est obligatoire');
            return;
        }
        
        try {
            setClosingLoading(true);
            const res = await api().post('/pos/cash-session/close', {
                closing_cash: counted,
                notes: closingNotes || (isAdmin ? 'Fermeture Admin' : null),
                force_close: forceCloseMode,
                force_close_reason: forceCloseMode ? (forceCloseReason || (isAdmin ? 'Fermeture Admin' : null)) : null,
                admin_override: isAdmin  // Flag to indicate admin override
            });
            
            if (res.data.status === 'pending_validation') {
                toast.success('Clôture soumise ! En attente de validation par la direction.');
            } else {
                toast.success('Caisse clôturée avec succès');
            }
            
            setClosingDialog({ open: false });
            setClosingCash('');
            setClosingNotes('');
            setOpenOrdersCheck(null);
            setForceCloseMode(false);
            setForceCloseReason('');
            fetchCashSession();
        } catch (error) {
            // Handle open orders error specially
            const detail = error.response?.data?.detail;
            if (detail?.code === 'OPEN_ORDERS_EXIST') {
                setOpenOrdersCheck({
                    count: detail.open_orders_count,
                    orders: detail.open_orders,
                    total: detail.total_open_amount
                });
                toast.error(detail.message);
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la clôture');
            }
        } finally {
            setClosingLoading(false);
        }
    };
    
    // Check for open orders before opening closing dialog
    const handleOpenClosingDialog = async () => {
        setCheckingOpenOrders(true);
        setOpenOrdersCheck(null);
        setForceCloseMode(false);
        setForceCloseReason('');
        
        try {
            const res = await api().get('/pos/cash-session/open-orders');
            if (res.data.has_open_orders) {
                setOpenOrdersCheck({
                    count: res.data.open_orders_count,
                    orders: res.data.open_orders,
                    total: res.data.total_open_amount
                });
            }
            setClosingDialog({ open: true });
        } catch (error) {
            toast.error('Erreur lors de la vérification des commandes');
        } finally {
            setCheckingOpenOrders(false);
        }
    };
    
    // ========================================
    // REGISTER OPENING FUNCTIONS
    // ========================================
    
    // Load available registers for opening
    const loadAvailableRegisters = async () => {
        try {
            const res = await api().get('/pos/registers');
            const registers = res.data || [];
            // Filter to show only closed registers
            setAvailableRegisters(registers.filter(r => r.is_active && (r.status === 'closed' || !r.status)));
        } catch (error) {
            console.error('Error loading registers:', error);
            toast.error('Erreur lors du chargement des caisses');
        }
    };
    
    // Open a register session
    const handleOpenRegister = async (data) => {
        try {
            // Backend expects opening_cash as query parameter, not in body
            const openingCash = data.opening_cash || 0;
            const res = await api().post(`/pos/registers/${data.register_id}/open?opening_cash=${openingCash}`);
            
            if (res.data) {
                toast.success(`Caisse ouverte avec succès`);
                setOpeningDialog({ open: false });
                // Refresh session
                fetchCashSession();
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'ouverture de la caisse');
            throw error;
        }
    };
    
    // Open the register selection dialog
    const handleShowOpeningDialog = async () => {
        await loadAvailableRegisters();
        setOpeningDialog({ open: true });
    };
    
    // ========================================
    // KEYBOARD SHORTCUTS
    // ========================================
    
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSearchQuery('');
                setAccompanimentDialog({ open: false, product: null });
            }
            if (e.ctrlKey && e.key === 'f') {
                e.preventDefault();
                searchRef.current?.focus();
            }
            if (e.ctrlKey && e.key === 'Enter' && cart.length > 0) {
                e.preventDefault();
                handleSendToKitchen();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [cart]);
    
    // ========================================
    // STOCK STATUS HELPERS
    // ========================================
    
    const getStockStatus = (product) => {
        if (product.is_available === false) return 'out';
        if (product.stock_quantity !== undefined && product.stock_quantity !== null) {
            if (product.stock_quantity <= 0) return 'out';
            if (product.stock_quantity <= 5) return 'low';
        }
        if (product.stock_status === 'low') return 'low';
        if (product.stock_status === 'out') return 'out';
        return 'ok';
    };
    
    const getStockColor = (status) => {
        switch (status) {
            case 'out': return 'bg-red-500';
            case 'low': return 'bg-orange-500';
            default: return 'bg-green-500';
        }
    };
    
    const getStockBorderColor = (status) => {
        switch (status) {
            case 'out': return 'border-red-300 bg-red-50/50 dark:bg-red-950/20';
            case 'low': return 'border-orange-300 bg-orange-50/50 dark:bg-orange-950/20';
            default: return 'border-transparent hover:border-primary/30';
        }
    };
    
    // Get quantity of a product in cart
    const getProductQuantityInCart = useCallback((productId) => {
        return cart.reduce((total, item) => {
            if (item.product_id === productId) {
                return total + item.quantity;
            }
            return total;
        }, 0);
    }, [cart]);
    
    // Category color schemes for professional POS look
    // Premium color scheme for categories - Refined Original Theme
    // Garde l'identité colorée mais plus élégante
    const getCategoryColorScheme = (categoryName) => {
        // HARMONIZED COLOR SCHEME - Emerald base with subtle variations
        // All cards use the same neutral base with emerald accents
        const baseScheme = { 
            bg: 'bg-white dark:bg-zinc-800', 
            border: 'border-gray-200 dark:border-zinc-700 hover:border-emerald-400 dark:hover:border-emerald-500', 
            accent: 'bg-emerald-500', 
            text: 'text-gray-900 dark:text-white' 
        };
        
        if (!categoryName) return baseScheme;
        const name = categoryName.toLowerCase();
        
        // All categories now use the same harmonized scheme
        // Only the accent bar color varies slightly for visual categorization
        if (name.includes('entr') || name.includes('starter') || name.includes('appetizer')) {
            return { ...baseScheme, accent: 'bg-emerald-500' };
        }
        if (name.includes('plat') || name.includes('main') || name.includes('dish') || name.includes('viande') || name.includes('poisson')) {
            return { ...baseScheme, accent: 'bg-emerald-600' };
        }
        if (name.includes('dessert') || name.includes('sweet') || name.includes('gateau') || name.includes('glace')) {
            return { ...baseScheme, accent: 'bg-emerald-400' };
        }
        if (name.includes('boisson') || name.includes('drink') || name.includes('jus') || name.includes('eau') || name.includes('soda') || name.includes('vin') || name.includes('biere') || name.includes('froid') || name.includes('chaud')) {
            return { ...baseScheme, accent: 'bg-emerald-500' };
        }
        
        return baseScheme;
    };
    
    // Haptic feedback for mobile
    const triggerHapticFeedback = () => {
        if (navigator.vibrate) {
            navigator.vibrate(50);
        }
    };
    
    // ========================================
    // CART MANAGEMENT - WITH FAST SERVICE MODE
    // ========================================
    
    const addToCart = useCallback((product) => {
        const stockStatus = getStockStatus(product);
        if (stockStatus === 'out') {
            toast.error('Produit en rupture de stock');
            return;
        }
        
        // Check if product needs accompaniment
        const needsAccompaniment = product.has_accompaniments || 
            product.requires_accompaniment || 
            (product.category === 'plats' && accompaniments.length > 0);
        
        if (needsAccompaniment && accompaniments.length > 0) {
            setAccompanimentDialog({ open: true, product });
            setSelectedAccompaniment(null);
            return;
        }
        
        // Direct add
        addProductToCartDirect(product);
    }, [accompaniments]);
    
    const addProductToCartDirect = useCallback(async (product, accompaniment = null) => {
        // Prepare item for backend
        const itemData = {
            product_id: product.id,
            product_name: product.name,
            unit_price: product.price,
            quantity: 1,
            destination: product.destination || 'cuisine',
            category: product.category,
            supplements: [],
            accompaniment: accompaniment ? {
                id: accompaniment.id,
                name: accompaniment.name,
                price: accompaniment.price || 0
            } : null,
            notes: ''
        };
        
        try {
            // Add to backend cart (this persists and syncs)
            // Force "bar" destination when on Bar register
            const itemDestination = isBarRegister ? 'bar' : (product.destination || 'cuisine');
            
            const updatedCart = await addItemToBackend(product, {
                quantity: 1,
                destination: itemDestination,
                category: product.category,
                accompaniment: accompaniment ? {
                    id: accompaniment.id,
                    name: accompaniment.name,
                    price: accompaniment.price || 0
                } : null
            });
            
            // Update local state from backend response
            if (updatedCart?.items) {
                const mappedItems = updatedCart.items.map(item => ({
                    id: item.id,
                    product_id: item.product_id,
                    product_name: item.product_name,
                    name: item.product_name,
                    price: item.unit_price,
                    quantity: item.quantity,
                    destination: item.destination,
                    category: item.category,
                    supplements: item.supplements || [],
                    accompaniment: item.accompaniment,
                    accompaniment_id: item.accompaniment?.id || null,
                    notes: item.notes,
                    sent_to_kitchen: false
                }));
                setCart(mappedItems);
            }
            
            toast.success(`${product.name} ajouté`, { duration: 800 });
            
        } catch (error) {
            console.error('Error adding to cart:', error);
            
            // Fallback: add to local cart only (offline mode)
            setCart(prev => {
                const existingIndex = prev.findIndex(item => 
                    item.product_id === product.id && 
                    item.accompaniment_id === (accompaniment?.id || null) &&
                    (!item.supplements || item.supplements.length === 0)
                );
                
                if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex].quantity += 1;
                    return updated;
                }
                
                return [...prev, {
                    id: `cart-${product.id}-${Date.now()}`,
                    product_id: product.id,
                    product_name: product.name,
                    price: product.price,
                    quantity: 1,
                    category: product.category,
                    destination: isBarRegister ? 'bar' : (product.destination || 'cuisine'),
                    image_url: product.image_url,
                    accompaniment,
                    accompaniment_id: accompaniment?.id || null,
                    supplements: [],
                    notes: '',
                    sent_to_kitchen: false
                }];
            });
            
            toast.success(`${product.name} ajouté (local)`, { duration: 800 });
        }
    }, [addItemToBackend]);
    
    // Fast Service Mode: 1 click = direct add with accompaniment
    const handleAccompanimentSelect = (accompaniment) => {
        const { product } = accompanimentDialog;
        
        if (fastServiceMode) {
            // Immediate add in fast service mode
            addProductToCartDirect(product, accompaniment);
            setAccompanimentDialog({ open: false, product: null });
        } else {
            setSelectedAccompaniment(accompaniment);
        }
    };
    
    const confirmAccompanimentSelection = () => {
        const { product } = accompanimentDialog;
        // Allow adding without accompaniment
        addProductToCartDirect(product, selectedAccompaniment || null);
        setAccompanimentDialog({ open: false, product: null });
    };
    
    // ========================================
    // CART SUPPLEMENTS (from cart line)
    // ========================================
    
    const openCartSupplementDialog = (index) => {
        // Pre-load existing supplements for the item
        const existingSupplements = cart[index]?.supplements || [];
        const productId = cart[index]?.product_id;
        
        // Trouver le produit pour obtenir ses suppléments autorisés
        const product = products.find(p => p.id === productId);
        const allowedSupplementIds = product?.allowed_supplements || null;
        
        setCartSupplementDialog({ 
            open: true, 
            cartIndex: index,
            selectedSupplements: [...existingSupplements], // Copy existing
            allowedSupplementIds: allowedSupplementIds // Passer les IDs autorisés
        });
    };
    
    // Handler for adding supplement in responsive dialog
    const handleAddSupplementInDialog = (supplement) => {
        setCartSupplementDialog(prev => {
            // Check if already selected
            if (prev.selectedSupplements.find(s => s.id === supplement.id)) {
                toast.info('Supplément déjà sélectionné');
                return prev;
            }
            return {
                ...prev,
                selectedSupplements: [...prev.selectedSupplements, supplement]
            };
        });
    };
    
    // Handler for removing supplement in responsive dialog
    const handleRemoveSupplementInDialog = (supplementId) => {
        setCartSupplementDialog(prev => ({
            ...prev,
            selectedSupplements: prev.selectedSupplements.filter(s => s.id !== supplementId)
        }));
    };
    
    // Handler for confirming supplements from responsive dialog
    const handleConfirmSupplements = () => {
        const { cartIndex, selectedSupplements } = cartSupplementDialog;
        if (cartIndex === null) return;
        
        setCart(prev => {
            const updated = [...prev];
            updated[cartIndex] = {
                ...updated[cartIndex],
                supplements: selectedSupplements
            };
            return updated;
        });
        
        // Update backend if item has ID
        const item = cart[cartIndex];
        if (item?.id) {
            updateBackendItemQuantity(item.id, item.quantity, item.notes, selectedSupplements)
                .catch(err => console.error('Error updating supplements:', err));
        }
        
        if (selectedSupplements.length > 0) {
            toast.success(`${selectedSupplements.length} supplément(s) ajouté(s)`);
        }
        
        setCartSupplementDialog({ open: false, cartIndex: null, selectedSupplements: [], allowedSupplementIds: null });
    };
    
    const addSupplementToCartItem = (supplement) => {
        const { cartIndex } = cartSupplementDialog;
        setCart(prev => {
            const updated = [...prev];
            const item = updated[cartIndex];
            if (!item.supplements) item.supplements = [];
            
            // Check if already added
            if (item.supplements.find(s => s.id === supplement.id)) {
                toast.info('Supplément déjà ajouté');
                return prev;
            }
            
            item.supplements.push(supplement);
            return updated;
        });
        toast.success(`+ ${supplement.name}`);
    };
    
    const removeSupplementFromCartItem = (cartIndex, supplementIndex) => {
        setCart(prev => {
            const updated = [...prev];
            updated[cartIndex].supplements.splice(supplementIndex, 1);
            return updated;
        });
    };
    
    const updateQuantity = useCallback(async (index, delta) => {
        const item = cart[index];
        if (!item) return;
        
        const newQuantity = item.quantity + delta;
        
        if (newQuantity <= 0) {
            // Remove item
            try {
                await removeBackendItem(item.id);
                setCart(prev => prev.filter((_, i) => i !== index));
            } catch (err) {
                console.error('Error removing item:', err);
                // Fallback: local removal
                setCart(prev => prev.filter((_, i) => i !== index));
            }
        } else {
            // Update quantity
            try {
                await updateBackendItemQuantity(item.id, newQuantity);
                setCart(prev => {
                    const updated = [...prev];
                    updated[index] = { ...updated[index], quantity: newQuantity };
                    return updated;
                });
            } catch (err) {
                console.error('Error updating quantity:', err);
                // Fallback: local update
                setCart(prev => {
                    const updated = [...prev];
                    updated[index] = { ...updated[index], quantity: newQuantity };
                    return updated;
                });
            }
        }
    }, [cart, updateBackendItemQuantity, removeBackendItem]);
    
    const removeItemFromCart = useCallback(async (index) => {
        const item = cart[index];
        if (!item) return;
        
        try {
            await removeBackendItem(item.id);
            setCart(prev => prev.filter((_, i) => i !== index));
        } catch (err) {
            console.error('Error removing item:', err);
            // Fallback: local removal
            setCart(prev => prev.filter((_, i) => i !== index));
        }
    }, [cart, removeBackendItem]);
    
    // Alias for backward compatibility
    const removeItem = removeItemFromCart;
    
    const clearCart = useCallback(async () => {
        try {
            await clearBackendCart();
        } catch (err) {
            console.error('Error clearing cart:', err);
        }
        
        setCart([]);
        setSelectedTable(null);
        setCurrentTableOrder(null);
        setSentItemIds(new Set());
        setCustomerName('');
        setOrderNotes('');
        
        // Clear localStorage
        try {
            localStorage.removeItem(CART_STORAGE_KEY_V2);
            localStorage.removeItem(CART_TABLE_KEY_V2);
        } catch (e) {
            console.error('Failed to clear cart storage:', e);
        }
    }, [clearBackendCart]);
    
    // ========================================
    // CANCEL AND RELEASE TABLE
    // ========================================
    const cancelAndReleaseTable = useCallback(async () => {
        if (!selectedTable) return;
        
        try {
            // If there's an existing order, cancel it
            if (currentTableOrder?.id) {
                await api().put(`/pos/orders/${currentTableOrder.id}`, { 
                    status: 'cancelled',
                    cancelled_at: new Date().toISOString()
                }).catch(() => {});
            }
            
            // Release the table
            await api().post(`/tables/${selectedTable.id}/close`).catch(() => {});
            
            // Clear everything
            setCart([]);
            setSelectedTable(null);
            setCurrentTableOrder(null);
            setSentItemIds(new Set());
            setCustomerName('');
            setOrderNotes('');
            
            // Clear localStorage
            try {
                localStorage.removeItem(CART_STORAGE_KEY_V2);
                localStorage.removeItem(CART_TABLE_KEY_V2);
            } catch (e) {
                console.error('Failed to clear cart storage:', e);
            }
            
            // Refresh tables list
            try {
                const res = await api().get('/tables');
                setTables(res.data || []);
            } catch (e) {
                console.error('Failed to refresh tables:', e);
            }
            
            toast.success(`Table ${selectedTable.number} libérée`);
        } catch (error) {
            console.error('Error cancelling table:', error);
            toast.error('Erreur lors de l\'annulation');
        }
    }, [selectedTable, currentTableOrder, api]);
    
    // ========================================
    // LOAD TABLE ORDER (when selecting an occupied table)
    // ========================================
    
    const loadTableOrder = useCallback(async (table, keepCurrentCart = false) => {
        if (!table) return;
        
        // CORRECTION MAJEURE: Par défaut, on VIDE TOUJOURS le panier quand on charge une table
        // Ceci corrige le bug "panier plein sur table vide"
        // Le paramètre keepCurrentCart est utilisé uniquement dans des cas très spécifiques
        
        setLoadingTableOrder(true);
        
        // PREMIÈRE CHOSE: Vider le panier local IMMÉDIATEMENT
        if (!keepCurrentCart) {
            setCart([]);
        }
        
        try {
            // Utiliser le nouvel endpoint qui charge ou crée la commande
            const res = await api().get(`/pos/tables/${table.id}/active-order`);
            const data = res.data;
            
            if (data.has_active_order && data.order) {
                const existingOrder = data.order;
                const existingItems = existingOrder.items || [];
                
                // Charger les items existants dans orderItems (items déjà envoyés)
                setOrderItems(existingItems);
                setCurrentTableOrder(existingOrder);
                setOrderNotes(existingOrder.notes || '');
                
                // Le panier (nouveaux items à envoyer) reste VIDE
                // Les items déjà envoyés sont dans orderItems
                
                // Marquer tous les items existants comme envoyés
                const sentIds = new Set(existingItems.map(item => item.id));
                setSentItemIds(sentIds);
                
                if (existingItems.length > 0) {
                    toast.info(`Table ${table.number} - ${existingItems.length} article(s) en cours`, {
                        icon: <Utensils className="w-5 h-5 text-blue-500" />
                    });
                }
            } else {
                // Table libre - créer une nouvelle commande automatiquement
                try {
                    const openRes = await api().post(`/pos/tables/${table.id}/open`);
                    const newData = openRes.data;
                    
                    setCurrentTableOrder(newData.order);
                    setOrderItems([]);
                    setSentItemIds(new Set());
                    setOrderNotes('');
                    
                    toast.success(`Table ${table.number} ouverte`, {
                        icon: <CheckCircle className="w-5 h-5 text-green-500" />
                    });
                } catch (err) {
                    console.error('Erreur ouverture table:', err);
                    toast.error('Erreur lors de l\'ouverture de la table');
                }
            }
        } catch (error) {
            console.error('Erreur chargement table:', error);
            // En cas d'erreur - vider tout
            setOrderItems([]);
            setCurrentTableOrder(null);
            setSentItemIds(new Set());
        } finally {
            setLoadingTableOrder(false);
        }
    }, [api]); // Retiré 'cart' des dépendances pour éviter les boucles
    
    // ========================================
    // LOAD TABLE DETAILS (moved before handleSelectTable to avoid hoisting issues)
    // ========================================
    const loadTableDetails = useCallback(async (table) => {
        if (!table) return;
        setLoadingTableOrder(true);
        
        try {
            const details = await cartService.getTableDetails(table.id);
            
            // Set sent items (read-only, already in kitchen)
            setOrderItems(details.sent_items || []);
            
            // Mark sent item IDs
            const sentIds = new Set((details.sent_items || []).map(item => item.id));
            setSentItemIds(sentIds);
            
            // If table has draft items but our cart is empty, offer to load them
            if (details.draft_items?.length > 0 && cart.length === 0) {
                // Could show a dialog here to ask user if they want to load drafts
                toast.info(`Table ${table.number} a ${details.draft_items.length} article(s) en brouillon`);
            }
            
        } catch (err) {
            console.error('Error loading table details:', err);
            setOrderItems([]);
            setSentItemIds(new Set());
        } finally {
            setLoadingTableOrder(false);
        }
    }, [cart.length]);
    
    // ========================================
    // SELECT TABLE (NEW SYSTEM - ONLY LINKS, DOES NOT CLEAR!)
    // ========================================
    
    const handleSelectTable = useCallback(async (table) => {
        if (!table) {
            // À emporter - délier de la table actuelle
            try {
                await unlinkFromTable();
                setSelectedTable(null);
                setCurrentTableOrder(null);
                setOrderItems([]);
                setSentItemIds(new Set());
            } catch (err) {
                console.error('Error unlinking table:', err);
            }
            return;
        }
        
        // Si même table, ne rien faire
        if (selectedTable?.id === table.id) {
            return;
        }
        
        try {
            // RÈGLE MÉTIER: Détecter si la table est vide
            const tableHasActiveOrder = table.status === 'occupee' && 
                (table.sent_items?.length > 0 || table.activeOrderId);
            
            if (!tableHasActiveOrder) {
                // TABLE VIDE: Vider le panier AVANT de lier
                console.log(`[POS] handleSelectTable: Table ${table.number} est VIDE - vidage du panier`);
                
                // Activer le blocage de réhydratation
                setBlockCartRehydration(true);
                
                // Vider le localStorage
                localStorage.removeItem(CART_TABLE_KEY_V2);
                localStorage.removeItem(CART_STORAGE_KEY_V2);
                localStorage.removeItem('pos_pending_cart');
                
                // Vider le state local
                setCart([]);
                setOrderItems([]);
                setSentItemIds(new Set());
                setCurrentTableOrder(null);
                
                // Vider le panier backend
                await clearBackendCart();
            }
            
            // Lier la table
            await linkToTable(table.id, !tableHasActiveOrder);
            setSelectedTable(table);
            
            // Charger les details de la table (sent_items)
            await loadTableDetails(table);
            
            // Désactiver le blocage après chargement
            if (!tableHasActiveOrder) {
                setTimeout(() => {
                    setBlockCartRehydration(false);
                }, 500);
            }
            
        } catch (err) {
            console.error('Error linking table:', err);
            toast.error('Erreur lors de la sélection de table');
            setBlockCartRehydration(false);
        }
    }, [selectedTable, linkToTable, unlinkFromTable, clearBackendCart, loadTableDetails]);
    
    // Confirmer le changement de table (après envoi ou annulation du panier)
    const confirmTableChange = useCallback(async (sendFirst = false) => {
        const newTable = tableChangeConfirmDialog.newTable;
        
        if (sendFirst && cart.length > 0) {
            // Envoyer d'abord les items du panier
            await handleSendToKitchen();
        } else {
            // Vider le panier sans envoyer
            await clearBackendCart();
            setCart([]);
        }
        
        setTableChangeConfirmDialog({ open: false, newTable: null });
        
        if (newTable) {
            await handleSelectTable(newTable);
        } else {
            // Mode emporter
            await unlinkFromTable();
            setSelectedTable(null);
            setCurrentTableOrder(null);
            setOrderItems([]);
            setSentItemIds(new Set());
        }
    }, [tableChangeConfirmDialog, cart, clearBackendCart, handleSelectTable, unlinkFromTable]);
    
    // ========================================
    // PROCESS URL PARAMS (after loadTableOrder is defined)
    // ========================================
    useEffect(() => {
        if (tables.length === 0) return;
        
        // Accepter les deux formats: table_id ET table (pour compatibilité)
        const tableId = searchParams.get('table_id') || searchParams.get('table');
        
        if (tableId) {
            // Chercher par ID OU par numéro de table (fallback)
            let table = tables.find(t => t.id === tableId);
            if (!table) {
                // Fallback: chercher par numéro
                table = tables.find(t => t.number === tableId || String(t.number) === tableId);
            }
            
            if (table) {
                // Détecter automatiquement si la table est vide
                const tableHasActiveOrder = table.status === 'occupee' && 
                    (table.sent_items?.length > 0 || table.activeOrderId);
                
                // force_clear OU table vide → vider le panier
                const forceClear = searchParams.get('force_clear') === 'true';
                const shouldClearCart = forceClear || !tableHasActiveOrder;
                const needsProcessing = selectedTable?.id !== table.id || forceClear;
                
                if (needsProcessing) {
                    console.log('[POS] Loading table from URL:', table.number, table.id, 
                        'forceClear:', forceClear, 'tableEmpty:', !tableHasActiveOrder);
                    
                    // ========================================
                    // RÈGLE MÉTIER: Table vide = Panier vide
                    // ========================================
                    if (shouldClearCart) {
                        // ACTIVER LE VERROU ANTI-RÉHYDRATATION
                        setBlockCartRehydration(true);
                        console.log('[POS] BLOCKING cart rehydration - table is EMPTY');
                        
                        // Nettoyer le localStorage
                        localStorage.removeItem(CART_TABLE_KEY_V2);
                        localStorage.removeItem(CART_STORAGE_KEY_V2);
                        localStorage.removeItem('pos_pending_cart');
                        
                        // Nettoyer le state local IMMÉDIATEMENT
                        setCart([]);
                        setOrderItems([]);
                        setSentItemIds(new Set());
                        setCurrentTableOrder(null);
                    }
                    
                    // Sélectionner la table (SYNCHRONE)
                    setSelectedTable(table);
                    
                    // Opérations async dans une IIFE
                    (async () => {
                        try {
                            // VIDER LE PANIER BACKEND si table vide
                            if (shouldClearCart) {
                                console.log('[POS] Clearing backend cart for empty table');
                                await clearBackendCart();
                            }
                            
                            // Lier le panier backend à la table
                            const result = await linkToTable(table.id, shouldClearCart);
                            
                            // CRITIQUE: Forcer le vidage local après linkToTable si table vide
                            if (result?.cart_cleared || shouldClearCart) {
                                console.log('[POS] Cart cleared - forcing local sync');
                                setCart([]);
                                setOrderItems([]);
                                setSentItemIds(new Set());
                            }
                            
                            // Recharger le panier depuis le backend
                            await fetchCart();
                            
                            // Charger la commande de la table (APRÈS le linkToTable)
                            await loadTableOrder(table, !shouldClearCart);
                            
                            // DÉSACTIVER le verrou après chargement complet
                            if (shouldClearCart) {
                                setTimeout(() => {
                                    setBlockCartRehydration(false);
                                    console.log('[POS] Cart rehydration UNBLOCKED');
                                }, 500);
                            }
                            
                        } catch (err) {
                            console.warn('[POS] Could not link cart to table:', err);
                            // Même en cas d'erreur, essayer de charger la table
                            await loadTableOrder(table, false);
                            setBlockCartRehydration(false);
                        }
                    })();
                    
                    // Nettoyer l'URL du paramètre force_clear
                    if (forceClear) {
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('force_clear');
                        window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
                    }
                }
                setUrlParamsProcessed(true);
            } else {
                console.warn('[POS] Table not found for ID:', tableId);
            }
        }
    }, [searchParams, tables, loadTableOrder, linkToTable, selectedTable?.id, fetchCart]);
    
    // ========================================
    // CART CALCULATIONS
    // ========================================
    
    const { cartSubtotal, supplementsTotal, cartTotal, itemCount } = useMemo(() => {
        let subtotal = 0;
        let suppsTotal = 0;
        let count = 0;
        
        cart.forEach(item => {
            subtotal += item.price * item.quantity;
            count += item.quantity;
            
            if (item.supplements) {
                item.supplements.forEach(sup => {
                    suppsTotal += (sup.price || 0) * item.quantity;
                });
            }
            
            if (item.accompaniment?.price) {
                subtotal += item.accompaniment.price * item.quantity;
            }
        });
        
        return {
            cartSubtotal: subtotal,
            supplementsTotal: suppsTotal,
            cartTotal: subtotal + suppsTotal,
            itemCount: count
        };
    }, [cart]);
    
    // Calcul du total de la commande complète (items envoyés + panier)
    const orderTotals = useMemo(() => {
        let orderSubtotal = 0;
        let orderItemCount = 0;
        
        // Ajouter les items déjà envoyés (orderItems)
        orderItems.forEach(item => {
            orderSubtotal += (item.line_total || item.unit_price * item.quantity) || 0;
            orderItemCount += item.quantity || 1;
        });
        
        // Ajouter les items du panier (non encore envoyés)
        cart.forEach(item => {
            let itemTotal = item.price * item.quantity;
            if (item.supplements) {
                item.supplements.forEach(sup => {
                    itemTotal += (sup.price || 0) * item.quantity;
                });
            }
            if (item.accompaniment?.price) {
                itemTotal += item.accompaniment.price * item.quantity;
            }
            orderSubtotal += itemTotal;
            orderItemCount += item.quantity;
        });
        
        return {
            orderTotal: orderSubtotal,
            orderItemCount: orderItemCount,
            sentItemsTotal: orderItems.reduce((sum, item) => sum + (item.line_total || 0), 0),
            sentItemsCount: orderItems.length
        };
    }, [orderItems, cart]);
    
    // ========================================
    // TABLE VALIDATION
    // ========================================
    
    const tableWarning = useMemo(() => {
        if (posSettings.tableRequired && !selectedTable && cart.length > 0) {
            return "Table non assignée - Veuillez sélectionner une table avant l'envoi";
        }
        return null;
    }, [posSettings.tableRequired, selectedTable, cart.length]);
    
    const canSendToKitchen = useMemo(() => {
        // Vérifier s'il y a des nouveaux items à envoyer (non encore envoyés)
        const newItems = cart.filter(item => !item.sent_to_kitchen && !sentItemIds.has(item.id));
        if (newItems.length === 0) return false;
        
        // Pour envoyer en cuisine, il faut soit:
        // 1. Une table sélectionnée, OU
        // 2. Le mode "À emporter" activé
        if (selectedTable || isTakeaway) return true;
        
        // Sans table et sans "À emporter", on ne peut pas envoyer
        return false;
    }, [cart, sentItemIds, selectedTable, isTakeaway]);
    
    // Compter les items non envoyés
    const itemsToSend = useMemo(() => {
        return cart.filter(item => !item.sent_to_kitchen && !sentItemIds.has(item.id));
    }, [cart, sentItemIds]);
    
    // ========================================
    // SESSION VALIDATION BEFORE CRITICAL OPERATIONS
    // ========================================
    
    const validateSessionBeforeAction = useCallback(async () => {
        // Skip validation if already detected as closed
        if (registerClosed) {
            return false;
        }
        
        // Admin can always proceed
        if (user?.role === 'ADMIN') {
            return true;
        }
        
        // Skip if no session
        if (!cashSession?.id && !cashSession?.register_id) {
            toast.error('Aucune session active. Opération impossible.');
            navigate('/dashboard');
            return false;
        }
        
        try {
            const response = await api().get('/pos/session/validate', {
                headers: {
                    'X-Session-Id': cashSession?.id || '',
                    'X-Register-Id': cashSession?.register_id || ''
                }
            });
            
            if (response.data && !response.data.valid) {
                handleRegisterClosed(response.data.reason || 'normal');
                return false;
            }
            
            return true;
        } catch (error) {
            if (error.response?.status === 423) {
                const detail = error.response.data?.detail;
                toast.error(detail?.message || 'Session fermée. Opération impossible.');
                handleRegisterClosed(detail?.reason || 'normal');
                return false;
            }
            // Network error - allow offline mode
            if (!navigator.onLine) {
                return true;
            }
            return true;
        }
    }, [api, cashSession, registerClosed, user?.role, handleRegisterClosed, navigate]);

    // ========================================
    // SEND TO KITCHEN (NEW BACKEND API + OFFLINE SUPPORT)
    // ========================================
    
    const handleSendToKitchen = async () => {
        // VALIDATE SESSION FIRST
        const isValid = await validateSessionBeforeAction();
        if (!isValid) {
            return;
        }
        
        // Check if there are items in the cart
        if (cart.length === 0) {
            toast.info('Aucun article à envoyer');
            return;
        }
        
        // Validate table if required (sauf si À emporter)
        // MODIFICATION: Assigner automatiquement une table "À emporter" si aucune table sélectionnée
        let shouldBeTakeaway = isTakeaway;
        let tableToUse = selectedTable;
        
        if (!selectedTable && !isTakeaway) {
            // Chercher une table "À emporter" disponible
            const emporterTables = tables.filter(t => 
                (t.zone === 'Emporter' || t.zone === 'À emporter' || t.zone === 'emporter' || t.zone === 'A emporter') &&
                (t.status === 'libre' || t.status === 'available' || t.status === 'free')
            );
            
            if (emporterTables.length > 0) {
                // Assigner automatiquement la première table "À emporter" disponible
                const emporterTable = emporterTables[0];
                tableToUse = emporterTable;
                
                // Lier le panier à cette table
                try {
                    await linkToTable(emporterTable.id);
                    setSelectedTable(emporterTable);
                    toast.success(`Table À emporter ${emporterTable.number} assignée automatiquement`);
                } catch (e) {
                    console.warn('Could not auto-assign emporter table:', e);
                    // Fallback au mode takeaway sans table
                    shouldBeTakeaway = true;
                    setIsTakeaway(true);
                    toast.info('Commande enregistrée en mode "À emporter"');
                }
            } else {
                // Aucune table "À emporter" disponible - fallback au mode takeaway
                shouldBeTakeaway = true;
                setIsTakeaway(true);
                toast.info('Commande enregistrée en mode "À emporter" (aucune table disponible)');
            }
        }
        
        // OFFLINE MODE: Save locally if not online
        if (isOffline) {
            try {
                const orderData = {
                    order_type: shouldBeTakeaway ? 'takeaway' : 'dine_in',
                    table_number: tableToUse?.number,
                    table_id: tableToUse?.id,
                    customer_name: customerName || undefined,
                    items: cart.map(item => ({
                        product_id: item.id,
                        product_name: item.name,
                        quantity: item.quantity,
                        unit_price: item.price,
                        accompaniment: item.accompaniment,
                        accompaniment_name: item.accompaniment_name,
                        supplements: item.supplements
                    })),
                    notes: orderNotes || undefined,
                    total: cartTotal,
                    is_takeaway: shouldBeTakeaway
                };
                
                await saveOrderLocally(orderData);
                
                // Clear local cart state
                setCart([]);
                setSelectedTable(null);
                setCurrentTableOrder(null);
                setOrderItems([]);
                setSentItemIds(new Set());
                setOrderNotes('');
                setCustomerName('');
                setIsTakeaway(false);
                
                // Clear backend cart items too (locally)
                try {
                    await clearBackendCart();
                } catch (e) { /* ignore if offline */ }
                
                return;
            } catch (error) {
                console.error('Error saving order offline:', error);
                toast.error('Erreur lors de l\'enregistrement hors-ligne');
                return;
            }
        }
        
        // ONLINE MODE: Use normal backend API
        try {
            // Use new backend API to send cart to kitchen/bar
            // If on Bar register, force destination to bar
            const sendOptions = {
                notes: orderNotes || undefined,
                is_takeaway: shouldBeTakeaway
            };
            
            // Force bar destination when on Bar register
            if (isBarRegister) {
                sendOptions.destination = 'bar';
                sendOptions.send_to_bar = true;
                sendOptions.send_to_kitchen = false;
            }
            
            const result = await sendBackendToKitchen(sendOptions);
            
            // Clear local cart state (backend already cleared it)
            setCart([]);
            
            // RESET COMPLET après envoi - la table reste sélectionnée mais le panier est vide
            // et on peut soit ajouter d'autres articles, soit encaisser
            
            // Recharger les détails de la table pour voir les items envoyés
            if (tableToUse) {
                // Recharger les items envoyés pour cette table
                try {
                    const details = await cartService.getTableDetails(tableToUse.id);
                    setOrderItems(details.sent_items || []);
                    const sentIds = new Set((details.sent_items || []).map(item => item.id));
                    setSentItemIds(sentIds);
                    setCurrentTableOrder(details.current_order || null);
                } catch (e) {
                    console.warn('Could not reload table details:', e);
                    // En cas d'erreur, on vide quand même les états locaux
                    setOrderItems([]);
                    setSentItemIds(new Set());
                    setCurrentTableOrder(null);
                }
            } else {
                // Mode à emporter - reset complet
                setSelectedTable(null);
                setCurrentTableOrder(null);
                setOrderItems([]);
                setSentItemIds(new Set());
            }
            
            // Reset notes et autres états
            setOrderNotes('');
            setCustomerName('');
            setIsTakeaway(false);  // Reset takeaway flag
            
            // IMPORTANT: Vider aussi les suppléments sélectionnés
            setCartSupplementDialog({ open: false, cartIndex: null, selectedSupplements: [], allowedSupplementIds: null });
            
            // Legacy cleanup
            try {
                localStorage.removeItem(CART_STORAGE_KEY_V2);
                localStorage.removeItem(CART_TABLE_KEY_V2);
            } catch (e) { /* ignore */ }
            
            // Refresh table data - IMPORTANT pour synchroniser l'état
            await refreshData();
            
            // Message de succès
            toast.success('Commande envoyée en cuisine', {
                icon: <ChefHat className="w-5 h-5 text-orange-500" />
            });
            
        } catch (error) {
            console.error('Error sending to kitchen:', error);
            toast.error(error.response?.data?.detail || 'Erreur envoi cuisine');
        }
    };
    
    // Fonction wrapper pour être utilisée dans confirmTableChange
    const sendCartToKitchen = handleSendToKitchen;
    
    // ========================================
    // SEND MESSAGE TO KITCHEN/BAR
    // ========================================
    
    const handleSendMessage = async () => {
        if (!messageText.trim()) {
            toast.error('Veuillez saisir un message');
            return;
        }
        
        try {
            const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
            const token = localStorage.getItem('token');
            
            const endpoint = messageDestination === 'bar' 
                ? `${API_BASE}/api/bar/messages`
                : `${API_BASE}/api/tables/kitchen-messages`;
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    table_id: selectedTable?.id || null,
                    table_number: selectedTable?.number || null,
                    message: messageText.trim(),
                    source: 'panier'
                })
            });
            
            if (!response.ok) throw new Error('Erreur lors de l\'envoi');
            
            const destinationLabel = messageDestination === 'bar' ? 'bar' : 'cuisine';
            toast.success(`Message envoyé ${messageDestination === 'bar' ? 'au' : 'à la'} ${destinationLabel}`);
            setShowMessageDialog(false);
            setMessageText('');
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Erreur lors de l\'envoi du message');
        }
    };
    
    // ========================================
    // PAYMENT & ORDER COMPLETION
    // ========================================
    
    const submitPayment = async () => {
        // VALIDATE SESSION FIRST
        const isValid = await validateSessionBeforeAction();
        if (!isValid) {
            return;
        }
        
        // ========================================
        // CORRECTION BUG: Source unique du total
        // ========================================
        
        // Recalculer le total à partir de TOUTES les sources
        // 1. Items du panier (cart)
        // 2. Items déjà envoyés (orderItems)
        // 3. Total de la commande existante (currentTableOrder)
        
        let totalToPay = 0;
        
        // Source 1: Panier actuel
        if (cart.length > 0) {
            cart.forEach(item => {
                let itemTotal = Number(item.price || 0) * Number(item.quantity || 1);
                if (item.supplements) {
                    item.supplements.forEach(sup => {
                        itemTotal += Number(sup.price || 0) * Number(item.quantity || 1);
                    });
                }
                if (item.accompaniment?.price) {
                    itemTotal += Number(item.accompaniment.price) * Number(item.quantity || 1);
                }
                totalToPay += itemTotal;
            });
        }
        
        // Source 2: Items déjà envoyés (orderItems)
        if (orderItems.length > 0) {
            orderItems.forEach(item => {
                totalToPay += Number(item.line_total || item.unit_price * item.quantity || 0);
            });
        }
        
        // Source 3: Commande existante si pas d'items calculés
        if (totalToPay === 0 && currentTableOrder?.total) {
            totalToPay = Number(currentTableOrder.total);
        }
        
        // Sécuriser contre NaN
        if (isNaN(totalToPay)) {
            totalToPay = 0;
        }
        
        console.log("TOTAL AVANT VALIDATION:", totalToPay);
        console.log("PANIER:", cart);
        console.log("ORDER ITEMS:", orderItems);
        console.log("CURRENT TABLE ORDER:", currentTableOrder);
        
        // Vérifier qu'il y a quelque chose à payer
        if (cart.length === 0 && orderItems.length === 0 && !currentTableOrder?.id) {
            toast.error('Aucun article à payer');
            return;
        }
        
        // Validation sécurisée du total
        if (!totalToPay || totalToPay <= 0 || isNaN(totalToPay)) {
            // Vérifier si on a des items mais total = 0 (bug)
            if (cart.length > 0 || orderItems.length > 0) {
                console.error("BUG DÉTECTÉ: Items présents mais total = 0", {
                    cart,
                    orderItems,
                    totalToPay
                });
                toast.error('Erreur de calcul du total. Veuillez rafraîchir la page.');
            } else {
                toast.error('Le montant total doit être supérieur à 0');
            }
            return;
        }
        
        // Utiliser le total recalculé
        const finalTotal = totalToPay;
        
        try {
            let orderId = currentTableOrder?.id;
            
            console.log('Submit payment - Initial state:', {
                cartLength: cart.length,
                cartTotal,
                finalTotal,
                orderItemsCount: orderItems.length,
                currentTableOrderId: orderId,
                selectedTable: selectedTable?.number,
                paymentMethod,
                paymentAmount
            });
            
            // Si pas de commande existante, créer d'abord la commande
            if (!orderId && cart.length > 0) {
                console.log('Creating new order...');
                const orderData = {
                    items: cart.map(item => ({
                        product_id: item.product_id,
                        product_name: item.product_name,
                        quantity: item.quantity,
                        unit_price: item.price,
                        supplements: item.supplements || [],
                        accompaniment: item.accompaniment,
                        notes: item.notes || '',
                        category: item.category,
                        destination: item.destination || 'cuisine'
                    })),
                    order_type: selectedTable ? 'table' : 'takeaway',
                    table_id: selectedTable?.id || null,
                    table_number: selectedTable?.number ? String(selectedTable.number) : null,
                    customer_name: customerName || (selectedTable ? `Table ${selectedTable.number}` : 'À emporter'),
                    subtotal: cartSubtotal,
                    supplements_total: supplementsTotal,
                    total: finalTotal,
                    notes: orderNotes,
                    status: 'pending',
                    send_to_kitchen: false,
                    is_takeaway: !selectedTable
                };
                
                const res = await api().post('/pos/orders', orderData);
                orderId = res.data?.id;
                
                if (!orderId) {
                    throw new Error('Échec de création de la commande');
                }
            }
            
            // Valider le paiement pour la commande
            if (orderId) {
                const paymentData = {
                    payment_method: paymentMethod,
                    amount_received: paymentMethod === 'cash' ? (parseFloat(paymentAmount) || finalTotal) : finalTotal,
                    total: finalTotal
                };
                
                console.log('Sending payment request:', { orderId, paymentData });
                const payRes = await api().post(`/pos/orders/${orderId}/pay`, paymentData);
                console.log('Payment response:', payRes.data);
                
                // Ajouter les points fidélité si un membre est sélectionné
                if (selectedLoyaltyMember) {
                    await addLoyaltyPointsFromPayment(selectedLoyaltyMember.phone, finalTotal, orderId);
                }
            } else {
                throw new Error('Aucune commande à payer');
            }
            
            // Après paiement validé, le backend a déjà mis la table en "a_debarrasser"
            // Ne PAS libérer la table ici - elle doit rester "à débarrasser" jusqu'au débarrassage manuel
            // L'ancien code `await api().put(\`/tables/${selectedTable.id}\`, { status: 'free' })` était incorrect
            
            toast.success('Paiement validé !', {
                icon: <CheckCircle className="w-5 h-5 text-green-500" />
            });
            
            // Maintenant on peut vider le panier local et désélectionner la table
            clearCart();
            setSelectedTable(null);  // Désélectionner la table (elle passe en "à débarrasser")
            setCurrentTableOrder(null);
            setOrderItems([]);
            setSentItemIds(new Set());
            setPaymentDialog({ open: false });
            setPaymentMethod('cash');
            setPaymentAmount('');
            setSelectedLoyaltyMember(null); // Reset loyalty member
            fetchData();
        } catch (error) {
            console.error('Payment error:', error);
            toast.error(error.response?.data?.detail || error.message || 'Erreur de paiement');
        }
    };
    
    // Bouton pour ouvrir le paiement (quand le client demande l'addition)
    const handleRequestPayment = () => {
        if (cart.length === 0) {
            toast.error('Aucun article dans la commande');
            return;
        }
        setPaymentDialog({ open: true });
    };
    
    // ========================================
    // LOYALTY PROGRAM - LE CERCLE ALDRIN'S
    // ========================================
    
    // Rechercher un membre fidélité par téléphone
    const searchLoyaltyMember = async (phone) => {
        if (!phone || phone.length < 3) {
            setLoyaltyMember(null);
            return;
        }
        
        setLoyaltySearching(true);
        try {
            const res = await api().get(`/loyalty/member/phone/${phone.replace(/\s/g, '')}`);
            setLoyaltyMember(res.data);
        } catch (error) {
            setLoyaltyMember(null);
        } finally {
            setLoyaltySearching(false);
        }
    };
    
    // Recherche avec debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (loyaltySearchPhone) {
                searchLoyaltyMember(loyaltySearchPhone);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [loyaltySearchPhone]);
    
    // Sélectionner un membre pour le paiement
    const selectLoyaltyMemberForPayment = (member) => {
        setSelectedLoyaltyMember(member);
        toast.success(`${member.first_name} sélectionné pour les points fidélité`);
    };
    
    // Ajouter/retirer des points manuellement
    const handleManualLoyaltyPoints = async () => {
        if (!loyaltyMember || loyaltyPointsToAdd === 0 || !loyaltyPointsReason) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        try {
            await api().post('/loyalty/manual-points', {
                member_id: loyaltyMember.id,
                points: loyaltyPointsToAdd,
                reason: loyaltyPointsReason,
                added_by: user?.name || user?.username || 'Unknown',
                added_by_role: user?.role || 'ADMIN'
            });
            toast.success(`${loyaltyPointsToAdd > 0 ? '+' : ''}${loyaltyPointsToAdd} points appliqués à ${loyaltyMember.first_name}`);
            
            // Rafraîchir les données du membre
            await searchLoyaltyMember(loyaltySearchPhone);
            
            // Reset
            setLoyaltyPointsToAdd(0);
            setLoyaltyPointsReason('');
            setLoyaltyAddPointsMode(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    // Ajouter les points fidélité après un paiement
    const addLoyaltyPointsFromPayment = async (phone, amount, orderId) => {
        try {
            const res = await api().post('/loyalty/add-points', {
                phone: phone.replace(/\s/g, ''),
                amount: amount,
                order_id: orderId
            });
            
            if (res.data.success && res.data.points_earned > 0) {
                toast.success(
                    `🎉 ${res.data.points_earned} point${res.data.points_earned > 1 ? 's' : ''} fidélité ajouté${res.data.points_earned > 1 ? 's' : ''} !`,
                    { description: `Total: ${res.data.total_points} points - Statut: ${res.data.status}` }
                );
                return res.data;
            }
        } catch (error) {
            console.error('Loyalty points error:', error);
            // Ne pas bloquer le paiement si l'ajout de points échoue
        }
        return null;
    };
    
    // Obtenir la couleur du statut fidélité
    const getLoyaltyStatusColor = (status) => {
        switch (status) {
            case 'Or': return 'bg-yellow-500 text-yellow-950';
            case 'Argent': return 'bg-gray-400 text-gray-950';
            default: return 'bg-orange-600 text-white';
        }
    };
    
    // Obtenir l'icône du statut
    const getLoyaltyStatusIcon = (status) => {
        switch (status) {
            case 'Or': return <Crown className="w-3 h-3" />;
            case 'Argent': return <Star className="w-3 h-3" />;
            default: return <Award className="w-3 h-3" />;
        }
    };
    
    // ========================================
    // REWARD SCANNER - Scanner récompense fidélité OPTIMISÉ
    // Scan automatique, validation rapide, reprise automatique
    // ========================================
    
    // Référence pour le scanner et audio
    const html5QrCodeRef = useRef(null);
    const lastScannedQRRef = useRef(null);
    const scanCooldownRef = useRef(false);
    
    // Son de confirmation (bip)
    const playBeepSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = 1200; // Fréquence du bip
            oscillator.type = 'sine';
            gainNode.gain.value = 0.3;
            
            oscillator.start();
            setTimeout(() => {
                oscillator.stop();
                audioContext.close();
            }, 150); // Durée du bip: 150ms
        } catch (e) {
            console.log('Audio not supported');
        }
    };
    
    // Démarrer le scanner
    const startRewardScanner = async () => {
        if (html5QrCodeRef.current) return;
        
        try {
            const { Html5Qrcode } = await import('html5-qrcode');
            const html5QrCode = new Html5Qrcode("reward-scanner-container");
            html5QrCodeRef.current = html5QrCode;
            
            // Configuration pour caméra arrière sur mobile
            const config = {
                fps: 10, // 100ms entre chaque scan
                qrbox: { width: 280, height: 280 },
                aspectRatio: 1,
                disableFlip: false,
            };
            
            // Préférer la caméra arrière
            const cameraConfig = { facingMode: "environment" };
            
            await html5QrCode.start(
                cameraConfig,
                config,
                (decodedText) => {
                    // Éviter les scans multiples du même QR
                    if (scanCooldownRef.current || decodedText === lastScannedQRRef.current) {
                        return;
                    }
                    
                    // Bip de confirmation
                    playBeepSound();
                    
                    // Marquer comme scanné
                    lastScannedQRRef.current = decodedText;
                    scanCooldownRef.current = true;
                    
                    // Traiter le QR code
                    handleRewardScanned(decodedText);
                },
                () => {} // Ignorer les erreurs de scan
            );
        } catch (e) {
            console.error('Erreur démarrage scanner:', e);
            toast.error('Impossible d\'accéder à la caméra');
        }
    };
    
    // Arrêter le scanner
    const stopRewardScanner = async () => {
        if (html5QrCodeRef.current) {
            try {
                await html5QrCodeRef.current.stop();
                html5QrCodeRef.current.clear();
            } catch (e) {}
            html5QrCodeRef.current = null;
        }
    };
    
    // Reprendre le scan après validation
    const resumeScanning = () => {
        setVerifiedReward(null);
        setSelectedRewardProduct(null);
        lastScannedQRRef.current = null;
        scanCooldownRef.current = false;
        
        // Redémarrer après un court délai
        setTimeout(() => {
            if (rewardScannerOpen) {
                startRewardScanner();
            }
        }, 500);
    };
    
    // Initialiser le scanner quand le dialog s'ouvre
    useEffect(() => {
        if (rewardScannerOpen) {
            // Reset states
            lastScannedQRRef.current = null;
            scanCooldownRef.current = false;
            
            // Démarrer le scanner après que le DOM soit prêt
            const timer = setTimeout(() => {
                startRewardScanner();
            }, 300);
            
            return () => {
                clearTimeout(timer);
                stopRewardScanner();
            };
        }
        
        return () => {
            stopRewardScanner();
        };
    }, [rewardScannerOpen]);
    
    // ÉTAPE 1: Vérifier le QR code automatiquement (NE DÉDUIT PAS les points)
    const handleRewardScanned = async (qrCode) => {
        console.log('=== REWARD SCAN START ===');
        console.log('QR Code brut scanné:', qrCode);
        
        setRewardScannerLoading(true);
        
        // Pause le scanner pendant la vérification
        if (html5QrCodeRef.current) {
            try {
                await html5QrCodeRef.current.pause();
                console.log('Scanner pausé');
            } catch (e) {
                console.log('Erreur pause scanner:', e);
            }
        }
        
        try {
            console.log('Appel API verify-reward-qr avec:', { qr_code: qrCode });
            
            const res = await api().post('/loyalty/verify-reward-qr', { 
                qr_code: qrCode
            });
            
            console.log('Réponse API:', res.data);
            
            if (res.data.success && res.data.verified) {
                console.log('QR vérifié avec succès');
                console.log('Client:', res.data.member_name);
                console.log('Récompense:', res.data.reward_name);
                console.log('Points à déduire:', res.data.points_cost);
                console.log('Choix de produits:', res.data.product_choices);
                
                setVerifiedReward(res.data);
                
                // Présélectionner le produit selon les cas
                if (res.data.has_choices && res.data.product_choices?.length === 1) {
                    const firstChoice = res.data.product_choices[0];
                    console.log('Un seul choix, présélection:', firstChoice);
                    setSelectedRewardProduct({
                        id: firstChoice.product?.id || firstChoice.choice_id,
                        name: firstChoice.product?.name || firstChoice.choice_name,
                        choice_name: firstChoice.choice_name
                    });
                } else if (res.data.has_choices && res.data.product_choices?.length > 1) {
                    console.log('Plusieurs choix disponibles, attente sélection utilisateur');
                    // Présélectionner le premier choix par défaut pour éviter l'erreur
                    const firstChoice = res.data.product_choices[0];
                    setSelectedRewardProduct({
                        id: firstChoice.product?.id || firstChoice.choice_id,
                        name: firstChoice.product?.name || firstChoice.choice_name,
                        choice_name: firstChoice.choice_name
                    });
                } else if (res.data.is_discount) {
                    console.log('Réduction:', res.data.discount_value);
                    setSelectedRewardProduct({
                        id: 'discount',
                        name: `Réduction ${res.data.discount_value?.toLocaleString()} FCFA`,
                        is_discount: true,
                        discount_value: res.data.discount_value
                    });
                } else {
                    console.log('Récompense simple sans choix');
                    setSelectedRewardProduct({
                        id: res.data.reward_id,
                        name: res.data.reward_name,
                        choice_name: res.data.reward_name
                    });
                }
                
                // Deuxième bip pour confirmer la détection
                playBeepSound();
                console.log('=== REWARD SCAN SUCCESS ===');
            } else {
                console.log('Réponse API invalide:', res.data);
                throw new Error('Réponse invalide du serveur');
            }
        } catch (error) {
            console.error('=== REWARD SCAN ERROR ===');
            console.error('Erreur complète:', error);
            console.error('Response data:', error.response?.data);
            console.error('Response status:', error.response?.status);
            
            let message = 'Erreur lors de la validation';
            
            if (error.response?.data?.detail) {
                message = error.response.data.detail;
            } else if (error.response?.status === 404) {
                message = 'QR code invalide ou inconnu';
            } else if (error.response?.status === 400) {
                message = error.response.data?.detail || 'QR code déjà utilisé ou expiré';
            } else if (error.message) {
                message = error.message;
            }
            
            console.log('Message d\'erreur affiché:', message);
            
            toast.error(message, { 
                icon: <AlertCircle className="w-5 h-5 text-red-500" />,
                duration: 4000
            });
            
            // Reprendre le scan après erreur
            resumeScanning();
        } finally {
            setRewardScannerLoading(false);
        }
    };
    
    // ÉTAPE 2: Confirmer et appliquer la récompense (DÉDUIT les points)
    const confirmAndApplyReward = async () => {
        console.log('=== CONFIRM REWARD START ===');
        console.log('verifiedReward:', verifiedReward);
        console.log('selectedRewardProduct:', selectedRewardProduct);
        
        if (!verifiedReward) {
            console.error('Pas de récompense vérifiée');
            toast.error('Aucune récompense détectée');
            return;
        }
        
        // Utiliser le produit sélectionné ou la récompense par défaut
        const productToAdd = selectedRewardProduct || {
            id: verifiedReward.reward_id,
            name: verifiedReward.reward_name
        };
        
        console.log('Produit à ajouter:', productToAdd);
        
        setRewardConfirmLoading(true);
        try {
            const requestData = {
                qr_code: verifiedReward.qr_code,
                selected_product_id: productToAdd.id,
                selected_product_name: productToAdd.name,
                order_id: currentTableOrder?.id || null,
                validated_by: user?.name || user?.username || 'Unknown',
                validated_by_role: user?.role || 'SERVEUR'
            };
            
            console.log('Appel API confirm-reward avec:', requestData);
            
            const res = await api().post('/loyalty/confirm-reward', requestData);
            
            console.log('Réponse confirmation:', res.data);
            
            if (res.data.success) {
                console.log('Confirmation réussie!');
                
                // Bip de succès (double bip)
                playBeepSound();
                setTimeout(() => playBeepSound(), 200);
                
                // Ajouter le produit gratuit au panier avec libellé professionnel
                // Format: "{Produit} - offert par fidélité"
                if (productToAdd.is_discount) {
                    console.log('Ajout réduction au panier');
                    const discountName = `Réduction ${productToAdd.discount_value.toLocaleString()} F - offert par fidélité`;
                    const discountItem = {
                        id: `reward_discount_${Date.now()}`,
                        name: discountName,
                        product_name: discountName, // Pour compatibilité avec l'affichage panier
                        price: -productToAdd.discount_value,
                        quantity: 1,
                        isReward: true,
                        isDiscount: true,
                        loyaltyReward: true,
                        rewardDetails: {
                            member_id: verifiedReward.member_id,
                            member_name: verifiedReward.member_name,
                            member_phone: verifiedReward.member_phone,
                            reward_id: verifiedReward.reward_id,
                            reward_name: verifiedReward.reward_name,
                            points_deducted: verifiedReward.points_cost,
                            qr_code: verifiedReward.qr_code,
                            redeemed_at: new Date().toISOString()
                        },
                        notes: `Client: ${verifiedReward.member_name} | -${verifiedReward.points_cost} pts`
                    };
                    setCart(prev => [...prev, discountItem]);
                } else {
                    console.log('Ajout produit offert au panier');
                    // Format professionnel: "{Produit} - offert par fidélité"
                    const productName = productToAdd.name || verifiedReward.reward_name || 'Produit';
                    const displayName = `${productName} - offert par fidélité`;
                    const rewardItem = {
                        id: `reward_item_${Date.now()}`,
                        name: displayName,
                        product_name: displayName, // Pour compatibilité avec l'affichage panier
                        originalName: productName,
                        price: 0,
                        quantity: 1,
                        isReward: true,
                        loyaltyReward: true,
                        rewardDetails: {
                            member_id: verifiedReward.member_id,
                            member_name: verifiedReward.member_name,
                            member_phone: verifiedReward.member_phone,
                            reward_id: verifiedReward.reward_id,
                            reward_name: verifiedReward.reward_name,
                            points_deducted: verifiedReward.points_cost,
                            product_id: productToAdd.id,
                            product_name: productName,
                            qr_code: verifiedReward.qr_code,
                            redeemed_at: new Date().toISOString()
                        },
                        notes: `Client: ${verifiedReward.member_name} | -${verifiedReward.points_cost} pts`
                    };
                    setCart(prev => [...prev, rewardItem]);
                }
                
                toast.success('Récompense appliquée !', {
                    description: `${productToAdd.name || 'Produit'} - offert par fidélité à ${verifiedReward.member_name}`,
                    icon: <CheckCircle className="w-5 h-5 text-green-500" />,
                    duration: 4000
                });
                
                console.log('=== CONFIRM REWARD SUCCESS ===');
                console.log('Produit ajouté au panier:', productToAdd.name);
                console.log('Points déduits:', verifiedReward.points_cost);
                console.log('Client:', verifiedReward.member_name);
                
                // Fermer le scanner automatiquement après succès
                closeRewardScanner();
            } else {
                console.error('Réponse sans success=true:', res.data);
                throw new Error('La confirmation a échoué');
            }
        } catch (error) {
            console.error('=== CONFIRM REWARD ERROR ===');
            console.error('Erreur complète:', error);
            console.error('Response:', error.response?.data);
            
            let message = 'Erreur lors de la validation';
            if (error.response?.data?.detail) {
                message = error.response.data.detail;
            } else if (error.message) {
                message = error.message;
            }
            
            toast.error(message);
            // Reprendre le scan après erreur
            resumeScanning();
        } finally {
            setRewardConfirmLoading(false);
        }
    };
    
    // Annuler et reprendre le scan
    const cancelRewardScan = () => {
        resumeScanning();
    };
    
    // Fermer complètement le scanner
    const closeRewardScanner = () => {
        stopRewardScanner();
        setRewardScannerOpen(false);
        setVerifiedReward(null);
        setSelectedRewardProduct(null);
        setRewardScannerLoading(false);
        setRewardConfirmLoading(false);
        lastScannedQRRef.current = null;
        scanCooldownRef.current = false;
    };
    
    // ========================================
    // MANAGER MODE - PRODUCT ACTIONS
    // ========================================
    
    const handleEditProduct = (product) => {
        setProductEditDialog({ open: true, product: { ...product }, isNew: false });
    };
    
    const handleDuplicateProduct = async (product) => {
        const newProduct = {
            ...product,
            id: undefined,
            name: `${product.name} (copie)`,
        };
        setProductEditDialog({ open: true, product: newProduct, isNew: true });
    };
    
    const handleToggleProductActive = async (product) => {
        try {
            await api().put(`/pos/products/${product.id}`, {
                is_available: !product.is_available
            });
            toast.success(product.is_available ? 'Produit désactivé' : 'Produit activé');
            fetchData();
        } catch (error) {
            toast.error('Erreur de mise à jour');
        }
    };
    
    const handleDeleteProduct = async (product) => {
        if (!window.confirm(`Supprimer "${product.name}" ?`)) return;
        try {
            await api().delete(`/pos/products/${product.id}`);
            toast.success('Produit supprimé');
            fetchData();
        } catch (error) {
            toast.error('Erreur de suppression');
        }
    };
    
    const saveProduct = async () => {
        const { product, isNew } = productEditDialog;
        try {
            if (isNew) {
                await api().post('/pos/products', product);
                toast.success('Produit créé');
            } else {
                await api().put(`/pos/products/${product.id}`, product);
                toast.success('Produit mis à jour');
            }
            setProductEditDialog({ open: false, product: null, isNew: false });
            fetchData();
        } catch (error) {
            toast.error('Erreur de sauvegarde');
        }
    };
    
    // ========================================
    // MANAGER MODE - SUPPLEMENT ACTIONS
    // ========================================
    
    const saveSupplement = async () => {
        const { supplement, isNew } = supplementEditDialog;
        try {
            if (isNew) {
                await api().post('/pos/supplements', supplement);
                toast.success('Supplément créé');
            } else {
                await api().put(`/pos/supplements/${supplement.id}`, supplement);
                toast.success('Supplément mis à jour');
            }
            setSupplementEditDialog({ open: false, supplement: null, isNew: false });
            fetchData();
        } catch (error) {
            toast.error('Erreur de sauvegarde');
        }
    };
    
    // ========================================
    // FORMULA MANAGEMENT
    // ========================================
    
    const saveFormula = async () => {
        if (!newFormula.name.trim()) {
            toast.error('Veuillez saisir un nom pour la formule');
            return;
        }
        if (!newFormula.price || parseFloat(newFormula.price) <= 0) {
            toast.error('Veuillez saisir un prix valide');
            return;
        }
        
        // Check that at least one step has choices
        const hasChoices = newFormula.steps.some(step => step.choices.length > 0);
        if (!hasChoices) {
            toast.error('Ajoutez au moins un produit à la formule');
            return;
        }
        
        try {
            const formulaData = {
                name: newFormula.name.trim(),
                description: newFormula.description.trim() || null,
                price: parseFloat(newFormula.price),
                steps: newFormula.steps.filter(step => step.choices.length > 0).map(step => ({
                    name: step.name,
                    choices: step.choices.map(c => ({ product_id: c.product_id, is_default: c.is_default || false })),
                    required: step.required,
                    max_selections: 1
                })),
                is_available: true,
                meal_type: newFormula.meal_type || 'all'
            };
            
            await api().post('/pos/formulas', formulaData);
            toast.success(`Formule "${newFormula.name}" créée avec succès!`);
            setFormulaDialog({ open: false });
            setNewFormula({
                name: '',
                description: '',
                price: '',
                steps: [{ name: 'Entrée', choices: [], required: true }, { name: 'Plat', choices: [], required: true }, { name: 'Dessert', choices: [], required: false }],
                meal_type: 'all'
            });
            setEditingFormula(null);
            fetchData();
        } catch (error) {
            console.error('Error creating formula:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création de la formule');
        }
    };
    
    // Update existing formula
    const updateFormula = async () => {
        if (!editingFormula || !newFormula.name.trim()) {
            toast.error('Veuillez saisir un nom pour la formule');
            return;
        }
        if (!newFormula.price || parseFloat(newFormula.price) <= 0) {
            toast.error('Veuillez saisir un prix valide');
            return;
        }
        
        try {
            const formulaData = {
                name: newFormula.name.trim(),
                description: newFormula.description.trim() || null,
                price: parseFloat(newFormula.price),
                steps: newFormula.steps.filter(step => step.choices.length > 0).map(step => ({
                    name: step.name,
                    choices: step.choices.map(c => ({ product_id: c.product_id, is_default: c.is_default || false })),
                    required: step.required,
                    max_selections: 1
                })),
                is_available: newFormula.is_available !== false,
                meal_type: newFormula.meal_type || 'all'
            };
            
            await api().put(`/pos/formulas/${editingFormula.id}`, formulaData);
            toast.success(`Formule "${newFormula.name}" mise à jour!`);
            setFormulaDialog({ open: false });
            setNewFormula({
                name: '',
                description: '',
                price: '',
                steps: [{ name: 'Entrée', choices: [], required: true }, { name: 'Plat', choices: [], required: true }, { name: 'Dessert', choices: [], required: false }],
                meal_type: 'all'
            });
            setEditingFormula(null);
            fetchData();
        } catch (error) {
            console.error('Error updating formula:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };
    
    // Delete formula
    const deleteFormula = async (formulaId, formulaName) => {
        if (!window.confirm(`Voulez-vous vraiment supprimer la formule "${formulaName}" ?`)) {
            return;
        }
        
        try {
            await api().delete(`/pos/formulas/${formulaId}`);
            toast.success(`Formule "${formulaName}" supprimée`);
            fetchData();
        } catch (error) {
            console.error('Error deleting formula:', error);
            toast.error('Erreur lors de la suppression');
        }
    };
    
    // Duplicate a formula
    const duplicateFormula = async (formula) => {
        try {
            const newFormula = {
                name: `${formula.name} (copie)`,
                description: formula.description || '',
                price: formula.price,
                meal_type: formula.meal_type || 'all',
                is_available: true,
                steps: formula.steps || [],
                components: formula.components || [],
                recipe_ingredients: formula.recipe_ingredients || [],
                badge: formula.badge || '',
                is_popular: false
            };
            
            await api().post('/pos/formulas', newFormula);
            toast.success(`Formule "${formula.name}" dupliquée`);
            fetchData();
        } catch (error) {
            console.error('Error duplicating formula:', error);
            toast.error('Erreur lors de la duplication');
        }
    };
    
    // Add formula to cart - opens the selection modal
    const addFormulaToCart = (formula) => {
        // Check if formula has steps that need selection
        const hasSteps = formula.steps && formula.steps.length > 0;
        
        if (hasSteps) {
            // Open modal for selection
            setFormulaModal({ open: true, formula });
        } else {
            // Simple formula without steps - add directly
            const formulaItem = {
                id: `formula-${formula.id}-${Date.now()}`,
                product_id: formula.id,
                product_name: formula.name,
                name: formula.name,
                price: formula.price,
                quantity: 1,
                is_formula: true,
                meal_type: formula.meal_type,
                selected_items: [],
                supplements: [],
                kitchen_items: [],
                bar_items: []
            };
            setCart(prev => [...prev, formulaItem]);
            triggerHapticFeedback();
            toast.success(`${formula.name} ajouté au panier`);
        }
    };
    
    // Handle formula modal add to cart
    const handleFormulaAddToCart = async (formulaOrder) => {
        try {
            // Send to backend with formula data
            const updatedCart = await addItemToBackend({
                id: formulaOrder.product_id,
                name: formulaOrder.name,
                price: formulaOrder.price,
                destination: 'cuisine'
            }, {
                quantity: 1,
                destination: 'cuisine',
                is_formula: true,
                selected_items: formulaOrder.selected_items || [],
                all_items: formulaOrder.all_items || [],
                supplements: formulaOrder.supplements || []
            });
            
            // Update local state from backend response
            if (updatedCart?.items) {
                const mappedItems = updatedCart.items.map(item => ({
                    id: item.id,
                    product_id: item.product_id,
                    product_name: item.product_name,
                    name: item.product_name,
                    price: item.unit_price,
                    quantity: item.quantity,
                    destination: item.destination,
                    category: item.category,
                    supplements: item.supplements || [],
                    accompaniment: item.accompaniment,
                    is_formula: item.is_formula,
                    selected_items: item.selected_items || [],
                    notes: item.notes,
                    sent_to_kitchen: false
                }));
                setCart(mappedItems);
            }
            
            setFormulaModal({ open: false, formula: null });
            triggerHapticFeedback();
            toast.success(`${formulaOrder.name} ajouté au panier`);
            
        } catch (error) {
            console.error('Error adding formula to cart:', error);
            // Fallback: add to local cart only
            setCart(prev => [...prev, formulaOrder]);
            setFormulaModal({ open: false, formula: null });
            triggerHapticFeedback();
            toast.success(`${formulaOrder.name} ajouté (local)`);
        }
    };
    
    // Open edit dialog for a formula - Use quick edit modal
    const openEditFormulaDialog = (formula) => {
        setFormulaQuickEdit({ open: true, formula: formula });
    };
    
    const addProductToFormulaStep = (stepIndex, product) => {
        setNewFormula(prev => {
            const newSteps = [...prev.steps];
            // Check if product is already in this step
            if (!newSteps[stepIndex].choices.find(c => c.product_id === product.id)) {
                newSteps[stepIndex].choices.push({
                    product_id: product.id,
                    product_name: product.name,
                    is_default: newSteps[stepIndex].choices.length === 0
                });
            }
            return { ...prev, steps: newSteps };
        });
    };
    
    const removeProductFromFormulaStep = (stepIndex, productId) => {
        setNewFormula(prev => {
            const newSteps = [...prev.steps];
            newSteps[stepIndex].choices = newSteps[stepIndex].choices.filter(c => c.product_id !== productId);
            return { ...prev, steps: newSteps };
        });
    };
    
    const addFormulaStep = () => {
        setNewFormula(prev => ({
            ...prev,
            steps: [...prev.steps, { name: `Étape ${prev.steps.length + 1}`, choices: [], required: false }]
        }));
    };
    
    const removeFormulaStep = (stepIndex) => {
        setNewFormula(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== stepIndex)
        }));
    };
    
    const updateFormulaStepName = (stepIndex, name) => {
        setNewFormula(prev => {
            const newSteps = [...prev.steps];
            newSteps[stepIndex].name = name;
            return { ...prev, steps: newSteps };
        });
    };
    
    // ========================================
    // FILTERED PRODUCTS
    // ========================================
    
    const filteredProducts = useMemo(() => {
        // Use displayProducts (role/bar filtered) instead of raw products
        let filtered = displayProducts || products;
        
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(p => p.category === selectedCategory);
        }
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(p => p.name.toLowerCase().includes(query));
        }
        
        // Sort: available first, then popular, then alphabetical
        const popularIds = new Set(popularProducts.map(p => p.id));
        
        filtered.sort((a, b) => {
            if (a.is_available !== b.is_available) return a.is_available ? -1 : 1;
            const aPopular = popularIds.has(a.id);
            const bPopular = popularIds.has(b.id);
            if (aPopular !== bPopular) return aPopular ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        
        return filtered;
    }, [displayProducts, products, selectedCategory, searchQuery, popularProducts]);
    
    // Top 6 popular
    const topPopular = useMemo(() => {
        if (selectedCategory === 'all') return popularProducts.slice(0, 6);
        return popularProducts.filter(p => p.category === selectedCategory).slice(0, 6);
    }, [popularProducts, selectedCategory]);
    
    // ========================================
    // RENDER
    // ========================================
    
    // Register Closed Screen - Block all access
    if (registerClosed) {
        return (
            <div className="fixed inset-0 bg-red-900/95 z-[9999] flex items-center justify-center">
                <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">
                        Caisse Fermée
                    </h1>
                    <p className="text-gray-600 mb-6">
                        {registerClosedReason === 'emergency' 
                            ? 'Cette caisse a été fermée d\'urgence par un administrateur.'
                            : 'Cette caisse a été fermée et validée par la direction.'
                        }
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        Vous allez être redirigé vers le tableau de bord...
                    </p>
                    <Button
                        className="w-full"
                        onClick={() => navigate('/dashboard', { replace: true })}
                    >
                        Retour au tableau de bord
                    </Button>
                </div>
            </div>
        );
    }
    
    // Session Loading Screen - Show while checking for cash session
    if (sessionLoading) {
        return (
            <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[9999] flex items-center justify-center">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
                    <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Chargement de la session...
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Vérification de votre caisse en cours
                    </p>
                </div>
            </div>
        );
    }
    
    // No Session Screen - Block non-admin users without a session
    // Admins can open a register from this screen
    if (!loading && !cashSession) {
        const canOpenRegister = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';
        
        return (
            <>
                <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[9999] flex items-center justify-center">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
                        <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertCircle className="w-10 h-10 text-amber-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            Aucune Session Active
                        </h1>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {canOpenRegister 
                                ? 'Vous n\'avez pas de caisse ouverte. Ouvrez une caisse pour commencer à travailler.'
                                : 'Vous n\'avez pas de caisse ouverte. Veuillez contacter un manager pour ouvrir une caisse.'
                            }
                        </p>
                        
                        <div className="space-y-3">
                            {canOpenRegister && (
                                <Button
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg"
                                    onClick={handleShowOpeningDialog}
                                >
                                    <Lock className="w-5 h-5 mr-2" />
                                    Ouvrir une Caisse
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => navigate('/dashboard', { replace: true })}
                            >
                                Retour au tableau de bord
                            </Button>
                        </div>
                    </div>
                </div>
                
                {/* Register Opening Dialog */}
                <RegisterOpeningDialog
                    open={openingDialog.open}
                    onOpenChange={(open) => setOpeningDialog({ open })}
                    registers={availableRegisters}
                    currentUser={user}
                    onOpen={handleOpenRegister}
                    api={api}
                />
            </>
        );
    }
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[80vh]">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg">Chargement...</p>
                </div>
            </div>
        );
    }
    
    return (
        <>
            {/* POS Lock Screen */}
            <POSLockScreen
                isLocked={isLocked}
                onUnlock={handleUnlockPOS}
                user={user}
                isAdmin={isAdmin}
                lockedByInactivity={lockedByInactivity}
            />
            
            {/* Ready Orders Notification Panel */}
            {showReadyPanel && (
                <ReadyOrdersPanel 
                    orders={readyOrders}
                    onDismiss={() => setShowReadyPanel(false)}
                    onServed={markOrderServed}
                    soundEnabled={readySoundEnabled}
                    onToggleSound={() => setReadySoundEnabled(!readySoundEnabled)}
                />
            )}
            
            {/* Bar/Kitchen Notification Popup */}
            {notificationPopup && (
                <NotificationPopup
                    notification={notificationPopup}
                    onClose={() => setNotificationPopup(null)}
                    onServed={markOrderServed}
                />
            )}
            
            <div className="h-[calc(100vh-56px)] sm:h-[calc(100vh-56px)] lg:h-[calc(100vh-100px)] flex flex-col" data-testid="pos-page-v2">
            
            {/* ===== SUPERVISION BAR (Real-time stats for managers) ===== */}
            {isManager && (
                <div className="hidden sm:block">
                    <SupervisionBar 
                        stats={supervisionStats}
                        recentActivity={recentActivity}
                        isManager={isManager}
                        onOpenSettings={canAccessSettings ? () => navigate('/supervision-settings') : null}
                    />
                </div>
            )}
            
            {/* ===== TOP BAR - Clean & Professional Style ===== */}
            <div className="flex items-center gap-2 mb-2 px-2 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex-wrap sm:flex-nowrap">
                {/* HOME BUTTON - Retour à l'accueil pour tous les profils */}
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 rounded-lg gap-1.5"
                    onClick={() => navigate('/dashboard', { replace: true })}
                    data-testid="home-button"
                >
                    <Home className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="hidden sm:inline text-xs font-medium">Accueil</span>
                </Button>
                
                {/* Connection Status Indicator */}
                <ConnectionStatusIndicator 
                    connectionState={connectionState}
                    isOnline={wsIsOnline}
                    reconnectAttempts={reconnectAttempts}
                    size="small"
                    showLabel={false}
                />
                
                {/* BAR MODE BADGE - Visible when on Bar Register */}
                {isBarRegister && (
                    <div className="relative group">
                        <button
                            onClick={() => setShowAllProductsInBarMode(!showAllProductsInBarMode)}
                            className={cn(
                                "flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all",
                                showAllProductsInBarMode 
                                    ? "bg-emerald-100 border-emerald-400 hover:bg-emerald-200"
                                    : "bg-amber-100 border-amber-400 hover:bg-amber-200"
                            )}
                            data-testid="bar-mode-toggle"
                        >
                            <span className="text-lg">{showAllProductsInBarMode ? '🍽️' : '🍺'}</span>
                            <span className={cn(
                                "font-semibold text-sm",
                                showAllProductsInBarMode ? "text-emerald-800" : "text-amber-800"
                            )}>
                                {showAllProductsInBarMode ? 'TOUS' : 'MODE BAR'}
                            </span>
                            <ChevronDown className={cn(
                                "w-3 h-3 transition-transform",
                                showAllProductsInBarMode ? "text-emerald-600 rotate-180" : "text-amber-600"
                            )} />
                        </button>
                        {/* Tooltip with explanation */}
                        <div className="absolute top-full left-0 mt-1 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 w-48 pointer-events-none">
                            {showAllProductsInBarMode 
                                ? "Affichage de tous les produits. Cliquez pour revenir au mode bar (boissons uniquement)."
                                : "Mode Bar actif : seules les boissons sont affichées. Cliquez pour voir tous les produits (plats inclus)."}
                        </div>
                    </div>
                )}
                
                {/* Table Badge */}
                <button
                    onClick={() => navigate('/tables')}
                    className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all",
                        "border shadow-sm min-w-[90px] h-9",
                        selectedTable 
                            ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600" 
                            : "bg-white text-gray-700 border-gray-300 hover:border-emerald-500 hover:bg-emerald-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                    )}
                    data-testid="table-selector-btn"
                >
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{selectedTable ? `Table ${selectedTable.number}` : 'Table'}</span>
                </button>
                
                {selectedTable && (
                    <Input
                        placeholder="Client"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-[100px] sm:max-w-[130px] h-9 text-sm"
                    />
                )}
                
                {/* Search */}
                <div className="flex-1 relative min-w-[120px] order-first sm:order-none w-full sm:w-auto mb-2 sm:mb-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        ref={searchRef}
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 text-sm"
                    />
                </div>
                
                {/* Fast Service Toggle - Harmonized style */}
                {isManager && (
                    <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 h-9 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <Zap className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Rapide</span>
                        <Switch
                            checked={fastServiceMode}
                            onCheckedChange={setFastServiceMode}
                            className="scale-75"
                        />
                    </div>
                )}
                
                {/* Manager Mode Toggle - Harmonized style */}
                {isManager && (
                    <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 h-9 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Manager</span>
                        <Switch
                            checked={managerMode}
                            onCheckedChange={setManagerMode}
                            className="scale-75"
                        />
                    </div>
                )}
                
                {/* Category Management Button - Only in Manager Mode */}
                {managerMode && isManager && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="hidden md:flex h-9 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 rounded-lg gap-1.5"
                        onClick={() => setCategoryManagerOpen(true)}
                        data-testid="category-manager-btn"
                    >
                        <FolderPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-xs font-medium">Catégories</span>
                    </Button>
                )}
                
                {/* Formula Management Button - Only in Manager Mode */}
                {managerMode && isManager && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="hidden md:flex h-9 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 rounded-lg gap-1.5"
                        onClick={() => setFormulaManagerOpen(true)}
                        data-testid="formula-manager-btn"
                    >
                        <UtensilsCrossed className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-xs font-medium">Formules</span>
                    </Button>
                )}
                
                {/* Cash Closing Button - Harmonized emerald theme */}
                {isManager && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="hidden md:flex h-9 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 rounded-lg gap-1.5"
                        onClick={() => setCashClosingModal({ 
                            open: true, 
                            registerId: cashSession?.register_id, 
                            registerName: cashSession?.register_name || 'Caisse',
                            sessionId: cashSession?.id
                        })}
                        data-testid="cash-closing-btn"
                    >
                        <Coins className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-xs font-medium">Clôture</span>
                    </Button>
                )}
                
                {/* Ready Orders Badge - Emerald theme */}
                {readyOrders.length > 0 && (
                    <button
                        onClick={() => setShowReadyPanel(true)}
                        className="flex items-center gap-1.5 px-3 h-9 rounded-lg font-medium text-sm transition-all bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="ready-orders-badge"
                    >
                        <Bell className="w-4 h-4" />
                        <span>{readyOrders.length} prête{readyOrders.length > 1 ? 's' : ''}</span>
                    </button>
                )}
                
                {/* Offline/Sync Indicator - Subtle style */}
                {(isOffline || pendingOrdersCount > 0 || isSyncing) && (
                    <div className={cn(
                        "flex items-center gap-1.5 px-3 h-9 rounded-lg font-medium text-sm transition-all",
                        isOffline 
                            ? "bg-gray-700 text-white" 
                            : isSyncing 
                                ? "bg-emerald-600 text-white" 
                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                    )} data-testid="offline-indicator">
                        {isOffline ? (
                            <>
                                <WifiOff className="w-4 h-4" />
                                <span className="hidden sm:inline">Hors-ligne</span>
                                {pendingOrdersCount > 0 && (
                                    <Badge variant="secondary" className="bg-white/20 text-white text-xs px-1.5">
                                        {pendingOrdersCount}
                                    </Badge>
                                )}
                            </>
                        ) : isSyncing ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                <span className="hidden sm:inline">Sync...</span>
                            </>
                        ) : pendingOrdersCount > 0 ? (
                            <>
                                <Clock className="w-4 h-4" />
                                <span className="hidden sm:inline">{pendingOrdersCount} en attente</span>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 px-1.5 text-xs"
                                    onClick={syncPendingOrders}
                                >
                                    <RefreshCw className="w-3 h-3" />
                                </Button>
                            </>
                        ) : null}
                    </div>
                )}
                
                {/* Separator */}
                <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
                
                {/* Close Session Button - ADMIN ONLY - Subtle danger */}
                {cashSession && isAdmin && (
                    <button
                        onClick={handleOpenClosingDialog}
                        disabled={checkingOpenOrders}
                        className={cn(
                            "flex items-center gap-1.5 px-3 h-9 rounded-lg font-medium text-sm transition-all",
                            "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700",
                            "hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800",
                            checkingOpenOrders && "opacity-50 cursor-wait"
                        )}
                        data-testid="close-session-btn"
                    >
                        {checkingOpenOrders ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <LogOut className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">Fermer</span>
                    </button>
                )}
                
                {/* Lock POS Button - Subtle style */}
                <button
                    onClick={handleLockPOS}
                    className="flex items-center gap-1.5 px-3 h-9 rounded-lg font-medium text-sm transition-all bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Verrouiller la caisse"
                    data-testid="lock-pos-btn"
                >
                    <Lock className="w-4 h-4" />
                    <span className="hidden sm:inline">Verrouiller</span>
                </button>
                
                {/* Theme Switch */}
                <ThemeSwitch compact />
                
                {/* Time - Gabon */}
                <div className="hidden lg:flex items-center h-9 px-2 text-sm font-mono text-gray-600 dark:text-gray-400">
                    {currentTime}
                </div>
            </div>
            
            {/* ===== MANAGER ACTION BAR - Harmonized with emerald theme ===== */}
            {managerMode && isManager && (
                <div className="flex items-center gap-2 mb-2 px-2 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto scrollbar-hide">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 px-2 whitespace-nowrap uppercase tracking-wide">Manager:</span>
                    <Button
                        size="sm"
                        onClick={() => setProductEditDialog({ open: true, product: { name: '', price: 0, category: 'plats', is_available: true, has_accompaniments: false }, isNew: true })}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white touch-target min-h-[36px]"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Produit</span>
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setFormulaDialog({ open: true })}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white touch-target min-h-[36px]"
                    >
                        <Package className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Formule</span>
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setSupplementEditDialog({ open: true, supplement: { name: '', price: 0, is_active: true }, isNew: true })}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white touch-target min-h-[36px]"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Supplément</span>
                    </Button>
                    {canAccessSettings && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSettingsDialog({ open: true })}
                            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 touch-target min-h-[36px]"
                        >
                            <Settings className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Paramètres</span>
                        </Button>
                    )}
                </div>
            )}
            
            {/* ===== MAIN LAYOUT - Responsive ===== */}
            <div className="flex-1 flex flex-col md:flex-row gap-2 min-h-0 pb-20 md:pb-0">
                
                {/* LEFT - Categories - Horizontal scroll on mobile/tablet, vertical on desktop */}
                <div className="md:w-[130px] flex md:flex-col bg-card rounded-xl border order-first">
                    <div className="hidden md:block p-2 border-b bg-muted/30">
                        <h3 className="text-[10px] font-bold text-muted-foreground uppercase">Catégories</h3>
                    </div>
                    {/* Mobile: Native horizontal scroll | Desktop: ScrollArea vertical */}
                    <div className="md:hidden overflow-x-auto overflow-y-hidden scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <div className="flex gap-1.5 p-1.5 pr-6" style={{ minWidth: 'max-content' }}>
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={cn(
                                    "flex flex-col items-center justify-center gap-0.5 rounded-lg transition-all touch-manipulation active:scale-95 flex-none",
                                    "w-[72px] h-[68px] px-1.5 py-1.5",
                                    selectedCategory === 'all' ? "bg-primary text-primary-foreground" : "hover:bg-muted/50"
                                )}
                            >
                                <span className="text-lg leading-none flex items-center justify-center w-6 h-6">🍽️</span>
                                <span className="text-[9px] font-semibold leading-tight">Tous</span>
                                <span className="text-[8px] opacity-70 leading-none">{products.filter(p => p.is_available !== false).length}</span>
                            </button>
                            
                            {/* Formulas/Menus Button - Mobile */}
                            {formulas.length > 0 && (
                                <button
                                    onClick={() => setSelectedCategory('formulas')}
                                    className={cn(
                                        "flex flex-col items-center justify-center gap-0.5 rounded-lg transition-all touch-manipulation active:scale-95 flex-none border",
                                        "w-[72px] h-[68px] px-1.5 py-1.5",
                                        selectedCategory === 'formulas' 
                                            ? "bg-emerald-600 text-white border-emerald-600" 
                                            : "hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                                    )}
                                >
                                    <span className="text-lg leading-none flex items-center justify-center w-6 h-6">🎁</span>
                                    <span className="text-[9px] font-semibold leading-tight">Formules</span>
                                    <span className="text-[8px] opacity-70 leading-none">{formulas.length}</span>
                                </button>
                            )}
                            
                            {displayCategories.map((cat) => {
                                const count = displayProducts.filter(p => p.category === cat.id && p.is_available !== false).length;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className={cn(
                                            "flex flex-col items-center justify-center gap-0.5 rounded-lg transition-all touch-manipulation active:scale-95 flex-none",
                                            "w-[72px] h-[68px] px-1.5 py-1.5",
                                            selectedCategory === cat.id ? "bg-primary text-primary-foreground" : "hover:bg-muted/50"
                                        )}
                                    >
                                        <span className="text-lg leading-none flex items-center justify-center w-6 h-6">{cat.icon || '📦'}</span>
                                        <span className="text-[9px] font-semibold text-center leading-[1.1] line-clamp-2 w-full px-0.5">{cat.name}</span>
                                        <span className="text-[8px] opacity-70 leading-none">{count}</span>
                                    </button>
                                );
                            })}
                            
                            {/* Mobile - Category Management Button */}
                            {managerMode && isManager && (
                                <button
                                    onClick={() => setCategoryManagerOpen(true)}
                                    className="flex flex-col items-center justify-center gap-0.5 rounded-lg transition-all touch-manipulation active:scale-95 flex-none border-2 border-dashed border-primary/30 w-[60px] h-[68px] px-1.5 py-1.5"
                                >
                                    <Plus className="w-4 h-4 text-primary" />
                                    <span className="text-[9px] font-medium text-primary">Gérer</span>
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Desktop: Vertical ScrollArea */}
                    <ScrollArea className="hidden md:block md:flex-1">
                        <div className="flex flex-col gap-1.5 p-1.5">
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={cn(
                                    "flex flex-col items-center justify-center gap-1 rounded-xl transition-all touch-manipulation active:scale-95 w-full",
                                    "min-h-[72px] py-2.5 px-2",
                                    selectedCategory === 'all' ? "bg-primary text-primary-foreground" : "hover:bg-muted/50"
                                )}
                            >
                                <span className="text-xl leading-none flex items-center justify-center w-7 h-7">🍽️</span>
                                <span className="text-[11px] font-semibold leading-tight">Tous</span>
                                <span className="text-[10px] opacity-70 leading-none">{products.filter(p => p.is_available !== false).length}</span>
                            </button>
                            
                            {/* Formulas/Menus Button - Desktop */}
                            {formulas.length > 0 && (
                                <button
                                    onClick={() => setSelectedCategory('formulas')}
                                    className={cn(
                                        "flex flex-col items-center justify-center gap-1 rounded-xl transition-all touch-manipulation active:scale-95 w-full border",
                                        "min-h-[72px] py-2.5 px-2",
                                        selectedCategory === 'formulas' 
                                            ? "bg-emerald-600 text-white border-emerald-600" 
                                            : "hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                                    )}
                                >
                                    <span className="text-xl leading-none flex items-center justify-center w-7 h-7">🎁</span>
                                    <span className="text-[11px] font-semibold leading-tight">Formules</span>
                                    <span className="text-[10px] opacity-70 leading-none">{formulas.length}</span>
                                </button>
                            )}
                            
                            {displayCategories.map((cat) => {
                                const count = displayProducts.filter(p => p.category === cat.id && p.is_available !== false).length;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(cat.id)}
                                        className={cn(
                                            "flex flex-col items-center justify-center gap-1 rounded-xl transition-all touch-manipulation active:scale-95 w-full",
                                            "min-h-[72px] py-2.5 px-2",
                                            selectedCategory === cat.id ? "bg-primary text-primary-foreground" : "hover:bg-muted/50"
                                        )}
                                    >
                                        <span className="text-xl leading-none flex items-center justify-center w-7 h-7">{cat.icon || '📦'}</span>
                                        <span className="text-[11px] font-semibold text-center leading-[1.15] line-clamp-2 w-full max-w-[80px]">{cat.name}</span>
                                        <span className="text-[10px] opacity-70 leading-none">{count}</span>
                                    </button>
                                );
                            })}
                            
                            {/* Category Management Button - Manager Mode Only */}
                            {managerMode && isManager && (
                                <button
                                    onClick={() => setCategoryManagerOpen(true)}
                                    className="flex flex-col items-center justify-center gap-1 rounded-xl transition-all touch-manipulation active:scale-95 w-full border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 min-h-[72px] py-2.5 px-2"
                                >
                                    <Plus className="w-5 h-5 text-primary" />
                                    <span className="text-[10px] font-medium text-primary">Gérer</span>
                                </button>
                            )}
                        </div>
                    </ScrollArea>
                </div>
                
                {/* CENTER - Products Grid */}
                <div className="flex-1 flex flex-col bg-card rounded-xl border overflow-hidden min-h-[200px] md:min-h-0">
                    <div className="p-2 border-b bg-muted/30 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold">
                                {selectedCategory === 'formulas' ? 'Formules & Menus' : selectedCategory === 'all' ? 'Tous les plats' : categories.find(c => c.id === selectedCategory)?.name || 'Plats'}
                            </h3>
                            {topPopular.length > 0 && (
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700 hidden sm:flex">
                                    <Flame className="w-3 h-3 mr-1" />
                                    Populaires
                                </Badge>
                            )}
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            {filteredProducts.filter(p => getStockStatus(p) !== 'out').length} dispo
                        </Badge>
                    </div>
                    
                    <ScrollArea className="flex-1 p-2 sm:p-3">
                        {/* Formulas Display */}
                        {selectedCategory === 'formulas' ? (
                            <div className="space-y-3">
                                {/* Petit Déjeuner - Orange/Yellow - ACCORDION */}
                                {formulas.filter(f => f.is_available && f.meal_type === 'petit_dejeuner').length > 0 && (
                                    <div className="rounded-xl border border-gray-200 dark:border-zinc-700 overflow-hidden">
                                        <button
                                            onClick={() => setExpandedFormulaSections(prev => ({
                                                ...prev,
                                                petit_dejeuner: !prev.petit_dejeuner
                                            }))}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors border-b border-gray-200 dark:border-zinc-700"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                                    <span className="text-lg">🌅</span>
                                                </div>
                                                <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200">Petit Déjeuner</h4>
                                                <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                                    {formulas.filter(f => f.is_available && f.meal_type === 'petit_dejeuner').length} formules
                                                </Badge>
                                            </div>
                                            {expandedFormulaSections.petit_dejeuner ? (
                                                <ChevronUp className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                        </button>
                                        {expandedFormulaSections.petit_dejeuner && (
                                            <div className="p-3 bg-white dark:bg-zinc-900 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {formulas.filter(f => f.is_available && f.meal_type === 'petit_dejeuner').map((formula) => (
                                                <FormulaCard
                                                    key={formula.id}
                                                    formula={formula}
                                                    managerMode={managerMode}
                                                    canEdit={canEditFormulas}
                                                    onAddToCart={addFormulaToCart}
                                                    onEdit={openEditFormulaDialog}
                                                    onDuplicate={duplicateFormula}
                                                    onDelete={deleteFormula}
                                                />
                                            ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Déjeuner - Green - ACCORDION */}
                                {formulas.filter(f => f.is_available && f.meal_type === 'dejeuner').length > 0 && (
                                    <div className="rounded-xl border border-green-200 overflow-hidden">
                                        <button
                                            onClick={() => setExpandedFormulaSections(prev => ({
                                                ...prev,
                                                dejeuner: !prev.dejeuner
                                            }))}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors border-b border-gray-200 dark:border-zinc-700"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                                    <span className="text-lg">☀️</span>
                                                </div>
                                                <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200">Déjeuner / Midi</h4>
                                                <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                                    {formulas.filter(f => f.is_available && f.meal_type === 'dejeuner').length} formules
                                                </Badge>
                                            </div>
                                            {expandedFormulaSections.dejeuner ? (
                                                <ChevronUp className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                        </button>
                                        {expandedFormulaSections.dejeuner && (
                                            <div className="p-3 bg-white dark:bg-zinc-900 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {formulas.filter(f => f.is_available && f.meal_type === 'dejeuner').map((formula) => (
                                                <FormulaCard
                                                    key={formula.id}
                                                    formula={formula}
                                                    managerMode={managerMode}
                                                    canEdit={canEditFormulas}
                                                    onAddToCart={addFormulaToCart}
                                                    onEdit={openEditFormulaDialog}
                                                    onDuplicate={duplicateFormula}
                                                    onDelete={deleteFormula}
                                                />
                                            ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Dîner - Blue/Purple - ACCORDION */}
                                {formulas.filter(f => f.is_available && f.meal_type === 'diner').length > 0 && (
                                    <div className="rounded-xl border border-gray-200 dark:border-zinc-700 overflow-hidden">
                                        <button
                                            onClick={() => setExpandedFormulaSections(prev => ({
                                                ...prev,
                                                diner: !prev.diner
                                            }))}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors border-b border-gray-200 dark:border-zinc-700"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                                    <span className="text-lg">🌙</span>
                                                </div>
                                                <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200">Dîner / Soir</h4>
                                                <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                                    {formulas.filter(f => f.is_available && f.meal_type === 'diner').length} formules
                                                </Badge>
                                            </div>
                                            {expandedFormulaSections.diner ? (
                                                <ChevronUp className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                        </button>
                                        {expandedFormulaSections.diner && (
                                            <div className="p-3 bg-white dark:bg-zinc-900 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {formulas.filter(f => f.is_available && f.meal_type === 'diner').map((formula) => (
                                                <FormulaCard
                                                    key={formula.id}
                                                    formula={formula}
                                                    managerMode={managerMode}
                                                    canEdit={canEditFormulas}
                                                    onAddToCart={addFormulaToCart}
                                                    onEdit={openEditFormulaDialog}
                                                    onDuplicate={duplicateFormula}
                                                    onDelete={deleteFormula}
                                                />
                                            ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Toute la journée - Harmonized - ACCORDION */}
                                {formulas.filter(f => f.is_available && (f.meal_type === 'all' || !f.meal_type)).length > 0 && (
                                    <div className="rounded-xl border border-gray-200 dark:border-zinc-700 overflow-hidden">
                                        <button
                                            onClick={() => setExpandedFormulaSections(prev => ({
                                                ...prev,
                                                all: !prev.all
                                            }))}
                                            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors border-b border-gray-200 dark:border-zinc-700"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                                    <span className="text-lg">🍽️</span>
                                                </div>
                                                <h4 className="font-bold text-sm text-gray-800 dark:text-gray-200">Toute la journée</h4>
                                                <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px]">
                                                    {formulas.filter(f => f.is_available && (f.meal_type === 'all' || !f.meal_type)).length} formules
                                                </Badge>
                                            </div>
                                            {expandedFormulaSections.all ? (
                                                <ChevronUp className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                        </button>
                                        {expandedFormulaSections.all && (
                                            <div className="p-3 bg-white dark:bg-zinc-900 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {formulas.filter(f => f.is_available && (f.meal_type === 'all' || !f.meal_type)).map((formula) => (
                                                <FormulaCard
                                                    key={formula.id}
                                                    formula={formula}
                                                    managerMode={managerMode}
                                                    canEdit={canEditFormulas}
                                                    onAddToCart={addFormulaToCart}
                                                    onEdit={openEditFormulaDialog}
                                                    onDuplicate={duplicateFormula}
                                                    onDelete={deleteFormula}
                                                />
                                            ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formulas.filter(f => f.is_available).length === 0 && (
                                    <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                                        <Package className="w-16 h-16 mb-3 opacity-20" />
                                        <p>Aucune formule disponible</p>
                                        {managerMode && (
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                className="mt-3"
                                                onClick={() => setFormulaDialog({ open: true })}
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Créer une formule
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                                <Package className="w-20 h-20 mb-4 opacity-20" />
                                <p className="text-lg">Aucun produit</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Popular Section */}
                                {selectedCategory === 'all' && topPopular.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                                                <Flame className="w-4 h-4 text-white" />
                                            </div>
                                            <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300">Populaires</h4>
                                            <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px]">Top ventes</Badge>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-2 lg:grid-cols-4 xl:grid-cols-5">
                                            {topPopular.slice(0, 4).map((product) => {
                                                const stockStatus = getStockStatus(product);
                                                const isDisabled = stockStatus === 'out';
                                                const category = categories.find(c => c.id === product.category);
                                                const colorScheme = getCategoryColorScheme(category?.name);
                                                const quantityInCart = getProductQuantityInCart(product.id);
                                                const hasStock = product.stock_quantity !== undefined && product.stock_quantity !== null;
                                                
                                                return (
                                                    <div 
                                                        key={`pop-${product.id}`} 
                                                        className="relative group"
                                                        data-testid={`product-card-${product.id}`}
                                                    >
                                                        <div
                                                            onClick={() => {
                                                                if (!isDisabled && !managerMode) {
                                                                    triggerHapticFeedback();
                                                                    addToCart(product);
                                                                }
                                                            }}
                                                            className={cn(
                                                                "relative rounded-xl border overflow-hidden cursor-pointer select-none",
                                                                "transition-all duration-150 ease-out",
                                                                "bg-white dark:bg-zinc-800",
                                                                isDisabled && !managerMode 
                                                                    ? "opacity-60 grayscale cursor-not-allowed border-gray-300" 
                                                                    : "border-gray-200 dark:border-zinc-700 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-lg hover:scale-[1.02]",
                                                                "active:scale-[0.97]",
                                                                "min-h-[100px] sm:min-h-[90px]"
                                                            )}
                                                        >
                                                            {/* Top accent bar - emerald for popular */}
                                                            <div className={cn(
                                                                "h-1 w-full",
                                                                isDisabled ? "bg-gray-400" : "bg-emerald-500"
                                                            )} />
                                                            
                                                            {/* Stock badge - TOP RIGHT */}
                                                            {hasStock && (
                                                                <div className={cn(
                                                                    "absolute top-2.5 right-2 z-10",
                                                                    "min-w-[28px] h-6 px-2 rounded-full",
                                                                    "flex items-center justify-center",
                                                                    "text-xs font-bold shadow-md",
                                                                    stockStatus === 'out' 
                                                                        ? "bg-red-500 text-white" 
                                                                        : stockStatus === 'low' 
                                                                            ? "bg-orange-500 text-white" 
                                                                            : "bg-emerald-500 text-white"
                                                                )}>
                                                                    {stockStatus === 'out' ? '0' : Math.floor(product.stock_quantity)}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Quantity in cart badge - TOP LEFT */}
                                                            {quantityInCart > 0 && (
                                                                <div className="absolute top-2.5 left-2 z-10">
                                                                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-600 text-white font-bold text-sm shadow-lg ring-2 ring-white dark:ring-zinc-800">
                                                                        {quantityInCart}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Popular star icon - TOP LEFT (when no cart qty) */}
                                                            {quantityInCart === 0 && !isDisabled && (
                                                                <div className="absolute top-2.5 left-2 z-10">
                                                                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
                                                                        <Star className="w-3.5 h-3.5 text-white fill-white" />
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Main content area */}
                                                            <div className="p-3 sm:p-2.5 flex flex-col justify-between h-[calc(100%-6px)]">
                                                                {/* Product Name - TOP, prominent, left-aligned */}
                                                                <h3 className={cn(
                                                                    "font-bold text-sm sm:text-xs leading-tight line-clamp-2 text-left",
                                                                    isDisabled 
                                                                        ? "text-gray-500 dark:text-gray-400" 
                                                                        : "text-gray-900 dark:text-white"
                                                                )}>
                                                                    {product.name}
                                                                </h3>
                                                                
                                                                {/* Bottom row: Price left, Icon right */}
                                                                <div className="flex items-end justify-between mt-2">
                                                                    <div className="flex items-baseline gap-0.5">
                                                                        <span className={cn(
                                                                            "text-lg sm:text-base font-black",
                                                                            isDisabled 
                                                                                ? "text-gray-400" 
                                                                                : "text-emerald-600 dark:text-emerald-400"
                                                                        )}>
                                                                            {product.price?.toLocaleString('fr-FR')}
                                                                        </span>
                                                                        <span className={cn(
                                                                            "text-xs font-semibold",
                                                                            isDisabled 
                                                                                ? "text-gray-400" 
                                                                                : "text-emerald-600/70"
                                                                        )}>F</span>
                                                                    </div>
                                                                    <span className={cn(
                                                                        "text-lg sm:text-base",
                                                                        isDisabled ? "opacity-40" : "opacity-60"
                                                                    )}>
                                                                        {category?.icon || '🍽️'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Out of Stock Overlay */}
                                                            {isDisabled && !managerMode && (
                                                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 dark:bg-zinc-900/80">
                                                                    <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                                                                        RUPTURE
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                
                                {/* All Products Section */}
                                <div>
                                    {selectedCategory === 'all' && topPopular.length > 0 && (
                                        <div className="flex items-center gap-2 mb-3 mt-2">
                                            <Package className="w-5 h-5 text-gray-500" />
                                            <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300">Tous les produits</h4>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-2 lg:grid-cols-4 xl:grid-cols-5">
                                        {(selectedCategory === 'all' 
                                            ? filteredProducts.filter(p => !topPopular.some(pop => pop.id === p.id))
                                            : filteredProducts
                                        ).map((product) => {
                                            const stockStatus = getStockStatus(product);
                                            const isPopular = popularProducts.some(p => p.id === product.id);
                                            const isDisabled = stockStatus === 'out';
                                            const category = categories.find(c => c.id === product.category);
                                            const colorScheme = getCategoryColorScheme(category?.name);
                                            const quantityInCart = getProductQuantityInCart(product.id);
                                            const hasStock = product.stock_quantity !== undefined && product.stock_quantity !== null;
                                            
                                            return (
                                                <div 
                                                    key={product.id} 
                                                    className="relative group"
                                                    data-testid={`product-card-${product.id}`}
                                                >
                                                    <div
                                                        onClick={() => {
                                                            if (!isDisabled && !managerMode) {
                                                                triggerHapticFeedback();
                                                                addToCart(product);
                                                            }
                                                        }}
                                                        className={cn(
                                                            "relative rounded-xl border overflow-hidden cursor-pointer select-none",
                                                            "transition-all duration-150 ease-out",
                                                            "bg-white dark:bg-zinc-800",
                                                            isDisabled && !managerMode 
                                                                ? "opacity-60 grayscale cursor-not-allowed border-gray-300 dark:border-zinc-600" 
                                                                : cn(colorScheme.border, "hover:shadow-lg hover:scale-[1.02]"),
                                                            "active:scale-[0.97]",
                                                            "min-h-[100px] sm:min-h-[90px]"
                                                        )}
                                                    >
                                                        {/* Top color accent bar */}
                                                        <div className={cn(
                                                            "h-1 w-full",
                                                            isDisabled ? "bg-gray-400" : colorScheme.accent
                                                        )} />
                                                        
                                                        {/* Stock badge - TOP RIGHT */}
                                                        {hasStock && (
                                                            <div className={cn(
                                                                "absolute top-2.5 right-2 z-10",
                                                                "min-w-[28px] h-6 px-2 rounded-full",
                                                                "flex items-center justify-center",
                                                                "text-xs font-bold shadow-md",
                                                                stockStatus === 'out' 
                                                                    ? "bg-red-500 text-white" 
                                                                    : stockStatus === 'low' 
                                                                        ? "bg-orange-500 text-white" 
                                                                        : "bg-emerald-500 text-white"
                                                            )}>
                                                                {stockStatus === 'out' ? '0' : Math.floor(product.stock_quantity)}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Quantity in cart badge - TOP LEFT */}
                                                        {quantityInCart > 0 && (
                                                            <div className="absolute top-2.5 left-2 z-10">
                                                                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-emerald-600 text-white font-bold text-sm shadow-lg ring-2 ring-white dark:ring-zinc-800">
                                                                    {quantityInCart}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Main content area */}
                                                        <div className="p-3 sm:p-2.5 flex flex-col justify-between h-[calc(100%-4px)]">
                                                            {/* Product Name - TOP, prominent, left-aligned */}
                                                            <h3 className={cn(
                                                                "font-bold text-sm sm:text-xs leading-tight line-clamp-2 text-left",
                                                                isDisabled 
                                                                    ? "text-gray-500 dark:text-gray-400" 
                                                                    : "text-gray-900 dark:text-white"
                                                            )}>
                                                                {product.name}
                                                            </h3>
                                                            
                                                            {/* Bottom row: Price left, Icon right */}
                                                            <div className="flex items-end justify-between mt-2">
                                                                <div className="flex items-baseline gap-0.5">
                                                                    <span className={cn(
                                                                        "text-lg sm:text-base font-black",
                                                                        isDisabled 
                                                                            ? "text-gray-400 dark:text-gray-500" 
                                                                            : "text-emerald-600 dark:text-emerald-400"
                                                                    )}>
                                                                        {product.price?.toLocaleString('fr-FR')}
                                                                    </span>
                                                                    <span className={cn(
                                                                        "text-xs font-semibold",
                                                                        isDisabled 
                                                                            ? "text-gray-400 dark:text-gray-500" 
                                                                            : "text-emerald-600/70 dark:text-emerald-400/70"
                                                                    )}>F</span>
                                                                </div>
                                                                <span className={cn(
                                                                    "text-lg sm:text-base",
                                                                    isDisabled ? "opacity-40" : "opacity-60"
                                                                )}>
                                                                    {category?.icon || '🍽️'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Out of Stock Overlay */}
                                                        {isDisabled && !managerMode && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 dark:bg-zinc-900/80">
                                                                <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                                                                    RUPTURE
                                                                </span>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Popular indicator - subtle bottom corner */}
                                                        {isPopular && !isDisabled && (
                                                            <div className="absolute bottom-1 right-1">
                                                                <Flame className="w-4 h-4 text-orange-500 drop-shadow" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Manager Actions Overlay - Only for authorized roles */}
                                                    {managerMode && canEditProducts && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleEditProduct(product)}
                                                                    className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 active:scale-95 transition-all shadow-lg"
                                                                    title="Modifier"
                                                                >
                                                                    <Edit className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDuplicateProduct(product)}
                                                                    className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:scale-95 transition-all shadow-lg"
                                                                    title="Dupliquer"
                                                                >
                                                                    <Copy className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleToggleProductActive(product)}
                                                                    className={cn("p-2 text-white rounded-lg active:scale-95 transition-all shadow-lg", product.is_available ? "bg-orange-500 hover:bg-orange-600" : "bg-green-500 hover:bg-green-600")}
                                                                    title={product.is_available ? "Désactiver" : "Activer"}
                                                                >
                                                                    <Ban className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteProduct(product)}
                                                                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 active:scale-95 transition-all shadow-lg"
                                                                    title="Supprimer"
                                                                >
                                                                    <Trash2 className="w-5 h-5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </ScrollArea>
                </div>
                
                {/* RIGHT - Cart - Sidebar toujours visible */}
                {/* DESKTOP VERSION */}
                <div className={cn(
                    "hidden md:flex w-full md:w-[280px] lg:w-[320px] flex-col bg-card rounded-xl border overflow-hidden"
                )}>
                    {/* Desktop Cart Header - Always visible */}
                    <div className={cn("p-2 sm:p-3 border-b", selectedTable ? "bg-emerald-500/10" : "bg-muted/30")}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShoppingCart className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
                                <h3 className="font-bold text-base sm:text-lg">Panier</h3>
                                {itemCount > 0 && <Badge className="bg-primary text-base sm:text-lg px-2">{itemCount}</Badge>}
                            </div>
                            {cart.length > 0 && (
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 touch-target" onClick={clearCart}>
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                        
                        {/* Table Assignment Zone - Compact */}
                        <div className="mt-1">
                            {selectedTable ? (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <Badge 
                                        className="bg-emerald-500 text-white text-xs px-2 py-0.5 cursor-pointer hover:bg-emerald-600"
                                        onClick={() => navigate('/tables')}
                                    >
                                        <MapPin className="w-3 h-3 mr-1" />
                                        Table {selectedTable.number}
                                    </Badge>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-5 px-1.5 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => setQuickMessageModal({ open: true, tableNumber: selectedTable.number })}
                                        data-testid="quick-message-btn"
                                    >
                                        <MessageSquare className="w-3 h-3 mr-0.5" />
                                        Message
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-red-500" onClick={async () => {
                                        await unlinkFromTable();
                                        setSelectedTable(null);
                                        setCart([]);
                                        setOrderItems([]);
                                        setCurrentTableOrder(null);
                                        setSentItemIds(new Set());
                                        setCustomerName('');
                                        setOrderNotes('');
                                        toast.success('Table libérée');
                                    }}>
                                        Délier
                                    </Button>
                                    {canCancelOrder && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-5 px-1.5 text-[10px] text-red-500"
                                            onClick={cancelAndReleaseTable}
                                        >
                                            Annuler
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="relative">
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 px-2 text-xs text-gray-700 bg-white border border-gray-300 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50" 
                                        onClick={() => {
                                            fetchAvailableTables();
                                            setShowTableSelect(!showTableSelect);
                                        }}
                                    >
                                        <MapPin className="w-3 h-3 mr-1" />
                                        Attribuer à une table
                                    </Button>
                                    
                                    {showTableSelect && (
                                        <QuickTableDropdown
                                            tables={availableTables}
                                            onSelect={assignCartToTable}
                                            onViewAll={() => {
                                                if (cart && cart.length > 0) {
                                                    localStorage.setItem('pos_pending_cart', JSON.stringify(cart));
                                                }
                                                navigate('/tables');
                                            }}
                                            loading={false}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Cart Content - Hidden on mobile when collapsed, always visible on desktop */}
                    <div className="flex flex-col flex-1 overflow-hidden min-h-0">
                        {/* Cart Items - Scrollable area that takes all available space */}
                        <ScrollArea className="flex-1">
                        {orderItems.length === 0 && cart.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-24 md:h-48 text-muted-foreground p-4">
                                <ShoppingCart className="w-10 md:w-16 h-10 md:h-16 mb-2 md:mb-3 opacity-20" />
                                <p className="text-sm md:text-base font-medium">Panier vide</p>
                                <p className="text-xs md:text-sm text-center mt-1">Cliquez sur un plat</p>
                            </div>
                        ) : (
                            <div className="p-2 space-y-2">
                                {/* ========== SECTION: ARTICLES ENVOYÉS ========== */}
                                {orderItems.length > 0 && (
                                    <>
                                        <div className="flex items-center gap-2 px-2 py-1 mb-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                            <span className="text-xs font-bold text-green-700 dark:text-green-400">
                                                Articles envoyés ({orderItems.length})
                                            </span>
                                            <span className="ml-auto text-xs font-bold text-green-700 dark:text-green-400">
                                                {orderTotals.sentItemsTotal.toLocaleString()} F
                                            </span>
                                        </div>
                                        
                                        {orderItems.map((item, index) => {
                                            const status = item.kitchen_status || item.bar_status;
                                            const statusColor = {
                                                'en_attente': 'bg-orange-100 border-orange-300 text-orange-700',
                                                'en_preparation': 'bg-blue-100 border-blue-300 text-blue-700',
                                                'pret': 'bg-green-100 border-green-300 text-green-700',
                                                'probleme': 'bg-red-100 border-red-300 text-red-700'
                                            }[status] || 'bg-gray-100 border-gray-300 text-gray-600';
                                            
                                            const statusLabel = {
                                                'en_attente': 'En attente',
                                                'en_preparation': 'En préparation',
                                                'pret': 'Prêt !',
                                                'probleme': 'Problème'
                                            }[status] || 'Envoyé';
                                            
                                            return (
                                                <div 
                                                    key={`sent-${item.id}-${index}`} 
                                                    className={cn("p-2 rounded-lg border", statusColor)}
                                                >
                                                    <div className="flex items-center gap-2 mb-1 pb-1 border-b border-current/20">
                                                        <Badge variant="secondary" className={cn("text-[10px]", statusColor)}>
                                                            {item.is_bar_item ? '🍹 Bar' : '🍳 Cuisine'}
                                                            {statusLabel && ` - ${statusLabel}`}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-bold text-xs truncate">{item.product_name}</p>
                                                            <p className="text-[10px] opacity-70">
                                                                {(item.unit_price || 0).toLocaleString()} F × {item.quantity}
                                                            </p>
                                                        </div>
                                                        <p className="font-bold text-sm">
                                                            {(item.line_total || item.unit_price * item.quantity).toLocaleString()} F
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                                
                                {/* ========== SECTION: NOUVEAUX ARTICLES (PANIER) ========== */}
                                {cart.length > 0 && (
                                    <>
                                        <div className="flex items-center gap-2 px-2 py-1 my-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                            <ShoppingCart className="w-4 h-4 text-blue-600" />
                                            <span className="text-xs font-bold text-blue-700 dark:text-blue-400">
                                                À envoyer ({cart.length})
                                            </span>
                                            <span className="ml-auto text-xs font-bold text-blue-700 dark:text-blue-400">
                                                {cartTotal.toLocaleString()} F
                                            </span>
                                        </div>
                                        
                                        {cart.map((item, index) => {
                                            return (
                                                <div 
                                                    key={`cart-${item.product_id}-${index}`} 
                                                    className={cn(
                                                        "p-2 sm:p-3 rounded-lg border bg-muted/30 border-transparent hover:border-muted",
                                                        item.is_formula && "bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50"
                                                    )}
                                                    data-testid={`cart-item-${index}`}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-1.5">
                                                                <p className={cn(
                                                                    "font-bold text-xs sm:text-sm truncate",
                                                                    item.isReward && "text-purple-700 dark:text-purple-400",
                                                                    item.is_formula && "text-emerald-700 dark:text-emerald-400"
                                                                )}>
                                                                    {item.product_name || item.name || 'Produit'}
                                                                </p>
                                                                {item.is_formula && (
                                                                    <span className="inline-flex items-center text-[9px] text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 px-1 py-0.5 rounded font-medium">
                                                                        FORMULE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {item.isReward && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] text-purple-600 bg-purple-100 dark:bg-purple-900/30 px-1.5 py-0.5 rounded mt-0.5">
                                                                    <Gift className="w-3 h-3" />
                                                                    Fidélité
                                                                </span>
                                                            )}
                                                            
                                                            {/* Formula selected items display */}
                                                            {item.is_formula && item.selected_items && item.selected_items.length > 0 && (
                                                                <div className="mt-1 pl-2 border-l-2 border-emerald-400 space-y-0.5">
                                                                    {item.selected_items.map((selItem, si) => (
                                                                        <p key={si} className="text-[10px] sm:text-xs text-emerald-700 dark:text-emerald-400">
                                                                            • {selItem.product_name}
                                                                            {selItem.price_adjustment > 0 && (
                                                                                <span className="text-orange-600 ml-1">+{selItem.price_adjustment.toLocaleString()}F</span>
                                                                            )}
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {item.accompaniment && (
                                                                <p className="text-[10px] sm:text-xs text-orange-600 mt-0.5">+ {item.accompaniment.name}</p>
                                                            )}
                                                            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                                                                {(item.price || 0)?.toLocaleString()} F × {item.quantity}
                                                            </p>
                                                        </div>
                                                        
                                                        {/* Quantity controls */}
                                                        <div className="flex items-center gap-1.5">
                                                            <button onClick={() => updateQuantity(index, -1)} className="w-8 h-8 rounded-lg bg-white border-2 border-gray-400 shadow-sm flex items-center justify-center touch-manipulation active:scale-90 hover:bg-gray-100">
                                                                <span className="text-lg font-black text-gray-900 leading-none flex items-center justify-center w-full h-full">−</span>
                                                            </button>
                                                            <span className="w-8 text-center font-black text-base text-gray-900">{item.quantity}</span>
                                                            <button onClick={() => updateQuantity(index, 1)} className="w-8 h-8 rounded-lg bg-white border-2 border-gray-400 shadow-sm flex items-center justify-center touch-manipulation active:scale-90 hover:bg-gray-100">
                                                                <span className="text-lg font-black text-gray-900 leading-none flex items-center justify-center w-full h-full">+</span>
                                                            </button>
                                                        </div>
                                                        
                                                        {/* Total */}
                                                        <p className="font-bold text-xs sm:text-sm w-12 sm:w-14 text-right">{(item.price * item.quantity).toLocaleString()}</p>
                                                        
                                                        {/* Remove button */}
                                                        <button onClick={() => removeItem(index)} className="w-6 sm:w-7 h-6 sm:h-7 rounded text-red-500 flex items-center justify-center touch-manipulation active:scale-90">
                                                            <X className="w-3 sm:w-4 h-3 sm:h-4" />
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Supplements */}
                                                    {item.supplements?.length > 0 && (
                                                        <div className="mt-2 pl-2 border-l-2 border-amber-400 space-y-0.5">
                                                            {item.supplements.map((sup, si) => (
                                                                <div key={si} className="flex justify-between items-center text-[10px] sm:text-xs">
                                                                    <span className="text-amber-600">+ {sup.name}</span>
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-amber-600">{(sup.price || 0).toLocaleString()} F</span>
                                                                        <button onClick={() => removeSupplementFromCartItem(index, si)} className="text-red-400 hover:text-red-600">
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Add Supplement Button */}
                                                    <button
                                                        onClick={() => openCartSupplementDialog(index)}
                                                        className="mt-2 flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        Supplément
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                                
                                {/* Order Notes */}
                                <div className="p-2">
                                    <Textarea
                                        placeholder="Notes pour la cuisine..."
                                        value={orderNotes}
                                        onChange={(e) => setOrderNotes(e.target.value)}
                                        className="text-xs h-16 resize-none"
                                    />
                                </div>
                            </div>
                        )}
                    </ScrollArea>
                    
                    {/* Cart Footer - Rétractable sur iPad/Desktop */}
                    {(cart.length > 0 || orderItems.length > 0) && (
                        <div className="border-t bg-muted/20">
                            {/* Toggle bar - visible only on md+ screens */}
                            <div 
                                className="hidden md:flex items-center justify-center py-1 cursor-pointer hover:bg-muted/40 transition-colors border-b"
                                onClick={() => setCartFooterCollapsed(!cartFooterCollapsed)}
                            >
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>{cartFooterCollapsed ? 'Afficher détails' : 'Masquer détails'}</span>
                                    <ChevronDown className={cn(
                                        "w-4 h-4 transition-transform duration-200",
                                        cartFooterCollapsed ? "" : "rotate-180"
                                    )} />
                                </div>
                            </div>
                            
                            {/* Collapsed view - Total + Buttons only (md+ screens) */}
                            {cartFooterCollapsed && (
                                <div className="hidden md:block p-3">
                                    <div className="flex justify-between text-xl font-black mb-3">
                                        <span>TOTAL</span>
                                        <span className="text-primary">{orderTotals.orderTotal.toLocaleString()} F</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            data-testid="send-to-kitchen-btn-collapsed"
                                            className={cn(
                                                "flex-1 h-10 text-sm font-bold shadow-lg touch-manipulation",
                                                canSendToKitchen ? "bg-emerald-600 hover:bg-emerald-700" : "bg-muted text-muted-foreground"
                                            )}
                                            onClick={handleSendToKitchen}
                                            disabled={!canSendToKitchen}
                                        >
                                            <ChefHat className="w-4 h-4 mr-1" />
                                            {itemsToSend.length > 0 ? `Envoyer (${itemsToSend.length})` : 'Envoyé'}
                                        </Button>
                                        <Button
                                            data-testid="pay-btn-collapsed"
                                            className="flex-1 h-10 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 shadow-lg touch-manipulation"
                                            onClick={handleRequestPayment}
                                        >
                                            <CreditCard className="w-4 h-4 mr-1" />
                                            Payer
                                        </Button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Full footer content - always visible on mobile, toggle on md+ */}
                            <div className={cn(
                                "p-3",
                                "md:block",
                                cartFooterCollapsed ? "hidden md:hidden" : "block"
                            )}>
                            {/* Résumé si items déjà envoyés */}
                            {orderItems.length > 0 && (
                                <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-green-700">Articles envoyés</span>
                                        <span className="font-bold text-green-700">{orderTotals.sentItemsTotal.toLocaleString()} F</span>
                                    </div>
                                    {cart.length > 0 && (
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-blue-600">Nouveaux articles</span>
                                            <span className="font-bold text-blue-600">+{cartTotal.toLocaleString()} F</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {supplementsTotal > 0 && (
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-muted-foreground">Suppléments</span>
                                    <span className="text-amber-600">+{supplementsTotal.toLocaleString()} F</span>
                                </div>
                            )}
                            
                            {/* Loyalty Member Badge - if selected */}
                            {selectedLoyaltyMember && (
                                <div className="mb-2 p-2 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className={cn("p-1.5 rounded-full", getLoyaltyStatusColor(selectedLoyaltyMember.status))}>
                                            {getLoyaltyStatusIcon(selectedLoyaltyMember.status)}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-green-800 dark:text-green-300">{selectedLoyaltyMember.first_name}</p>
                                            <p className="text-[10px] text-green-600 dark:text-green-400">{selectedLoyaltyMember.points} pts • +{Math.floor(orderTotals.orderTotal / 1000)} pts</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => setSelectedLoyaltyMember(null)}
                                        className="text-green-600 hover:text-green-800"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            
                            <div className="flex justify-between text-2xl font-black mb-3">
                                <span>TOTAL</span>
                                <span className="text-primary">{orderTotals.orderTotal.toLocaleString()} F</span>
                            </div>
                            
                            {/* Loyalty Buttons - Conditional on permissions */}
                            {canScanLoyalty && (
                                <>
                                    {/* Loyalty Button - Scanner fidélité client */}
                                    <Button
                                        variant="outline"
                                        className="w-full h-9 mb-2 border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950/30"
                                        onClick={() => setLoyaltyDialog({ open: true })}
                                        data-testid="loyalty-btn-pos"
                                    >
                                        <Gift className="w-4 h-4 mr-2" />
                                        {selectedLoyaltyMember ? 'Changer client fidélité' : 'Scanner fidélité'}
                                    </Button>
                                    
                                    {/* Reward Scanner Button - Scanner récompense */}
                                    <Button
                                        variant="outline"
                                        className="w-full h-9 mb-2 border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-950/30"
                                        onClick={() => setRewardScannerOpen(true)}
                                        data-testid="scan-reward-btn"
                                    >
                                        <ScanLine className="w-4 h-4 mr-2" />
                                        Scanner récompense
                                    </Button>
                                </>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2">
                                {/* Info about items sent */}
                                {sentItemIds.size > 0 && (
                                    <div className="text-xs text-center text-muted-foreground mb-1">
                                        {sentItemIds.size} article(s) envoyé(s) | {itemsToSend.length} à envoyer
                                    </div>
                                )}
                                
                                {/* Option À emporter */}
                                {itemsToSend.length > 0 && (
                                    <div className="flex items-center justify-center gap-2 py-2 px-3 bg-gray-50 dark:bg-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-700">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isTakeaway}
                                                onChange={(e) => setIsTakeaway(e.target.checked)}
                                                className="w-5 h-5 rounded border-2 border-emerald-400 text-emerald-500 focus:ring-emerald-500 accent-emerald-500"
                                            />
                                            <span className={cn(
                                                "text-sm font-bold flex items-center gap-1",
                                                isTakeaway ? "text-emerald-600 dark:text-emerald-400" : "text-gray-600 dark:text-gray-400"
                                            )}>
                                                <Package className="w-4 h-4" />
                                                À emporter
                                            </span>
                                        </label>
                                    </div>
                                )}
                                
                                {/* Message buttons - ALWAYS VISIBLE in footer */}
                                <div className="flex gap-2 mb-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 h-9 text-xs border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-700 hover:text-emerald-600 dark:hover:text-emerald-400"
                                        onClick={() => {
                                            setMessageDestination('cuisine');
                                            setShowMessageDialog(true);
                                        }}
                                    >
                                        <MessageSquare className="w-3.5 h-3.5 mr-1" />
                                        <span className="hidden sm:inline">Message </span>Cuisine
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 h-9 text-xs border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-700 hover:text-emerald-600 dark:hover:text-emerald-400"
                                        onClick={() => {
                                            setMessageDestination('bar');
                                            setShowMessageDialog(true);
                                        }}
                                    >
                                        <Wine className="w-3.5 h-3.5 mr-1" />
                                        <span className="hidden sm:inline">Message </span>Bar
                                    </Button>
                                </div>
                                
                                <div className="flex gap-2">
                                    {/* Send to Kitchen/Bar - Harmonized Emerald */}
                                    <Button
                                        data-testid="send-to-kitchen-btn"
                                        className={cn(
                                            "flex-1 h-11 sm:h-12 text-sm sm:text-base font-bold shadow-lg touch-manipulation",
                                            canSendToKitchen ? "bg-emerald-600 hover:bg-emerald-700" : "bg-muted text-muted-foreground"
                                        )}
                                        onClick={handleSendToKitchen}
                                        disabled={!canSendToKitchen}
                                    >
                                        <ChefHat className="w-4 sm:w-5 h-4 sm:h-5 mr-1 sm:mr-2" />
                                        <span className="hidden sm:inline">
                                            {itemsToSend.length > 0 ? `Envoyer (${itemsToSend.length})` : 'Tout envoyé'}
                                        </span>
                                        <span className="sm:hidden">
                                            {itemsToSend.length > 0 ? `Env. (${itemsToSend.length})` : 'Envoyé'}
                                        </span>
                                    </Button>
                                    
                                    {/* Payment button - Refined Green */}
                                    <Button
                                        data-testid="pay-btn"
                                        className="flex-1 h-11 sm:h-12 text-sm sm:text-base font-bold bg-emerald-600 hover:bg-emerald-700 shadow-lg touch-manipulation"
                                        onClick={handleRequestPayment}
                                    >
                                        <CreditCard className="w-4 sm:w-5 h-4 sm:h-5 mr-1 sm:mr-2" />
                                        Payer
                                    </Button>
                                </div>
                            </div>
                            </div>{/* End of full footer content */}
                        </div>
                    )}
                    </div>{/* End of Cart Content wrapper */}
                </div>
                
                {/* ===== UNIFIED CART - Works on all devices ===== */}
                <UnifiedCart
                    cart={cart}
                    orderItems={orderItems}
                    selectedTable={selectedTable}
                    orderTotals={orderTotals}
                    availableTables={availableTables}
                    loadingTables={false}
                    onUpdateQuantity={updateQuantity}
                    onRemoveItem={removeItem}
                    onClearCart={clearCart}
                    onSendToKitchen={handleSendToKitchen}
                    onRequestPayment={handleRequestPayment}
                    onAssignTable={assignCartToTable}
                    onUnlinkTable={async () => {
                        await unlinkFromTable();
                        setSelectedTable(null);
                        setCart([]);
                        setOrderItems([]);
                        setCurrentTableOrder(null);
                        setSentItemIds(new Set());
                        setCustomerName('');
                        setOrderNotes('');
                        toast.success('Table libérée');
                    }}
                    onFetchTables={fetchAvailableTables}
                    onLoyaltyClick={() => setLoyaltyDialog({ open: true })}
                    onRewardScanClick={() => setRewardScannerOpen(true)}
                    onMessageClick={(dest) => {
                        setMessageDestination(dest);
                        setShowMessageDialog(true);
                    }}
                    onOpenSupplementDialog={openCartSupplementDialog}
                    canSendToKitchen={canSendToKitchen}
                    canScanLoyalty={canScanLoyalty}
                    selectedLoyaltyMember={selectedLoyaltyMember}
                    isTakeaway={isTakeaway}
                    setIsTakeaway={setIsTakeaway}
                    isExpanded={mobileCartExpanded}
                    onToggleExpand={() => setMobileCartExpanded(!mobileCartExpanded)}
                    getLoyaltyStatusColor={getLoyaltyStatusColor}
                    getLoyaltyStatusIcon={getLoyaltyStatusIcon}
                />
            </div>
            
            {/* ===== DIALOGS ===== */}
            
            {/* Accompaniment Dialog */}
            <Dialog open={accompanimentDialog.open} onOpenChange={(open) => !open && setAccompanimentDialog({ open: false, product: null })}>
                <DialogContent className="max-w-lg mx-4 sm:mx-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
                            <Utensils className="w-5 sm:w-6 h-5 sm:h-6 text-emerald-500" />
                            Choisir un accompagnement
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                            <p className="font-bold text-base sm:text-lg">{accompanimentDialog.product?.name}</p>
                            <p className="text-primary font-bold">{accompanimentDialog.product?.price?.toLocaleString()} F</p>
                        </div>
                        
                        <p className="text-sm font-medium mb-3 text-muted-foreground">Choisissez un accompagnement (optionnel)</p>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {/* Sans accompagnement option */}
                            <button
                                onClick={() => {
                                    addProductToCartDirect(accompanimentDialog.product, null);
                                    setAccompanimentDialog({ open: false, product: null });
                                }}
                                className={cn(
                                    "p-3 sm:p-4 rounded-xl border text-left transition-all touch-manipulation active:scale-95",
                                    "border-gray-300 dark:border-zinc-600 hover:border-gray-400 dark:hover:border-zinc-500 bg-gray-50 dark:bg-zinc-800"
                                )}
                            >
                                <span className="text-xl sm:text-2xl block mb-1">🚫</span>
                                <span className="font-bold text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sans accomp.</span>
                            </button>
                            {accompaniments.map(acc => (
                                <button
                                    key={acc.id}
                                    onClick={() => handleAccompanimentSelect(acc)}
                                    className={cn(
                                        "p-3 sm:p-4 rounded-xl border text-left transition-all touch-manipulation active:scale-95",
                                        selectedAccompaniment?.id === acc.id ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" : "border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-700"
                                    )}
                                >
                                    <span className="text-xl sm:text-2xl block mb-1">{acc.icon || '🍚'}</span>
                                    <span className="font-bold text-xs sm:text-sm">{acc.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    {!fastServiceMode && (
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button variant="outline" onClick={() => setAccompanimentDialog({ open: false, product: null })} className="w-full sm:w-auto">
                                Annuler
                            </Button>
                            <Button onClick={confirmAccompanimentSelection} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                                Ajouter avec accomp.
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Cart Supplement Dialog - NEW RESPONSIVE COMPONENT */}
            <SupplementSelectionDialog
                open={cartSupplementDialog.open}
                onOpenChange={(open) => {
                    if (!open) {
                        setCartSupplementDialog({ open: false, cartIndex: null, selectedSupplements: [], allowedSupplementIds: null });
                    }
                }}
                supplements={supplements}
                selectedSupplements={cartSupplementDialog.selectedSupplements}
                onAddSupplement={handleAddSupplementInDialog}
                onRemoveSupplement={handleRemoveSupplementInDialog}
                onConfirm={handleConfirmSupplements}
                productName={cartSupplementDialog.cartIndex !== null ? cart[cartSupplementDialog.cartIndex]?.product_name : ''}
                currentTotal={cartSupplementDialog.cartIndex !== null ? (cart[cartSupplementDialog.cartIndex]?.price || 0) * (cart[cartSupplementDialog.cartIndex]?.quantity || 1) : 0}
                allowedSupplementIds={cartSupplementDialog.allowedSupplementIds}
            />
            
            {/* Table Dialog - SUPPRIMÉ: Utilise maintenant QuickTableDropdown ou sélection directe */}
            
            {/* Dialog de confirmation de changement de table */}
            <Dialog open={tableChangeConfirmDialog.open} onOpenChange={(open) => !open && setTableChangeConfirmDialog({ open: false, newTable: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl text-gray-800 dark:text-gray-200">
                            <AlertTriangle className="w-6 h-6 text-amber-500" />
                            Articles non envoyés
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <p className="text-gray-600 dark:text-gray-400">
                            Vous avez <strong>{cart.length} article(s)</strong> dans le panier qui n'ont pas été envoyés en cuisine.
                        </p>
                        <p className="text-gray-600 dark:text-gray-400">
                            Que souhaitez-vous faire avant de changer de table ?
                        </p>
                        
                        <div className="bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl p-4">
                            <p className="font-bold text-gray-800 dark:text-gray-200 mb-2">Articles en attente :</p>
                            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 max-h-32 overflow-y-auto">
                                {cart.map((item, idx) => (
                                    <li key={idx}>• {item.product_name} x{item.quantity}</li>
                                ))}
                            </ul>
                            <p className="mt-2 font-bold text-orange-800">
                                Total: {cartTotal.toLocaleString()} F
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setTableChangeConfirmDialog({ open: false, newTable: null })}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={() => confirmTableChange(false)}
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                        </Button>
                        {currentTableOrder && (
                            <Button 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => confirmTableChange(true)}
                            >
                                <Send className="w-4 h-4 mr-2" />
                                Envoyer d'abord
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Payment Dialog - Extracted Component */}
            <PaymentDialog
                open={paymentDialog.open}
                onOpenChange={(open) => setPaymentDialog({ open })}
                cartTotal={cartTotal}
                itemCount={itemCount}
                selectedTable={selectedTable}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                paymentAmount={paymentAmount}
                setPaymentAmount={setPaymentAmount}
                selectedLoyaltyMember={selectedLoyaltyMember}
                getLoyaltyStatusColor={getLoyaltyStatusColor}
                getLoyaltyStatusIcon={getLoyaltyStatusIcon}
                onOpenLoyaltyDialog={() => {
                    setPaymentDialog({ open: false });
                    setLoyaltyDialog({ open: true });
                }}
                onSubmitPayment={submitPayment}
            />
            
            {/* Loyalty Dialog - Extracted Component */}
            <LoyaltyDialog
                open={loyaltyDialog.open}
                onOpenChange={(open) => setLoyaltyDialog({ open })}
                searchPhone={loyaltySearchPhone}
                setSearchPhone={setLoyaltySearchPhone}
                isSearching={loyaltySearching}
                member={loyaltyMember}
                getLoyaltyStatusColor={getLoyaltyStatusColor}
                getLoyaltyStatusIcon={getLoyaltyStatusIcon}
                onSelectMember={selectLoyaltyMemberForPayment}
                onOpenPaymentDialog={() => setPaymentDialog({ open: true })}
                cartLength={cart.length}
                paymentDialogOpen={paymentDialog.open}
                canAddPointsManual={canAddLoyaltyPointsManual}
                addPointsMode={loyaltyAddPointsMode}
                setAddPointsMode={setLoyaltyAddPointsMode}
                pointsToAdd={loyaltyPointsToAdd}
                setPointsToAdd={setLoyaltyPointsToAdd}
                pointsReason={loyaltyPointsReason}
                setPointsReason={setLoyaltyPointsReason}
                onManualPointsSubmit={handleManualLoyaltyPoints}
            />
            
            {/* ===== REWARD SCANNER DIALOG - SCAN AUTOMATIQUE OPTIMISÉ ===== */}
            <Dialog open={rewardScannerOpen} onOpenChange={(open) => {
                if (!open) closeRewardScanner();
            }}>
                <DialogContent className="max-w-md mx-4 sm:mx-auto p-0 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <DialogTitle className="flex items-center gap-2 text-white text-lg">
                            <ScanLine className="w-5 h-5" />
                            Scanner récompense fidélité
                        </DialogTitle>
                        <DialogDescription className="text-purple-100 text-sm mt-1">
                            {!verifiedReward 
                                ? "Pointez la caméra vers le QR code du client"
                                : "Récompense détectée - Confirmez"
                            }
                        </DialogDescription>
                    </div>
                    
                    <div className="p-4">
                        {!verifiedReward ? (
                            /* Scanner Mode - Scan automatique */
                            <div className="space-y-4">
                                {/* Camera Container avec cadre visuel */}
                                <div className="relative">
                                    <div 
                                        id="reward-scanner-container" 
                                        className="w-full aspect-square rounded-xl overflow-hidden bg-black"
                                        data-testid="reward-qr-scanner"
                                    />
                                    
                                    {/* Cadre de visée */}
                                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                        <div className="w-64 h-64 border-4 border-white/50 rounded-2xl relative">
                                            {/* Coins du cadre */}
                                            <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-purple-400 rounded-tl-lg" />
                                            <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-purple-400 rounded-tr-lg" />
                                            <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-purple-400 rounded-bl-lg" />
                                            <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-purple-400 rounded-br-lg" />
                                        </div>
                                    </div>
                                    
                                    {/* Loading overlay */}
                                    {rewardScannerLoading && (
                                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                                            <Loader2 className="w-10 h-10 animate-spin text-white mb-2" />
                                            <span className="text-white font-medium">Vérification...</span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Instructions */}
                                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                    <Camera className="w-4 h-4" />
                                    <span>Scan automatique • Bip = QR détecté</span>
                                </div>
                                
                                {/* Saisie manuelle du code */}
                                <div className="pt-3 border-t">
                                    <p className="text-xs text-muted-foreground text-center mb-2">
                                        Ou entrez le code manuellement :
                                    </p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Ex: REWARD:ABC123 ou ABC123"
                                            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && e.target.value.trim()) {
                                                    console.log('Saisie manuelle:', e.target.value);
                                                    playBeepSound();
                                                    handleRewardScanned(e.target.value.trim());
                                                    e.target.value = '';
                                                }
                                            }}
                                            data-testid="manual-qr-input"
                                        />
                                        <Button
                                            size="sm"
                                            className="bg-purple-600 hover:bg-purple-700"
                                            onClick={(e) => {
                                                const input = e.target.closest('.flex').querySelector('input');
                                                if (input && input.value.trim()) {
                                                    console.log('Saisie manuelle (bouton):', input.value);
                                                    playBeepSound();
                                                    handleRewardScanned(input.value.trim());
                                                    input.value = '';
                                                }
                                            }}
                                            data-testid="manual-qr-submit"
                                        >
                                            Valider
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Confirmation Mode - Récompense détectée */
                            <div className="space-y-4">
                                {/* Success Header */}
                                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border-2 border-green-200 dark:border-green-800">
                                    <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Gift className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-green-800 dark:text-green-300 text-lg">
                                            Récompense détectée
                                        </h3>
                                        <p className="text-green-700 dark:text-green-400 font-medium">
                                            {verifiedReward.reward_name}
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Quick Info Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-center">
                                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Client</p>
                                        <p className="font-bold text-lg mt-1">{verifiedReward.member_name}</p>
                                    </div>
                                    <div className="p-4 bg-purple-50 dark:bg-purple-900/30 rounded-xl text-center">
                                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Points</p>
                                        <p className="font-bold text-2xl text-purple-600 mt-1">-{verifiedReward.points_cost}</p>
                                    </div>
                                </div>
                                
                                {/* Product Selection (only if multiple choices) */}
                                {verifiedReward.has_choices && verifiedReward.product_choices?.length > 1 && (
                                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                                        <p className="text-sm font-bold text-amber-800 dark:text-amber-300 mb-3">
                                            Choisir le produit offert :
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {verifiedReward.product_choices.map((choice, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setSelectedRewardProduct({
                                                        id: choice.product?.id || choice.choice_id,
                                                        name: choice.product?.name || choice.choice_name,
                                                        choice_name: choice.choice_name
                                                    })}
                                                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                                                        selectedRewardProduct?.id === (choice.product?.id || choice.choice_id)
                                                            ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/50'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 bg-white dark:bg-gray-800'
                                                    }`}
                                                    data-testid={`reward-choice-${idx}`}
                                                >
                                                    <span className="font-medium text-sm">
                                                        {choice.product?.name || choice.choice_name}
                                                    </span>
                                                    {selectedRewardProduct?.id === (choice.product?.id || choice.choice_id) && (
                                                        <Check className="w-4 h-4 text-purple-600 mx-auto mt-1" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Selected Product Display (if single choice or discount) */}
                                {selectedRewardProduct && (!verifiedReward.has_choices || verifiedReward.product_choices?.length <= 1) && (
                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
                                        <p className="text-sm text-blue-600 dark:text-blue-400">Produit offert</p>
                                        <p className="font-bold text-lg">{selectedRewardProduct.name}</p>
                                        <p className="text-green-600 font-bold">0 FCFA</p>
                                    </div>
                                )}
                                
                                {/* Discount Display */}
                                {verifiedReward.is_discount && (
                                    <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-xl text-center">
                                        <p className="text-sm text-green-800 dark:text-green-300">Réduction appliquée</p>
                                        <p className="text-3xl font-black text-green-600">
                                            -{verifiedReward.discount_value?.toLocaleString()} FCFA
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* Footer Buttons */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t flex gap-3">
                        <Button 
                            variant="outline" 
                            onClick={verifiedReward ? cancelRewardScan : closeRewardScanner}
                            className="flex-1"
                            data-testid="reward-cancel-btn"
                        >
                            {verifiedReward ? 'Annuler' : 'Fermer'}
                        </Button>
                        {verifiedReward && (
                            <Button 
                                onClick={confirmAndApplyReward}
                                disabled={rewardConfirmLoading || (verifiedReward.has_choices && verifiedReward.product_choices?.length > 1 && !selectedRewardProduct)}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                                data-testid="reward-validate-btn"
                            >
                                {rewardConfirmLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Validation...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        Valider
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Product Edit Dialog - Extracted Component */}
            <ProductEditDialog
                open={productEditDialog.open}
                onOpenChange={(open) => !open && setProductEditDialog({ open: false, product: null, isNew: false })}
                productData={productEditDialog.product}
                setProductData={setProductEditDialog}
                isNew={productEditDialog.isNew}
                categories={categories}
                accompaniments={accompaniments}
                supplements={supplements}
                onSave={saveProduct}
            />
            
            {/* Supplement Edit Dialog */}
            <Dialog open={supplementEditDialog.open} onOpenChange={(open) => !open && setSupplementEditDialog({ open: false, supplement: null, isNew: false })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-amber-500" />
                            {supplementEditDialog.isNew ? 'Nouveau supplément' : 'Modifier le supplément'}
                        </DialogTitle>
                    </DialogHeader>
                    {supplementEditDialog.supplement && (
                        <div className="py-4 space-y-4">
                            <div>
                                <Label>Nom *</Label>
                                <Input
                                    value={supplementEditDialog.supplement.name}
                                    onChange={(e) => setSupplementEditDialog(prev => ({
                                        ...prev,
                                        supplement: { ...prev.supplement, name: e.target.value }
                                    }))}
                                    placeholder="Ex: Extra fromage"
                                />
                            </div>
                            <div>
                                <Label>Prix (FCFA)</Label>
                                <Input
                                    type="number"
                                    value={supplementEditDialog.supplement.price}
                                    onChange={(e) => setSupplementEditDialog(prev => ({
                                        ...prev,
                                        supplement: { ...prev.supplement, price: parseFloat(e.target.value) || 0 }
                                    }))}
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <p className="font-medium">Supplément actif</p>
                                <Switch
                                    checked={supplementEditDialog.supplement.is_active !== false}
                                    onCheckedChange={(v) => setSupplementEditDialog(prev => ({
                                        ...prev,
                                        supplement: { ...prev.supplement, is_active: v }
                                    }))}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSupplementEditDialog({ open: false, supplement: null, isNew: false })}>
                            Annuler
                        </Button>
                        <Button onClick={saveSupplement} className="bg-amber-600 hover:bg-amber-700">
                            <Check className="w-4 h-4 mr-2" />
                            {supplementEditDialog.isNew ? 'Créer' : 'Enregistrer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Message Dialog for Kitchen/Bar */}
            <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {messageDestination === 'bar' ? (
                                <>
                                    <Wine className="w-5 h-5 text-indigo-600" />
                                    Message pour le Bar
                                </>
                            ) : (
                                <>
                                    <ChefHat className="w-5 h-5 text-orange-600" />
                                    Message pour la Cuisine
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {selectedTable && (
                            <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm">Table {selectedTable.number}</span>
                            </div>
                        )}
                        {!selectedTable && (
                            <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                                <Package className="w-4 h-4 text-amber-600" />
                                <span className="text-sm text-amber-700 dark:text-amber-400">Commande sans table</span>
                            </div>
                        )}
                        <Textarea
                            placeholder={`Votre message pour ${messageDestination === 'bar' ? 'le bar' : 'la cuisine'}...`}
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            className="min-h-[100px]"
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSendMessage}
                            className={messageDestination === 'bar' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-orange-600 hover:bg-orange-700'}
                        >
                            <Send className="w-4 h-4 mr-2" />
                            Envoyer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Formula Creation/Edit Dialog - Extracted Component */}
            <FormulaDialog
                open={formulaDialog.open}
                onClose={() => {
                    setFormulaDialog({ open: false });
                    setEditingFormula(null);
                    setNewFormula({
                        name: '',
                        description: '',
                        price: '',
                        steps: [{ name: 'Entrée', choices: [], required: true }, { name: 'Plat', choices: [], required: true }, { name: 'Dessert', choices: [], required: false }],
                        meal_type: 'all'
                    });
                }}
                editingFormula={editingFormula}
                newFormula={newFormula}
                setNewFormula={setNewFormula}
                categories={categories}
                products={products}
                formulaProductFilter={formulaProductFilter}
                setFormulaProductFilter={setFormulaProductFilter}
                onAddStep={addFormulaStep}
                onRemoveStep={removeFormulaStep}
                onUpdateStepName={updateFormulaStepName}
                onAddProductToStep={addProductToFormulaStep}
                onRemoveProductFromStep={removeProductFromFormulaStep}
                onSave={saveFormula}
                onUpdate={updateFormula}
            />
            
            {/* Settings Dialog - Extracted Component */}
            <SettingsDialog
                open={settingsDialog.open}
                onOpenChange={(open) => setSettingsDialog({ open })}
                posSettings={posSettings}
                setPosSettings={setPosSettings}
            />
            
            {/* ===== CASH CLOSING DIALOG ===== */}
            <Dialog open={closingDialog.open} onOpenChange={(open) => {
                if (!open) {
                    setOpenOrdersCheck(null);
                    setForceCloseMode(false);
                    setForceCloseReason('');
                }
                setClosingDialog({ open });
            }}>
                <DialogContent className="max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-xl">
                            <Calculator className="w-6 h-6 text-blue-600" />
                            Clôturer la caisse
                        </DialogTitle>
                        <DialogDescription>
                            Récapitulatif de la session en cours
                        </DialogDescription>
                    </DialogHeader>
                    
                    {cashSession && (
                        <div className="py-4 space-y-4">
                            {/* OPEN ORDERS WARNING */}
                            {openOrdersCheck && openOrdersCheck.count > 0 && (
                                <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="font-bold text-red-800">
                                                Impossible de fermer la caisse
                                            </p>
                                            <p className="text-sm text-red-700 mt-1">
                                                {openOrdersCheck.count} commande(s) ouverte(s) pour un total de {openOrdersCheck.total.toLocaleString()} F
                                            </p>
                                            
                                            {/* Open orders list */}
                                            <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                                                {openOrdersCheck.orders.map((order, idx) => (
                                                    <div key={idx} className="flex justify-between items-center text-sm bg-white p-2 rounded border">
                                                        <div>
                                                            <span className="font-medium">{order.order_number}</span>
                                                            <span className="text-muted-foreground ml-2">({order.table_number})</span>
                                                        </div>
                                                        <span className="font-semibold">{order.total.toLocaleString()} F</span>
                                                    </div>
                                                ))}
                                            </div>
                                            
                                            <p className="text-xs text-red-600 mt-3">
                                                Ces commandes doivent être payées, transférées ou annulées avant la fermeture.
                                            </p>
                                            
                                            {/* Force close option for managers */}
                                            {isManager && (
                                                <div className="mt-4 pt-3 border-t border-red-200">
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={forceCloseMode}
                                                            onChange={(e) => setForceCloseMode(e.target.checked)}
                                                            className="w-4 h-4 rounded border-red-400 text-red-600 focus:ring-red-500"
                                                        />
                                                        <span className="text-sm font-medium text-red-800">
                                                            Forcer la fermeture (réservé aux managers)
                                                        </span>
                                                    </label>
                                                    
                                                    {forceCloseMode && (
                                                        <div className="mt-2">
                                                            <Input
                                                                placeholder="Motif de la fermeture forcée (obligatoire)..."
                                                                value={forceCloseReason}
                                                                onChange={(e) => setForceCloseReason(e.target.value)}
                                                                className="border-red-300 focus:border-red-500"
                                                                data-testid="force-close-reason"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Warning if no cash sales but orders exist */}
                            {cashSession.warning && !openOrdersCheck?.count && (
                                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-amber-800">{cashSession.warning}</p>
                                </div>
                            )}
                            
                            {/* Session Info Header */}
                            <div className={`p-4 rounded-lg border ${isBarRegister ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
                                {isBarRegister && (
                                    <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-amber-200">
                                        <div className="flex items-center gap-2">
                                            <span className="text-2xl">{showAllProductsInBarMode ? '🍽️' : '🍺'}</span>
                                            <span className={cn("font-bold", showAllProductsInBarMode ? "text-emerald-700" : "text-amber-700")}>
                                                {showAllProductsInBarMode ? 'TOUS LES PRODUITS' : 'MODE BAR'}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => setShowAllProductsInBarMode(!showAllProductsInBarMode)}
                                            className={cn(
                                                "text-xs px-3 py-1.5 rounded-full font-medium transition-all",
                                                showAllProductsInBarMode 
                                                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                                    : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                            )}
                                            data-testid="bar-mode-toggle-mobile"
                                        >
                                            {showAllProductsInBarMode ? 'Mode Bar' : 'Voir tout'}
                                        </button>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Caisse</span>
                                        <p className="font-semibold">{cashSession.register_name || 'Caisse principale'}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Utilisateur</span>
                                        <p className="font-semibold">{cashSession.opened_by_name}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Ouverture</span>
                                        <p className="font-semibold">
                                            {cashSession.opened_at ? new Date(cashSession.opened_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Nb. tickets</span>
                                        <p className="font-semibold">{cashSession.orders_count || 0}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Sales Breakdown */}
                            <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                                    <Receipt className="w-4 h-4" />
                                    Récapitulatif des encaissements
                                </h4>
                                <div className="p-4 bg-gray-50 rounded-lg border space-y-2">
                                    {/* Opening Cash */}
                                    <div className="flex justify-between text-sm pb-2 border-b">
                                        <span className="text-muted-foreground">Fond de caisse</span>
                                        <span className="font-semibold">{(cashSession.opening_cash || 0).toLocaleString()} F</span>
                                    </div>
                                    
                                    {/* Cash Sales */}
                                    <div className="flex justify-between text-sm">
                                        <span className="flex items-center gap-2">
                                            <Banknote className="w-4 h-4 text-green-600" />
                                            Ventes espèces
                                        </span>
                                        <span className="font-semibold text-green-600">+{(cashSession.cash_sales || 0).toLocaleString()} F</span>
                                    </div>
                                    
                                    {/* Card Sales */}
                                    <div className="flex justify-between text-sm">
                                        <span className="flex items-center gap-2">
                                            <CreditCard className="w-4 h-4 text-blue-600" />
                                            Ventes carte bancaire
                                        </span>
                                        <span className="font-semibold text-blue-600">{(cashSession.card_sales || 0).toLocaleString()} F</span>
                                    </div>
                                    
                                    {/* Mobile Money Sales */}
                                    <div className="flex justify-between text-sm">
                                        <span className="flex items-center gap-2">
                                            <Smartphone className="w-4 h-4 text-orange-600" />
                                            Ventes mobile money
                                        </span>
                                        <span className="font-semibold text-orange-600">{(cashSession.mobile_sales || 0).toLocaleString()} F</span>
                                    </div>
                                    
                                    {/* Mobile Money Detail (if any) */}
                                    {(cashSession.mtn_sales > 0 || cashSession.airtel_sales > 0 || cashSession.orange_sales > 0 || cashSession.moov_sales > 0) && (
                                        <div className="pl-6 text-xs text-muted-foreground space-y-1">
                                            {cashSession.mtn_sales > 0 && <div>MTN Money: {cashSession.mtn_sales.toLocaleString()} F</div>}
                                            {cashSession.airtel_sales > 0 && <div>Airtel Money: {cashSession.airtel_sales.toLocaleString()} F</div>}
                                            {cashSession.orange_sales > 0 && <div>Orange Money: {cashSession.orange_sales.toLocaleString()} F</div>}
                                            {cashSession.moov_sales > 0 && <div>Moov Money: {cashSession.moov_sales.toLocaleString()} F</div>}
                                        </div>
                                    )}
                                    
                                    {/* Total encaissé */}
                                    <div className="flex justify-between text-sm pt-2 border-t font-bold">
                                        <span>Total encaissé</span>
                                        <span className="text-primary">{(cashSession.total_sales || 0).toLocaleString()} F</span>
                                    </div>
                                    
                                    {/* Discounts & Cancellations if any */}
                                    {(cashSession.total_discounts > 0 || cashSession.cancelled_count > 0) && (
                                        <div className="pt-2 border-t space-y-1">
                                            {cashSession.total_discounts > 0 && (
                                                <div className="flex justify-between text-sm text-orange-600">
                                                    <span>Total remises</span>
                                                    <span>-{cashSession.total_discounts.toLocaleString()} F</span>
                                                </div>
                                            )}
                                            {cashSession.cancelled_count > 0 && (
                                                <div className="flex justify-between text-sm text-red-600">
                                                    <span>Annulations ({cashSession.cancelled_count})</span>
                                                    <span>-{(cashSession.total_cancelled || 0).toLocaleString()} F</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Expected Cash - HIGHLIGHTED */}
                            <div className="p-4 bg-blue-100 border-2 border-blue-300 rounded-lg">
                                <p className="text-sm text-blue-700 mb-1 font-medium">Espèces attendues en caisse</p>
                                <p className="text-3xl font-black text-blue-800">
                                    {(cashSession.expected_cash || 0).toLocaleString()} F
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                    = Fond ({(cashSession.opening_cash || 0).toLocaleString()} F) + Espèces ({(cashSession.cash_sales || 0).toLocaleString()} F)
                                </p>
                            </div>
                            
                            {/* Cash Input */}
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold block">
                                    Montant compté en caisse (F CFA) *
                                </Label>
                                <Input
                                    type="number"
                                    placeholder="Entrez le montant compté..."
                                    value={closingCash}
                                    onChange={(e) => setClosingCash(e.target.value)}
                                    className="text-xl h-14 font-bold text-center"
                                    data-testid="closing-cash-input"
                                />
                            </div>
                            
                            {/* Difference Preview with status */}
                            {closingCash && (
                                <div className={cn(
                                    "p-4 rounded-lg text-center border-2",
                                    parseFloat(closingCash) === (cashSession.expected_cash || 0) 
                                        ? "bg-green-50 border-green-400" 
                                        : parseFloat(closingCash) > (cashSession.expected_cash || 0) 
                                            ? "bg-blue-50 border-blue-400" 
                                            : "bg-red-50 border-red-400"
                                )}>
                                    <p className="text-sm font-medium">
                                        {parseFloat(closingCash) === (cashSession.expected_cash || 0) 
                                            ? "✓ Équilibre" 
                                            : parseFloat(closingCash) > (cashSession.expected_cash || 0) 
                                                ? "↑ Excédent" 
                                                : "↓ Manquant"}
                                    </p>
                                    <p className={cn(
                                        "text-2xl font-black",
                                        parseFloat(closingCash) === (cashSession.expected_cash || 0) 
                                            ? "text-green-700" 
                                            : parseFloat(closingCash) > (cashSession.expected_cash || 0) 
                                                ? "text-blue-700" 
                                                : "text-red-700"
                                    )}>
                                        {parseFloat(closingCash) === (cashSession.expected_cash || 0) 
                                            ? '0' 
                                            : (parseFloat(closingCash) > (cashSession.expected_cash || 0) ? '+' : '')}
                                        {parseFloat(closingCash) !== (cashSession.expected_cash || 0) && 
                                            (parseFloat(closingCash || 0) - (cashSession.expected_cash || 0)).toLocaleString()
                                        } F
                                    </p>
                                </div>
                            )}
                            
                            {/* Notes - Required if difference != 0 */}
                            <div>
                                <Label className="text-sm font-semibold mb-2 block">
                                    Observations / Notes 
                                    {closingCash && parseFloat(closingCash) !== (cashSession.expected_cash || 0) && (
                                        <span className="text-red-500 ml-1">*</span>
                                    )}
                                </Label>
                                <Input
                                    placeholder={closingCash && parseFloat(closingCash) !== (cashSession.expected_cash || 0) 
                                        ? "Motif de l'écart obligatoire..." 
                                        : "Ex: RAS, rendu monnaie client..."}
                                    value={closingNotes}
                                    onChange={(e) => setClosingNotes(e.target.value)}
                                    data-testid="closing-notes-input"
                                />
                                {closingCash && parseFloat(closingCash) !== (cashSession.expected_cash || 0) && !closingNotes && (
                                    <p className="text-xs text-red-500 mt-1">Le motif de l'écart est obligatoire</p>
                                )}
                            </div>
                            
                            {/* Info for employees */}
                            {!isManager && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-xs text-amber-700">
                                        Votre clôture sera soumise à la direction pour validation avant d'être finalisée.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setClosingDialog({ open: false })}
                            className="w-full sm:w-auto"
                        >
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleCloseCash}
                            disabled={
                                closingLoading || 
                                !closingCash || 
                                (closingCash && parseFloat(closingCash) !== (cashSession?.expected_cash || 0) && !closingNotes) ||
                                (openOrdersCheck?.count > 0 && !forceCloseMode) ||
                                (forceCloseMode && !forceCloseReason)
                            }
                            className={cn(
                                "w-full sm:w-auto",
                                forceCloseMode ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
                            )}
                            data-testid="confirm-closing-btn"
                        >
                            {closingLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4 mr-2" />
                            )}
                            {forceCloseMode 
                                ? 'Forcer la fermeture' 
                                : isManager 
                                    ? 'Clôturer la caisse' 
                                    : 'Soumettre pour validation'
                            }
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Cash Closing Modal (Detailed FCFA counting) */}
            <CashClosingModal
                open={cashClosingModal.open}
                onClose={() => setCashClosingModal({ open: false, registerId: null, registerName: '' })}
                registerId={cashClosingModal.registerId}
                registerName={cashClosingModal.registerName}
                sessionId={cashClosingModal.sessionId}
                api={api}
                onClosingComplete={() => {
                    setCashClosingModal({ open: false, registerId: null, registerName: '' });
                    toast.success('Clôture soumise - En attente de validation par un directeur');
                    // Redirect to dashboard after successful closing
                    navigate('/dashboard');
                }}
            />
            
            {/* Category Manager - Mode Manager */}
            <CategoryManager
                api={api}
                categories={categories}
                products={products}
                isOpen={categoryManagerOpen}
                onClose={() => setCategoryManagerOpen(false)}
                onCategoriesChange={fetchData}
            />
            
            {/* Formula Selection Modal - Pop-up intelligent pour formules */}
            <FormulaSelectionModal
                isOpen={formulaModal.open}
                onClose={() => setFormulaModal({ open: false, formula: null })}
                formula={formulaModal.formula}
                products={products}
                supplements={supplements}
                onAddToCart={handleFormulaAddToCart}
            />
            
            {/* Formula Manager - Configuration des formules (back-office) */}
            <FormulaManager
                api={api}
                products={products}
                categories={categories}
                isOpen={formulaManagerOpen}
                onClose={() => setFormulaManagerOpen(false)}
                onFormulasChange={fetchData}
            />
            
            {/* Formula Quick Edit - Édition rapide depuis le POS */}
            <FormulaQuickEdit
                api={api}
                formula={formulaQuickEdit.formula}
                products={products}
                isOpen={formulaQuickEdit.open}
                onClose={() => setFormulaQuickEdit({ open: false, formula: null })}
                onSave={fetchData}
            />
            
            {/* Quick Message Modal - Message rapide vers cuisine/bar */}
            <QuickMessageModal
                isOpen={quickMessageModal.open}
                onClose={() => setQuickMessageModal({ open: false, tableNumber: null })}
                tableNumber={quickMessageModal.tableNumber || selectedTable?.number}
                api={api}
            />
        </div>
        </>
    );
};

export default POSPageV2;
